import './App.css';

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { LucideIcon } from 'lucide-react';
import MapViewOL from './MapViewOL';
import { MAX_INTERACTIVE_ZOOM } from './types';
import type { AppKind, AppModule, BasemapId, HeatmapConfig, HomePageConfig, Layer, LayerScaleVisibility, LocalRasterData, ServerConnection, User } from './types';
import DashboardBuilder from './dashboard/DashboardBuilder';
import DashboardViewer from './dashboard/DashboardViewer';
import { createDashboardModule, isDashboard } from './dashboard/services/dashboardService';
import { AuthProvider, useAuth, usePermissions } from './auth/AuthContext';
import { moduleService, ADMIN_OWNER_ID } from './services/moduleService';
import { temporaryLayerService, withoutTemporary } from './services/temporaryLayerService';
import { getModuleAccess, ModulePermissionError } from './auth/modulePermissions';
import { userService } from './services/userService';
import {
  serverConnectionsService,
  type ServerConnectionBrowseResult,
  type ServerConnectionService,
} from './services/serverConnectionsService';
import { arcgisServiceUrlFor, withArcgisToken } from './utils/arcgis';
import ShareModuleDialog from './components/ShareModuleDialog';
import LayerShareDialog from './components/layers/LayerShareDialog';
import { layerLibraryService, type SharedLayerEdit } from './services/layerLibraryService';
import LayerOptionsMenu, { type LayerMenuItem } from './components/layers/LayerOptionsMenu';
import SymbologyPanel from './components/layers/symbology/SymbologyPanel';
import AppHeader from './components/header/AppHeader';
import HeaderProperties from './components/header/HeaderProperties';
import AddToolDialog from './components/tools/AddToolDialog';
import { ToolStrip, ToolsToggle } from './components/tools/ToolBar';
import ToolPanelHeader, { ToolActions } from './components/tools/ToolPanel';
import {
  renderToolPanel,
  toolOwnsMapClicks,
  type MapPoint,
  type ToolContext,
} from './components/tools/panels';
import { TOOL_REGISTRY, toolConfigWithDefaults } from './tools/toolRegistry';
import { fetchLayerFieldNames, fetchNumericFieldNames } from './tools/toolFields';
import Tooltip from './components/glass/Tooltip';
import LabelProperties from './labeling/LabelProperties';
import PopupProperties from './popup/PopupProperties';
import { arcgisPopupKey, localPopupKey, wmsPopupKey, type LayerPopup } from './popup/PopupTypes';
import {
  labelingIsActive,
  labelingIsConfigured,
  normaliseLabeling,
  type LayerLabeling,
} from './labeling/LabelTypes';
import { resolutionAt, scaleAt, SCALE_PRESETS, formatScale, parseScale } from './labeling/ScaleVisibility';
import FloatingAttributeTable from './components/layers/FloatingAttributeTable';
import AddLayersButton from './components/layers/AddLayersButton';
import AddLayersDialog, { type AddLayersMode } from './components/layers/AddLayersDialog';
import { computeBounds, type ParsedLocalLayer } from './layers/localFile';
import { featureKey } from './utils/featureKey';
import { nearestPositions } from './utils/spatialSelect';
import {
  buildFilterExpression,
  clauseIsComplete,
  matchesClauses,
  type AttributeClause,
  type AttributeOperator,
} from './components/layers/attributeQuery';
import { distanceBetween, formatArea, formatLength, geodesicCircle, measureGeometry } from './utils/measure';
import { captureMapOL } from './utils/mapCapture';
import { downloadSheet, printFrameAspectRatio, printSheet, renderPrintSheet } from './utils/printSheet';
import { collectLegend, loadCorsImage } from './utils/legendSymbols';
import { filterLegendToPrintExtent } from './utils/printLegendExtent';
import { loadFeatures } from './components/tools/panels/featureLoad';
import { prepare, relate, type Bbox } from './utils/spatialRelate';
import { sortByHomeOrder } from './utils/moduleOrder';
import { toCsv } from './utils/csv';
import { writeZip } from './layers/zip';
import { downloadKml, featuresToKml } from './layers/kmlExport';
import LegendPanel from './components/layers/LegendPanel';
import { filteredLocalData, filteredLocalFeatures } from './layers/localFilter';
import BlueViewMark from './components/brand/BlueViewMark';
import ContourBackground from './components/brand/ContourBackground';
import MapGeoidGlobe from './components/brand/MapGeoidGlobe';
import GlobalLocationSearch from './components/search/GlobalLocationSearch';
import ApplicationSearch from './components/search/ApplicationSearch';
import { applicationSearchService } from './services/applicationSearchService';
import {
  getLayerCapabilities,
  getLayerDisplayName,
  normaliseGeometryType,
} from './layers/capabilities';
import { getLayerAdapter } from './layers/adapters';
import { detectWfsGeometryType } from './layers/sld';
import {
  settingsService,
  DEFAULT_HOME_CONFIG,
  DEFAULT_HEADER_COLOR,
  DEFAULT_SUBTITLE_COLOR,
  DEFAULT_TITLE_COLOR,
} from './services/settingsService';
import { createId } from './services/storage';
import LoginScreen from './components/LoginScreen';
import SettingsPage from './components/settings/SettingsPage';
import ProfilePage from './components/settings/ProfilePage';
import UserMenu from './components/header/UserMenu';
import NotificationBell from './components/header/NotificationBell';
import { sanitizeRichText } from './utils/sanitizeHtml';
import { motion } from 'framer-motion';
import { Box, Button, IconButton, Paper, Stack, Typography } from '@mui/material';
import {
  Plus,
  X,
  Map,
  LayoutGrid,
  Users,
  FileText,
  ArrowLeft,
  ZoomIn,
  ZoomOut,
  Home,
  LocateFixed,
  Navigation,
  Square,
  Globe2,
  PenLine,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Layers,
  Save,
  Trash2,
  Check,
  Crosshair,
  MoreVertical,
  TableProperties,
  Download,
  Move,
  Share2,
  Droplet,
  ArrowUp,
  ArrowDown,
  Copy,
  RefreshCw,
  Info,
  Palette,
  Type,
  Eye,
  Rocket,
  MessageSquare,
  Printer,
  Loader2,
  SlidersHorizontal,
  FolderPlus,
  FolderMinus,
} from 'lucide-react';

type GeoServerLayer = {
  name: string;
  title: string;
  bounds?: [number, number, number, number];
};

type ArcgisLayer = {
  id: number;
  name: string;
  bounds?: [number, number, number, number];
  /** Raw Esri geometry type, e.g. "esriGeometryPolyline". */
  geometryType?: string;
  /** Scale band, already resolved through the layer's group ancestors. */
  minScale?: number;
  maxScale?: number;
};

type ServiceType = 'geoserver' | 'arcgis';

type AttributeFeature = Record<string, unknown> & {
  id: string;
  properties: Record<string, unknown>;
  geometry?: GeoJSON.Geometry;
};

/**
 * FIXED BRAND CONSTANTS — never editable through Settings, by any role.
 * The Home Page Editor deliberately has no fields for these.
 */
const BRAND = {
  markLeft: 'Blue',
  markRight: 'View',
  poweredByPrefix: 'powered by',
  poweredByName: { lead: 'Map', tail: 'Ge', trail: 'id' },
} as const;

/**
 * A clause row's id, for the Filter and Query tools.
 *
 * `Date.now()` (millisecond resolution) is what this replaces: the Filter
 * and Query panels are both mounted at once (one hidden with CSS, not
 * unmounted — see `activeTab`), so their two INITIAL clauses are created
 * within the same render and can land on the identical millisecond,
 * silently making them share one row of `queryFieldValues` /
 * `queryFieldValuesLoading` — value suggestions meant for a Query field
 * showing up on an unrelated Filter clause, or vice versa. A monotonic
 * counter can't collide no matter how close together two clauses are born.
 */
let clauseIdCounter = 0;
function nextClauseId(): number {
  clauseIdCounter += 1;
  return clauseIdCounter;
}

/**
 * Tab labels, taken FROM the registry rather than kept beside it.
 *
 * The hand-written map this replaces held eight entries — the eight tools that
 * happened to have a panel — so every other tool's tab showed its raw id
 * ("basemapGallery", "attributeTable"). One source of truth means a tool can
 * never again be named in two places and disagree.
 */
const TOOL_LABELS: Record<string, string> = Object.fromEntries(
  TOOL_REGISTRY.map((tool) => [tool.id, tool.name]),
);

const MODULE_ICONS: { match: string[]; icon: LucideIcon }[] = [
  { match: ['map'], icon: Map },
  { match: ['dashboard', 'analytic'], icon: LayoutGrid },
  { match: ['user'], icon: Users },
  { match: ['construction', 'report', 'document'], icon: FileText },
];

/** "#RRGGBB" -> "r, g, b", the form glass.css's `--bv-*-rgb` tokens need for
 *  `rgba(var(--bv-primary-rgb), 0.1)`-style rules. Empty string on a bad hex,
 *  so a malformed saved value drops the override instead of breaking the CSS. */
function hexToRgbTriplet(hex: string): string {
  const match = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!match) return '';
  const int = parseInt(match[1], 16);
  return `${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}`;
}

/**
 * White or near-navy text, whichever reads on top of `hex` — WCAG's own
 * relative-luminance formula, the same one used to pick body text against an
 * arbitrary background. `.gl-btn--primary`'s text was hardcoded white,
 * matching the default blue; a custom primary colour light enough to pick
 * (a pale yellow, or white itself) then rendered genuinely invisible white
 * text on a near-white button — not a styling quibble, a button an admin
 * could no longer find. Falls back to white on anything unparsed, same as
 * the colour simply not being set at all.
 */
function contrastTextFor(hex: string): string {
  const match = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!match) return '#fff';
  const int = parseInt(match[1], 16);
  const channel = (shift: number) => {
    const c = ((int >> shift) & 255) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(16) + 0.7152 * channel(8) + 0.0722 * channel(0);
  // 0.5 rather than WCAG's usual 0.179 threshold: this is choosing between
  // two FIXED colours (white vs. the app's own navy), not pass/failing a
  // contrast ratio, so the switch wants to land where a person would
  // actually call the button "light" rather than right at white text's own
  // legal minimum.
  return luminance > 0.5 ? '#0F2942' : '#fff';
}

/**
 * The inline CSS custom properties an `AppColorTheme`'s custom colours
 * translate to. Shared between the builder preview and the published viewer
 * so the two can never drift — see AppModule.colorTheme's own doc for why
 * `--popup-accent` follows `primaryColor` too.
 */
function colorThemeVars(colorTheme: AppModule['colorTheme']): React.CSSProperties {
  const vars: Record<string, string> = {};
  if (colorTheme?.primaryColor) {
    const rgb = hexToRgbTriplet(colorTheme.primaryColor);
    vars['--bv-primary'] = colorTheme.primaryColor;
    if (rgb) vars['--bv-primary-rgb'] = rgb;
    vars['--popup-accent'] = colorTheme.primaryColor;
    // See contrastTextFor's own doc: .gl-btn--primary's text was hardcoded
    // white, which a light enough custom primary colour turned invisible.
    vars['--bv-primary-contrast'] = contrastTextFor(colorTheme.primaryColor);
  }
  if (colorTheme?.accentColor) {
    const rgb = hexToRgbTriplet(colorTheme.accentColor);
    vars['--bv-accent'] = colorTheme.accentColor;
    if (rgb) vars['--bv-accent-rgb'] = rgb;
  }
  return vars as React.CSSProperties;
}

function pickIcon(name: string) {
  const lower = name.toLowerCase();
  const found = MODULE_ICONS.find((m) => m.match.some((k) => lower.includes(k)));
  return found ? found.icon : Map;
}

function freshLayers(): Layer[] {
  return [];
}

/** A module created by `owner`. Admin-created modules land in the admin
 *  workspace; user-created modules are owned by (and private to) that user. */
function makeModule(name: string, owner: User): AppModule {
  return {
    id: createId('mod'),
    name,
    title: name,
    built: false,
    layers: freshLayers(),
    tools: [],
    geoServerUrl: '',
    geoServerLayers: [],
    arcgisUrl: '',
    arcgisLayers: [],
    homeCenter: [28.7041, 77.1025],
    homeZoom: 12,
    ownerId: owner.id,
    visibility: owner.role === 'admin' ? 'admin-workspace' : 'user-owned',
    createdAt: new Date().toISOString(),
  };
}

/** Normalises an ArcGIS REST endpoint down to its MapServer/FeatureServer root,
 * tolerating the trailing `/export`, `/<layerId>`, or query string users paste in. */
function getArcgisBaseUrl(arcgisUrl: string) {
  try {
    const url = new URL(arcgisUrl.trim());
    let path = url.pathname.replace(/\/+$/, '');
    path = path.replace(/\/(export|exportImage|legend|layers)$/i, '');
    path = path.replace(/\/\d+$/, '');
    url.pathname = path;
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return '';
  }
}

/** ArcGIS reports extents in the service's own SR — usually Web Mercator, not WGS84.
 * Converts an ArcGIS extent to the [west, south, east, north] lat/lng tuple the rest
 * of the app (and getLayerZoom) expects. Returns undefined for SRs we can't convert. */
function getArcgisExtentBounds(extent: any): [number, number, number, number] | undefined {
  if (!extent) return undefined;

  const { xmin, ymin, xmax, ymax } = extent;
  if (![xmin, ymin, xmax, ymax].every((value) => Number.isFinite(value))) return undefined;

  const wkid = extent.spatialReference?.latestWkid ?? extent.spatialReference?.wkid;
  let bounds: [number, number, number, number] | undefined;

  if (wkid === 4326) {
    bounds = [xmin, ymin, xmax, ymax];
  } else if (wkid === 102100 || wkid === 3857 || wkid === 900913) {
    const EARTH_HALF_CIRCUMFERENCE = 20037508.34;
    const toLon = (x: number) => (x / EARTH_HALF_CIRCUMFERENCE) * 180;
    const toLat = (y: number) => {
      const lat = (y / EARTH_HALF_CIRCUMFERENCE) * 180;
      return (180 / Math.PI) * (2 * Math.atan(Math.exp((lat * Math.PI) / 180)) - Math.PI / 2);
    };
    bounds = [toLon(xmin), toLat(ymin), toLon(xmax), toLat(ymax)];
  }

  if (!bounds) return undefined;

  // Services publish junk extents surprisingly often (values far outside the
  // projection's valid range); zooming to those throws the map somewhere useless.
  const [west, south, east, north] = bounds;
  const isSane =
    west >= -180 && east <= 180 && south >= -90 && north <= 90 && west < east && south < north;

  return isSane ? bounds : undefined;
}

/** MapServer supports `export` (raster); FeatureServer only supports `query`
 * (vector) — they need completely different rendering paths. */
function getArcgisServiceKind(arcgisUrl: string): 'MapServer' | 'FeatureServer' | null {
  if (/\/FeatureServer/i.test(arcgisUrl)) return 'FeatureServer';
  if (/\/MapServer/i.test(arcgisUrl)) return 'MapServer';
  return null;
}

/** How long before a token's real expiry to renew it. Generous on purpose —
 *  a renewal only costs one background request, and there is no downside to
 *  running it a few days early; there is a real one to leaving it until the
 *  last moment, since a viewer's map would then break exactly while nobody
 *  is looking at it closely enough to notice why. */
const ARCGIS_TOKEN_RENEWAL_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;

/**
 * Silently renews a module's ArcGIS connection token when it is close to
 * expiring, and persists the refresh so it sticks for the next person too —
 * used by both `Builder` and `EndUserViewer`, each on their own mount.
 *
 * `arcgisTokenExpiresAt === undefined` (never recorded) is treated as due
 * immediately — either a module saved before this existed, or a connection
 * that has never actually been resolved yet. Once resolved, a connection
 * with no real token (Basic Auth fallback — see `_resolve_arcgis_url` on
 * the backend) stores `null` explicitly, which this then leaves alone
 * forever: retrying a server that has already said it has no token to give
 * would just be a wasted request on every single open.
 *
 * Failures are swallowed to a console warning — this runs unattended, and a
 * renewal that could not complete must not interrupt anyone's map. Whatever
 * token is already embedded in `arcgisUrl` keeps working exactly as it did
 * a moment ago; only a genuinely EXPIRED one would actually break, and by
 * then the next attempt (next open, still within the module's cached
 * lifetime) tries again.
 */
async function renewArcgisConnectionIfNeeded(
  module: Pick<AppModule, 'id' | 'arcgisConnectionId' | 'arcgisConnectionService' | 'arcgisTokenExpiresAt'>,
  onRenewed: (arcgisUrl: string, expiresAt: string | null) => void,
): Promise<void> {
  if (!module.arcgisConnectionId) return;

  const expiresAt = module.arcgisTokenExpiresAt;
  if (expiresAt !== undefined) {
    if (expiresAt === null) return; // resolved before; this connection has no token to renew
    const dueInMs = new Date(expiresAt).getTime() - Date.now();
    if (Number.isFinite(dueInMs) && dueInMs >= ARCGIS_TOKEN_RENEWAL_WINDOW_MS) return; // not due yet
  }

  try {
    const resolved = await moduleService.renewArcgisConnection(module.id);
    const nextExpiresAt = resolved.expiresAt ?? null;
    onRenewed(resolved.authenticatedBaseUrl, nextExpiresAt);
    await moduleService.patchArcgisConnection(module.id, {
      arcgisUrl: resolved.authenticatedBaseUrl,
      arcgisConnectionId: module.arcgisConnectionId,
      arcgisConnectionService: module.arcgisConnectionService ?? null,
      arcgisTokenExpiresAt: nextExpiresAt,
    });
  } catch (error) {
    console.warn(`[arcgis] could not renew the connection token for module "${module.id}"`, error);
  }
}

function getLayerZoom(bounds?: [number, number, number, number]) {
  if (!bounds) return null;
  const [west, south, east, north] = bounds;
  const extent = Math.max(Math.abs(east - west), Math.abs(north - south), 0.01);
  return {
    center: [(south + north) / 2, (west + east) / 2] as [number, number],
    zoom: Math.max(4, Math.min(16, Math.floor(Math.log2(360 / extent) + 1))),
  };
}

/** The box that contains every given box, for framing a batch of layers. */
function unionBounds(boxes: Array<[number, number, number, number] | undefined>) {
  const known = boxes.filter(
    (box): box is [number, number, number, number] => Array.isArray(box),
  );
  if (known.length === 0) return undefined;
  return known.reduce(
    (current, next) =>
      [
        Math.min(current[0], next[0]),
        Math.min(current[1], next[1]),
        Math.max(current[2], next[2]),
        Math.max(current[3], next[3]),
      ] as [number, number, number, number],
  );
}

/**
 * Zoom ↔ scale, in the units services publish their limits in.
 *
 * The constant is Esri's own level-0 denominator for a 256px tile at 96dpi,
 * which is also the map's own tile geometry — so these numbers can be
 * compared directly against a layer's minScale/maxScale.
 */
const SCALE_AT_ZOOM_0 = 591657527.591555;
const scaleForZoom = (zoom: number) => SCALE_AT_ZOOM_0 / 2 ** zoom;
const zoomForScale = (scale: number) => Math.log2(SCALE_AT_ZOOM_0 / scale);

/** The lowest zoom at which a scale band draws, or 0 when it has no floor. */
function minZoomForBand(minScale?: number) {
  return minScale ? Math.ceil(zoomForScale(minScale)) : 0;
}

/** Picks the tighter of two 0-means-unbounded bounds — 0 when neither is set. */
function tighterBound(a: number | undefined, b: number | undefined, pick: (x: number, y: number) => number) {
  const av = a || 0;
  const bv = b || 0;
  return av && bv ? pick(av, bv) : av || bv;
}

/**
 * The scale band that actually governs a layer: the service's own
 * minScale/maxScale (if any) intersected with whatever admin-set
 * `visibleScale` range is enabled. Every scale-band decision below reads
 * this rather than either source alone, so a layer can be restricted by
 * either — or both at once — and the tighter limit wins.
 */
function effectiveScaleBand(layer: Pick<Layer, 'minScale' | 'maxScale' | 'visibleScale'>) {
  const admin = layer.visibleScale?.enabled ? layer.visibleScale : undefined;
  return {
    // The zoomed-out bound: the SMALLER of the two ceilings is more restrictive.
    minScale: tighterBound(layer.minScale, admin?.minScale, Math.min),
    // The zoomed-in bound: the LARGER of the two floors is more restrictive.
    maxScale: tighterBound(layer.maxScale, admin?.maxScale, Math.max),
  };
}

/**
 * Whether this layer draws at this zoom.
 *
 * Esri's naming reads backwards until you hold it the right way up: minScale
 * is the SMALLEST-scale (most zoomed OUT) map the layer appears on, so it
 * draws while scale <= minScale. 0 means "no limit" at that end.
 */
function isVisibleAtZoom(layer: Pick<Layer, 'minScale' | 'maxScale' | 'visibleScale'>, zoom: number) {
  const { minScale, maxScale } = effectiveScaleBand(layer);
  const scale = scaleForZoom(zoom);
  if (minScale && scale > minScale) return false;
  if (maxScale && scale < maxScale) return false;
  return true;
}

/**
 * Where to put the map for a layer: its extent, pulled inside its scale band.
 *
 * Framing the extent alone is what made a scale-banded service look broken.
 * Esri's own Water_Network_Base_Map is the canonical example — its "_1K"
 * layers cover a few square miles, so the extent frames at about z14, and the
 * service refuses to draw them above 1:1,950 (roughly z19). The layer arrived
 * in the TOC, the map moved, and nothing appeared.
 */
function getLayerView(layer: Pick<Layer, 'bounds' | 'minScale' | 'maxScale' | 'visibleScale'>) {
  const view = getLayerZoom(layer.bounds);
  if (!view) return null;

  const { minScale, maxScale } = effectiveScaleBand(layer);
  let zoom = Math.max(view.zoom, minZoomForBand(minScale));
  // maxScale caps how far IN the layer draws, so it lowers the ceiling. Applied
  // second and never below the floor: a band narrower than one zoom level
  // cannot be satisfied, and being too far in beats being too far out.
  if (maxScale) {
    zoom = Math.max(minZoomForBand(minScale), Math.min(zoom, Math.floor(zoomForScale(maxScale))));
  }
  return { center: view.center, zoom: Math.max(0, Math.min(22, zoom)) };
}

/** Plain-language reason a layer is not on the map right now. */
function scaleBandHint(layer: Pick<Layer, 'minScale' | 'maxScale' | 'visibleScale'>) {
  const { minScale, maxScale } = effectiveScaleBand(layer);
  const round = (value: number) => Math.round(value).toLocaleString();
  if (minScale && maxScale) {
    return `This layer only draws between 1:${round(maxScale)} and 1:${round(
      minScale,
    )}. Select to move there.`;
  }
  if (minScale) {
    return `This layer only draws at 1:${round(minScale)} and closer. Select to zoom in.`;
  }
  return `This layer only draws at 1:${round(maxScale ?? 0)} and wider. Select to zoom out.`;
}

/**
 * One scale bound of a layer's visibility range.
 *
 * Free text rather than a number input, mirroring the label scale-visibility
 * box: "1:25,000" can be pasted straight from ArcGIS, and the value is only
 * normalised on blur so typing the second digit of "25000" does not reformat
 * the field under the cursor.
 */
function ScaleBoundInput({
  label,
  value,
  currentScale,
  onChange,
}: {
  label: string;
  value: number;
  currentScale: number;
  onChange: (value: number) => void;
}) {
  const [text, setText] = useState(value > 0 ? formatScale(value) : '');
  useEffect(() => {
    setText(value > 0 ? formatScale(value) : '');
  }, [value]);

  return (
    <label className="block">
      <span className="flex items-center justify-between text-[10.5px] text-slate-500">
        {label}
        <button
          type="button"
          onClick={() => onChange(Math.round(currentScale))}
          className="text-[10px] font-semibold text-blue-600 hover:text-blue-800"
        >
          Use current
        </button>
      </span>
      <input
        type="text"
        inputMode="numeric"
        value={text}
        placeholder="No limit"
        aria-label={label}
        onChange={(event) => setText(event.target.value)}
        onBlur={() => onChange(parseScale(text))}
        className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11.5px] outline-none focus:border-blue-400"
      />
    </label>
  );
}

/**
 * A WFS GetFeature request for a layer.
 *
 * `count` is a parameter because 100 is the right size for a quick look and
 * the wrong size for collecting a field's distinct values — a list built from
 * the first hundred rows of a large layer quietly omits most of what the field
 * actually holds.
 */
function getWfsUrl(geoServerUrl: string, layerName: string, count = 100, cqlFilter?: string) {
  try {
    const url = new URL(geoServerUrl.trim());
    const path = url.pathname.replace(/\/+$/, '');
    url.pathname = /\/(wms|wfs)$/i.test(path) ? path.replace(/\/(wms|wfs)$/i, '/wfs') : `${path}/wfs`;
    url.search = '';
    url.searchParams.set('service', 'WFS');
    url.searchParams.set('request', 'GetFeature');
    url.searchParams.set('version', '2.0.0');
    url.searchParams.set('typeNames', layerName);
    url.searchParams.set('outputFormat', 'application/json');
    // Without this, a layer whose STORED (native) CRS is not WGS84 comes back
    // in that native CRS — every reader of this geometry (Query, Select-by-
    // box, "zoom to") assumes EPSG:4326 lon/lat, so the feature lands nowhere
    // near its real position for exactly such a layer. See
    // featureSearchService.ts, the one other WFS GetFeature caller that
    // already set this.
    url.searchParams.set('srsName', 'EPSG:4326');
    url.searchParams.set('count', String(count));
    // A layer's filter travels with every read of it, so a selection box and
    // the map agree on what exists.
    if (cqlFilter?.trim()) url.searchParams.set('cql_filter', cqlFilter.trim());
    return url.toString();
  } catch {
    return '';
  }
}

function getGeometryBounds(geometry?: GeoJSON.Geometry | null): [number, number, number, number][] {
  if (!geometry) return [];

  const addPoint = (lon: number, lat: number, current: [number, number, number, number] | null) => {
    if (!current) return [lon, lat, lon, lat] as [number, number, number, number];
    return [
      Math.min(current[0], lon),
      Math.min(current[1], lat),
      Math.max(current[2], lon),
      Math.max(current[3], lat),
    ] as [number, number, number, number];
  };

  switch (geometry.type) {
    case 'Point':
      return [[geometry.coordinates[0], geometry.coordinates[1], geometry.coordinates[0], geometry.coordinates[1]]];
    case 'MultiPoint':
    case 'LineString': {
      let bounds: [number, number, number, number] | null = null;
      for (const coord of geometry.coordinates as GeoJSON.Position[]) {
        bounds = addPoint((coord as [number, number])[0], (coord as [number, number])[1], bounds);
      }
      return bounds ? [bounds] : [];
    }
    case 'MultiLineString':
    case 'Polygon':
      return (geometry.coordinates as GeoJSON.Position[][]).flatMap((part) =>
        getGeometryBounds({ type: 'LineString', coordinates: part } as GeoJSON.LineString),
      );
    case 'MultiPolygon':
      return (geometry.coordinates as GeoJSON.Position[][][]).flatMap((polygon) =>
        getGeometryBounds({ type: 'Polygon', coordinates: polygon } as GeoJSON.Polygon),
      );
    case 'GeometryCollection':
      return geometry.geometries.flatMap(getGeometryBounds);
    default:
      return [];
  }
}

/** How long the map-contents sidebar takes to fold away. */
const TOC_TRANSITION_MS = 200;

function TableOfContents({
  layers,
  geoServerUrl,
  arcgisUrl = '',
  onToggleLayer,
  onToggleExpand,
  onZoomToLayer,
  onZoomToSelection,
  drawType,
  onDrawTypeChange,
  onStartDrawing,
  sketchVertices = 0,
  onFinishShape,
  onStopDrawing,
  onMeasureModeChange,
  onSelectModeChange,
  onPrintFrameRatioChange,
  selectedFeatures = null,
  onMeasurementsChange,
  onSelectionChange,
  getSelection,
  measuredFeature = null,
  onClearAll,
  onDeleteDrawnFeature,
  onZoomToDrawnFeature,
  onSaveDrawings,
  onCloseDrawPanel,
  isDrawing,
  tools,
  openTools,
  activeToolTab,
  onToolTabChange,
  onToolClose,
  onToolOpen,
  mapCenter,
  mapZoom,
  onGoToLocation,
  module,
  onLayerChange,
  onLayerRemove,
  onLayerDuplicate,
  onLayerMove,
  onLayerRefresh,
  onOpenSymbology,
  onOpenLabeling,
  onOpenPopup,
  onToggleLabels,
  onOpacityPreview,
  onOpenAttributeTable,
  canManageLayers = true,
  toolConfigs = {},
  basemap = 'street',
  onBasemapChange,
  pointer = null,
  onAddData,
  onOverlayChange,
  onCreateTempLayer,
  onCreateTempRasterLayer,
  onCreateGroup,
  onSwipeChange,
  swipeLayerId = null,
  swipeVertical = false,
  onEditModeChange,
  editSketchResult = null,
  onDraftGeometryChange,
  onMoveGeometryChange,
  moveResult = null,
  snapEnabled = false,
  onSnapEnabledChange,
}: {
  layers: Layer[];
  geoServerUrl: string;
  arcgisUrl?: string;
  onToggleLayer: (id: string) => void;
  onToggleExpand: (id: string) => void;
  onZoomToLayer: (layer: Layer) => void;
  onZoomToSelection?: (features: AttributeFeature[]) => void;
  drawType?: 'point' | 'line' | 'polygon' | 'circle';
  onDrawTypeChange?: (type: 'point' | 'line' | 'polygon' | 'circle') => void;
  onStartDrawing?: () => void;
  /** Vertices in the shape being drawn, so Finish can be offered and enabled. */
  sketchVertices?: number;
  onFinishShape?: () => void;
  /** Stops a sketch in progress when the Measure tool takes over the map. */
  onStopDrawing?: () => void;
  /**
   * Tells the host which shape the Measure tool wants sketched, or null when it
   * is off. The host owns the map, so it decides what the click does; the panel
   * only says what it is waiting for.
   */
  onMeasureModeChange?: (mode: 'line' | 'polygon' | null, imperial?: boolean) => void;
  /** What shape the Select tool wants sketched, or null when it is off. */
  onSelectModeChange?: (
    mode: 'rectangle' | 'polygon' | 'circle' | null,
    layerIds?: string[],
    combine?: 'new' | 'add' | 'remove',
  ) => void;
  /** The Print tool's page map-frame width:height, or null while it is
   *  closed — lets the map show the red extent-preview box at the same
   *  ratio `buildSheet` will crop its capture to. */
  onPrintFrameRatioChange?: (ratio: number | null) => void;
  /** What the last selection shape caught, tagged by the layer it came from. */
  selectedFeatures?: { token: number; rows: AttributeFeature[] } | null;
  /** The measurements to keep drawn on the map, with their readings. */
  onMeasurementsChange?: (shapes: GeoJSON.Feature[]) => void;
  /**
   * Publishes what is currently chosen — by the Query tool here, by the
   * attribute table elsewhere. One channel, so the map highlight, the table's
   * ticks and Zoom to selected all read the same set.
   */
  onSelectionChange?: (features: AttributeFeature[]) => void;
  /** Reads the current selection, for tools that combine with it. */
  getSelection?: () => AttributeFeature[];
  /** A finished measuring sketch, handed back for the panel to turn into a
   *  number. Carries a token so the same shape twice still counts twice. */
  measuredFeature?: { token: number; feature: GeoJSON.Feature } | null;
  onClearAll?: () => void;
  /** Removes one feature (by its index in the Drawings layer) without
   *  disturbing the rest of the sketch pad. */
  onDeleteDrawnFeature?: (index: number) => void;
  /** Frames one drawn feature, the same "zoom + blink" treatment the
   *  attribute table's own Zoom to selected uses. */
  onZoomToDrawnFeature?: (feature: GeoJSON.Feature) => void;
  /** Promotes the sketch pad from a private, temporary layer into a real
   *  layer on the map, so it is included the next time the module is saved.
   *  Absent in the read-only viewer — a reader's drawings stay their own. */
  onSaveDrawings?: () => void;
  onCloseDrawPanel?: () => void;
  isDrawing?: boolean;
  tools: string[];
  openTools?: string[];
  activeToolTab?: string;
  onToolTabChange?: (tab: string) => void;
  onToolClose?: (toolId: string) => void;
  /** Opens (and switches to) a tool tab that may not already be in
   *  `openTools` — Near Me's "Directions from here" needs this to actually
   *  land the admin on the Directions tab, not just switch to a tab that
   *  isn't open and so immediately bounces back to Layers. */
  onToolOpen?: (toolId: string) => void;
  mapCenter: [number, number];
  mapZoom: number;
  onGoToLocation: (center: [number, number], zoom: number) => void;
  /** Supplies service URLs used for capability detection + adapter calls. */
  module?: AppModule;
  /** Layer-management callbacks. Omitted in read-only contexts (viewer). */
  onLayerChange?: (layerId: string, changes: Partial<Layer>) => void;
  onLayerRemove?: (layerId: string) => void;
  onLayerDuplicate?: (layerId: string) => void;
  onLayerMove?: (layerId: string, direction: -1 | 1) => void;
  onLayerRefresh?: () => void;
  /** Opens the right-side symbology panel (owned by the Builder, over the map). */
  onOpenSymbology?: (layerId: string) => void;
  /** Opens the right-side Label Properties panel. Same ownership as symbology. */
  onOpenLabeling?: (layerId: string) => void;
  /** Opens the right-side Pop-up Properties panel. Same ownership again. */
  onOpenPopup?: (layerId: string) => void;
  /**
   * Flips a layer's labels on or off, with no panel.
   *
   * Supplied in the read-only viewer, where end users cannot configure labels
   * but should still be able to clear the map when the text gets in the way.
   */
  onToggleLabels?: (layerId: string) => void;
  /**
   * Sets a layer's opacity for THIS viewing session only, with no panel — the
   * read-only viewer's equivalent of `onLayerChange`'s opacity write. Like
   * `onToggleLabels`, supplied only where end users cannot edit the saved
   * module: the slider still needs somewhere to write to, it just never
   * reaches the module, so a refresh puts the layer back exactly as the
   * Admin configured it.
   */
  onOpacityPreview?: (layerId: string, opacity: number) => void;
  /**
   * Asks the map shell to open the floating attribute table.
   * The window is NOT rendered here: the TOC is a glass surface, and a
   * `position: fixed` child of a `backdrop-filter` ancestor is clipped into
   * that ancestor's box instead of floating over the map.
   */
  onOpenAttributeTable?: (layer: Layer) => void;
  /** False for shared/read-only modules — mutating options are hidden. */
  canManageLayers?: boolean;
  /**
   * Per-tool settings from the module. Passed in so panels can HONOUR the
   * questions the Tool Library asked — before this they were saved and then
   * ignored, which made every setting in the dialog decorative.
   */
  toolConfigs?: Record<string, Record<string, unknown>>;
  basemap?: BasemapId;
  onBasemapChange?: (basemap: BasemapId) => void;
  /** Last point clicked on the map; the Identify and Coordinates tools read it. */
  pointer?: MapPoint | null;
  onAddData?: () => void;
  /** Fleeting geometry a tool draws — a direction line. */
  onOverlayChange?: (overlay: GeoJSON.FeatureCollection | null) => void;
  /** Adds a tool's result to the TOC as this person's own temporary layer. */
  onCreateTempLayer?: (
    name: string,
    data: GeoJSON.FeatureCollection,
    source: string,
    renderer?: Layer['renderer'],
    /** Density tool only — draws as a heatmap instead of `renderer` above. */
    heatmap?: HeatmapConfig,
  ) => string;
  /** The Georeference tool's own raster twin of `onCreateTempLayer` above —
   *  the only way a panel can add a brand-new RASTER layer; `onCreateTempLayer`
   *  only ever takes a vector `FeatureCollection`. */
  onCreateTempRasterLayer?: (
    name: string,
    raster: LocalRasterData,
    bounds: [number, number, number, number],
    source: string,
  ) => string;
  /** Adds a new, empty TOC folder — admin-only organisation, no data of its own. */
  onCreateGroup?: (name: string) => void;
  onSwipeChange?: (swipe: { layerId: string | null; vertical: boolean }) => void;
  swipeLayerId?: string | null;
  swipeVertical?: boolean;
  /** Arms/disarms the Edit tool's "New feature" sketch — a fourth mode
   *  alongside Draw/Measure/Select, sharing the same map controller. */
  onEditModeChange?: (shape: 'point' | 'line' | 'polygon' | null) => void;
  /** The shape that sketch produced, for the Edit panel that armed it. */
  editSketchResult?: { token: number; feature: GeoJSON.Feature } | null;
  /** Shows the Edit tool's open record's own geometry on the map as a
   *  highlighted, dashed, non-interactive preview — a brand-new sketch, the
   *  feature being reshaped (Esri's own Reshape Feature tool, not
   *  vertex-dragging — the sketch itself comes back through
   *  `editSketchResult`/`sketchedFeature` like any other sketch), or a
   *  feature mid-move would otherwise have no on-map representation at all
   *  until Save. Pass null to take the preview back off. */
  onDraftGeometryChange?: (geometry: GeoJSON.Geometry | null) => void;
  /** Puts a feature's geometry on the map as a single draggable whole, or
   *  null to take it back off — the Edit tool's "Move" flow. */
  onMoveGeometryChange?: (geometry: GeoJSON.Geometry | null) => void;
  /** The geometry after a move-drag finished. */
  moveResult?: { token: number; geometry: GeoJSON.Geometry } | null;
  /** Whether the Edit tool's sketching/reshaping snaps to nearby features. */
  snapEnabled?: boolean;
  onSnapEnabledChange?: (enabled: boolean) => void;
}) {
  const { currentUser } = useAuth();
  const { can } = usePermissions();
  const [internalActiveTab, setInternalActiveTab] = useState<string>('layers');
  const [menuLayerId, setMenuLayerId] = useState<string | null>(null);
  const [renamingLayerId, setRenamingLayerId] = useState<string | null>(null);
  /** Layer whose "Share this layer" dialog is open — the owner's own grant
   *  list for that ONE layer, see LayerShareDialog. */
  const [sharingLayerId, setSharingLayerId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [opacityLayerId, setOpacityLayerId] = useState<string | null>(null);
  const [visibleRangeLayerId, setVisibleRangeLayerId] = useState<string | null>(null);
  /** Layer currently choosing which group to join — the inline picker opened
   *  from its 3-dot menu's "Move to group". */
  const [groupPickerLayerId, setGroupPickerLayerId] = useState<string | null>(null);
  const [renamingGroupId, setRenamingGroupId] = useState<string | null>(null);
  const [groupRenameDraft, setGroupRenameDraft] = useState('');
  const [addingGroup, setAddingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [infoLayer, setInfoLayer] = useState<Layer | null>(null);
  const [infoRows, setInfoRows] = useState<{ label: string; value: string }[] | null>(null);
  /** A shared layer's recent edits, shown under "Layer information" — null
   *  while loading, empty once loaded with nothing yet, undefined for a
   *  layer that is not shared at all (no section rendered). */
  const [infoEdits, setInfoEdits] = useState<SharedLayerEdit[] | null | undefined>(undefined);
  const [layerNotice, setLayerNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const menuAnchorRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const tabScrollRef = useRef<HTMLDivElement | null>(null);
  const [showTabScrollLeft, setShowTabScrollLeft] = useState(false);
  const [showTabScrollRight, setShowTabScrollRight] = useState(false);

  /* ── Layer groups ────────────────────────────────────────────────────────
     A group is an entry in the SAME flat `layers` array (`isGroup: true`, no
     data of its own); a layer joins one by carrying that group's id in
     `groupId`. Kept flat rather than nested arrays so every place that
     already iterates `layers` — persistence, the map-visibility memos, the
     legend, print — needed no shape change; only the TOC's own rendering and
     menu had to learn the new field. */
  const groups = useMemo(() => layers.filter((layer) => layer.isGroup), [layers]);
  const childrenOf = useCallback(
    (groupId: string) => layers.filter((layer) => layer.groupId === groupId),
    [layers],
  );
  /** Ids of every ungrouped layer and every group, in their own order — the
   *  population "Move up"/"Move down" reasons about, since a grouped child
   *  is not part of that ordering at all. */
  const topLevelIds = useMemo(
    () => layers.filter((layer) => !layer.groupId).map((layer) => layer.id),
    [layers],
  );

  /**
   * Render order for the Layers tab: every top-level entry (an ungrouped
   * layer, or a group) in its normal position, and — right after an
   * EXPANDED group — its children, wherever they actually sit in `layers`.
   * A child's array position does not matter for display, only `groupId`
   * does, so moving a layer into a group is one field change, not a reorder.
   */
  const layerRows = useMemo(() => {
    const rows: { layer: Layer; nested: boolean }[] = [];
    for (const layer of layers) {
      if (layer.groupId) continue; // shown under its group instead, below
      rows.push({ layer, nested: false });
      if (layer.isGroup && layer.expanded) {
        for (const child of layers.filter((entry) => entry.groupId === layer.id)) {
          rows.push({ layer: child, nested: true });
        }
      }
    }
    return rows;
  }, [layers]);

  /**
   * A group's checkbox is a master switch for the whole group, but it must
   * NOT overwrite any child's own `checked` — a child individually turned
   * off before the group was must stay off when the group comes back on,
   * and a published Map Viewer cannot mutate a child's `checked` at all
   * (see `changeTemporaryLayer`'s `VIEWER_EDITABLE_KEYS`), so writing
   * through children here silently did nothing there anyway. Instead, the
   * map-visibility memos (`visibleArcgisLayers`, `localLayers`, etc., in
   * both Builder and EndUserViewer) each additionally check the PARENT
   * group's own `checked` via their own `groupVisible` — so flipping only
   * this one field is enough to hide or show the whole group everywhere,
   * children's individual state untouched either way.
   */
  const toggleGroup = (group: Layer) => {
    onToggleLayer(group.id);
  };

  const createGroup = () => {
    const name = newGroupName.trim();
    if (!name) return;
    onCreateGroup?.(name);
    setNewGroupName('');
    setAddingGroup(false);
  };

  const commitGroupRename = (group: Layer) => {
    const trimmed = groupRenameDraft.trim();
    if (trimmed) onLayerChange?.(group.id, { name: trimmed });
    setRenamingGroupId(null);
  };

  /**
   * Ungroups every child and removes the now-empty folder. Never deletes a
   * layer — a group is organisation, not a container something can be lost
   * inside of by mistake.
   */
  const removeGroup = (group: Layer) => {
    for (const child of childrenOf(group.id)) {
      onLayerChange?.(child.id, { groupId: undefined });
    }
    onLayerRemove?.(group.id);
  };

  const moveToGroup = (layerId: string, groupId: string | null) => {
    onLayerChange?.(layerId, { groupId: groupId ?? undefined });
    setGroupPickerLayerId(null);
  };

  const selectedDrawType = drawType ?? 'point';
  /* Which sketch shapes this app offers. Empty configuration means all four,
     the same "empty means everything" rule the layer scoping uses. */
  const drawShapes = (() => {
    const chosen = toolConfigWithDefaults('draw', toolConfigs.draw).shapes;
    const list = Array.isArray(chosen) ? (chosen as string[]) : [];
    return list.length > 0 ? list : ['point', 'line', 'polygon', 'circle'];
  })();
  const currentlyDrawing = isDrawing ?? false;
  const activeTab = activeToolTab ?? internalActiveTab;

  /* Seeded from the tool's configuration rather than hard-coded, so the layout
     an admin chose is the one the panel opens on. Lazy initialisers: the
     settings arrive with the module, before the first render. */
  const printDefaults = toolConfigWithDefaults('print', toolConfigs.print);
  const [printOrientation, setPrintOrientation] = useState<'Portrait' | 'Landscape'>(
    String(printDefaults.layout ?? '').includes('portrait') ? 'Portrait' : 'Landscape',
  );
  const [printPageSize, setPrintPageSize] = useState<'A4' | 'A3' | 'Letter'>('A4');
  const [printTitle, setPrintTitle] = useState(String(printDefaults.title ?? ''));
  const [printSubtitle, setPrintSubtitle] = useState('');
  const [printIncludeLegend, setPrintIncludeLegend] = useState(
    printDefaults.includeLegend !== false,
  );
  const [printScaleBar, setPrintScaleBar] = useState(true);
  const [printNorthArrow, setPrintNorthArrow] = useState(true);
  const [printDate, setPrintDate] = useState(true);
  const [printNote, setPrintNote] = useState<string | null>(null);
  const [printGenerating, setPrintGenerating] = useState(false);

  const [measureType, setMeasureType] = useState<'line' | 'polygon'>('line');
  /* Seeded from the tool's configuration: the Measure tool asks an admin for
     metric or imperial, and that answer used to be saved and ignored. */
  const measureDefaults = toolConfigWithDefaults('measure', toolConfigs.measure);
  const [measureUnit, setMeasureUnit] = useState<'meters' | 'kilometers' | 'feet' | 'miles'>(
    measureDefaults.lengthUnit === 'imperial' ? 'feet' : 'meters',
  );
  const [measureAreaUnit, setMeasureAreaUnit] = useState<
    'sqmeters' | 'sqkilometers' | 'hectares' | 'acres'
  >(
    measureDefaults.areaUnit === 'hectare'
      ? 'hectares'
      : measureDefaults.areaUnit === 'acre'
        ? 'acres'
        : 'sqmeters',
  );
  const [measurements, setMeasurements] = useState<
    { id: number; type: string; value: string; geometry?: GeoJSON.Geometry }[]
  >([]);
  const [isMeasuring, setIsMeasuring] = useState(false);

  const [filterLayer, setFilterLayer] = useState('');
  const [filterLayerFields, setFilterLayerFields] = useState<string[]>([]);
  const [filterFieldsLoading, setFilterFieldsLoading] = useState(false);
  const [filterClauses, setFilterClauses] = useState<{
    id: number;
    field: string;
    operator: string;
    value: string;
    conjunction: string;
    showSuggestions?: boolean;
  }[]>([
    { id: nextClauseId(), field: '', operator: 'is equal to', value: '', conjunction: 'And' },
  ]);
  const [filterInvertWhere, setFilterInvertWhere] = useState(false);
  /* An inline message instead of alert(): a modal dialog to say "fill in a
     condition" stops the whole app to deliver one line. */
  const [filterError, setFilterError] = useState<string | null>(null);
  /** Fields the current layer publishes as numbers; drives filter quoting. */
  const [numericFields, setNumericFields] = useState<Set<string>>(new Set());
  /** Guards `numericFields` against an out-of-order response — see `fetchLayerFields`. */
  const numericFieldsRequestRef = useRef(0);
  /**
   * Layers this tool has filtered.
   *
   * The Filter tool is a way of LOOKING at a map, not a change to it — so its
   * filters live as long as the tool is open and no longer. Tracked by id
   * rather than cleared wholesale, so if a filter is ever set from somewhere
   * else it is not swept away by a tool that did not put it there.
   */
  const filterAppliedRef = useRef<string[]>([]);

  /** Puts every layer this tool filtered back to showing everything. */
  const clearToolFilters = useCallback(() => {
    for (const layerId of filterAppliedRef.current) {
      onLayerChange?.(layerId, {
        filter: undefined,
        filterClauses: undefined,
        filterInvert: undefined,
      });
    }
    filterAppliedRef.current = [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onLayerChange]);

  /* The X is not the only way the panel goes away. Removing the tool from the
     Tool Library, and leaving the map altogether, both take it off screen —
     and a filter with no panel left to explain or undo it is a map quietly
     hiding features for no visible reason. */
  useEffect(() => {
    if (tools.includes('filter')) return;
    clearToolFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tools]);

  useEffect(
    () => () => clearToolFilters(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  /** Layers the map is ACTUALLY filtering, derived rather than remembered. */
  const filteredLayers = layers.filter((layer) => layer.filter?.trim());

  const [queryLayer, setQueryLayer] = useState('');
  const [queryLayerFields, setQueryLayerFields] = useState<string[]>([]);
  const [queryFieldsLoading, setQueryFieldsLoading] = useState(false);
  const [queryClauses, setQueryClauses] = useState<{
    id: number;
    field: string;
    operator: string;
    value: string;
    conjunction: string;
    showSuggestions?: boolean;
  }[]>([
    { id: nextClauseId(), field: '', operator: 'is equal to', value: '', conjunction: 'And' },
  ]);
  const [invertWhere, setInvertWhere] = useState(false);
  const [queryFieldValues, setQueryFieldValues] = useState<Record<number, string[]>>({});
  const [queryFieldValuesLoading, setQueryFieldValuesLoading] = useState<Record<number, boolean>>({});
  const [queryResults, setQueryResults] = useState<GeoJSON.Feature[]>([]);
  const [queryLoading, setQueryLoading] = useState(false);
  /** Set when a service-backed layer hit `loadLayerFeatures`'s own fetch cap
   *  — "Invert" in particular needs the true complement, so this is worth
   *  saying out loud rather than quietly answering from a partial read. */
  const [queryTruncated, setQueryTruncated] = useState(false);

  /* Every shape is offered at runtime. It used to be pinned by a config field,
     which meant an admin decided once, for everyone, which of three equally
     ordinary gestures a person was allowed to use. */
  const [selectMode, setSelectMode] = useState<'rectangle' | 'polygon' | 'circle'>('rectangle');
  /** What the next shape does to what is already selected. */
  const [selectCombine, setSelectCombine] = useState<'new' | 'add' | 'remove'>('new');
  /**
   * The selection, counted per layer.
   *
   * Order follows the layer list rather than the order the reads happened to
   * return, so the same box gives the same reading twice.
   */
  /** Selection counts keyed by layer id, for the picker's badges. */
  const caughtPerLayer = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const row of selectedFeatures?.rows ?? []) {
      const layerId = String(row.layerId ?? '');
      if (layerId) counts[layerId] = (counts[layerId] ?? 0) + 1;
    }
    return counts;
  }, [selectedFeatures]);

  const selectionByLayer = useMemo(() => {
    const rows = selectedFeatures?.rows ?? [];
    // A plain record, not a generic Map: this build's TypeScript rejects
    // `new Map<K, V>()` here (TS2350), the same way it did in loadArcgisLayers.
    const groups: Record<string, AttributeFeature[]> = {};
    for (const row of rows) {
      const layerId = String(row.layerId ?? '');
      if (!layerId) continue;
      (groups[layerId] ||= []).push(row);
    }
    return layers
      .filter((layer) => groups[layer.id]?.length)
      .map((layer) => ({
        layerId: layer.id,
        layerName: getLayerDisplayName(layer),
        rows: groups[layer.id],
        count: groups[layer.id].length,
      }));
  }, [selectedFeatures, layers]);
  /* A LIST, not one id. A selection box does not know about layer boundaries —
     dragging over a junction naturally catches the pipe, the valve and the
     parcel under it, and forcing one layer at a time made the tool ask a
     narrower question than the gesture does. */
  const [selectLayerIds, setSelectLayerIds] = useState<string[]>([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [selectionActive, setSelectionActive] = useState(false);

  const [bookmarkName, setBookmarkName] = useState('');
  const [bookmarks, setBookmarks] = useState<{
    id: number;
    name: string;
    date: string;
    center: [number, number];
    zoom: number;
  }[]>([]);
  /** Non-null while a bookmark's name is being edited inline; holds the draft text. */
  const [renamingBookmarkId, setRenamingBookmarkId] = useState<number | null>(null);
  const [renameBookmarkValue, setRenameBookmarkValue] = useState('');

  /* ── Near me ─────────────────────────────────────────────────────────
     The incident point is a PLACE, not a string. It used to be typed text
     that nothing parsed, feeding a search that returned three hardcoded
     sentences after a fake 700ms delay. */
  const [incidentPoint, setIncidentPoint] = useState<{ lat: number; lng: number } | null>(null);
  const [incidentTyped, setIncidentTyped] = useState('');
  /** True while the next map click will drop the incident flag. */
  const [pickingIncident, setPickingIncident] = useState(false);
  const incidentConsumedRef = useRef('');
  const [assetSearchRadius, setAssetSearchRadius] = useState(
    Number(toolConfigWithDefaults('assetSearch', toolConfigs.assetSearch).radius ?? 500),
  );
  const [assetSearchLoading, setAssetSearchLoading] = useState(false);
  const [assetSearchResults, setAssetSearchResults] = useState<
    {
      label: string;
      layerName: string;
      layerId: string;
      metres: number;
      lat: number;
      lng: number;
      /** The whole feature, not just its nearest point — so clicking a
       *  result can select it (map highlight + attribute table), not only
       *  pan to a bare coordinate. */
      feature: GeoJSON.Feature;
    }[]
  >([]);
  const [assetSearchError, setAssetSearchError] = useState<string | null>(null);
  /** Which layer groups are open in the results list — collapsed by default
   *  so a search against many layers reads as a scannable count-per-layer
   *  list first, not an immediate wall of every feature found. */
  const [expandedNearMeLayers, setExpandedNearMeLayers] = useState<Set<string>>(new Set());
  /** A From/To pair for the Directions tool to pick up — Near Me's own
   *  "Directions from here" button, see ToolContext.directionsSeed. */
  const [directionsSeed, setDirectionsSeed] = useState<
    { token: number; from: { label: string; lat: number; lng: number }; to: { label: string; lat: number; lng: number } } | null
  >(null);
  const directionsSeedTokenRef = useRef(0);

  const [internalVisibleTools, setInternalVisibleTools] = useState<string[]>([]);
  const visibleTools = (openTools ?? internalVisibleTools).filter((toolId) => tools.includes(toolId));

  useEffect(() => {
    setInternalVisibleTools((current) => current.filter((toolId) => tools.includes(toolId)));
  }, [tools]);

  useEffect(() => {
    if (activeTab !== 'layers' && activeTab !== 'legend' && !visibleTools.includes(activeTab)) {
      if (activeToolTab === undefined) {
        setInternalActiveTab('layers');
      }
      onToolTabChange?.('layers');
    }
  }, [visibleTools, activeTab, activeToolTab, onToolTabChange]);

  const setResolvedActiveTab = (tab: string) => {
    if (activeToolTab === undefined) {
      setInternalActiveTab(tab);
    }
    onToolTabChange?.(tab);
  };

  /* What the legend should describe. Split out rather than filtered inline so
     the panel and the "not shown at this zoom" count come from one pass. */
  const legendVisible = layers.filter(
    (layer) => !layer.isGroup && layer.checked && (layer.showInLegend ?? true) && (layer.opacity ?? 1) > 0,
  );
  const legendLayers = legendVisible.filter((layer) => isVisibleAtZoom(layer, mapZoom));
  const legendHiddenByZoom = legendVisible.length - legendLayers.length;

  const updateTabScrollButtons = () => {
    const node = tabScrollRef.current;
    if (!node) {
      setShowTabScrollLeft(false);
      setShowTabScrollRight(false);
      return;
    }

    const maxScrollLeft = Math.max(0, node.scrollWidth - node.clientWidth);
    setShowTabScrollLeft(node.scrollLeft > 4);
    setShowTabScrollRight(node.scrollLeft < maxScrollLeft - 4);
  };

  useEffect(() => {
    updateTabScrollButtons();
    const node = tabScrollRef.current;
    if (!node) return;

    const handleScroll = () => updateTabScrollButtons();
    const handleResize = () => updateTabScrollButtons();

    node.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      node.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [visibleTools.length]);

  /**
   * Builds the 3-dot menu for a layer from its detected capabilities.
   * Options the source cannot perform are never added, and mutating options
   * are omitted entirely when the user lacks layer-management permission.
   */
  const buildLayerMenu = (layer: Layer, index: number): LayerMenuItem[] => {
    if (!module) return [];

    /* A group has no service and no data — `getLayerCapabilities` cannot
       classify it (it would default to 'wms' and offer Symbology,
       Transparency and the rest for a folder that draws nothing), so it
       gets its own tiny menu instead of falling through to that logic. */
    if (layer.isGroup) {
      if (!canManageLayers || !onLayerChange) return [];
      return [
        {
          id: 'rename-group',
          label: 'Rename group',
          icon: PenLine,
          onSelect: () => {
            setGroupRenameDraft(layer.name);
            setRenamingGroupId(layer.id);
          },
        },
        {
          id: 'ungroup',
          label: 'Ungroup',
          icon: Trash2,
          separatorBefore: true,
          danger: true,
          tooltip: 'Removes the folder. The layers inside are kept, moved back to the top level.',
          onSelect: () => removeGroup(layer),
        },
      ];
    }

    const capabilities = getLayerCapabilities(layer, module);
    const items: LayerMenuItem[] = [];
    /* A scratch layer belongs to the person looking at it, not to the map, so
       it stays theirs to rename, restyle and remove even in the read-only
       viewer — `canManageLayers` guards the SHARED map, and this is not part
       of it. Without this, a viewer could create a buffer and then have no way
       to get rid of it. */
    const editable = (canManageLayers || Boolean(layer.temporary)) && Boolean(onLayerChange);

    if (capabilities.zoomTo) {
      items.push({
        id: 'zoom',
        label: 'Zoom to layer',
        icon: LocateFixed,
        onSelect: () => void zoomToLayer(layer),
      });
    }

    if (capabilities.attributeTable) {
      items.push({
        id: 'attributes',
        label: 'Attribute table',
        icon: TableProperties,
        onSelect: () => onOpenAttributeTable?.(layer),
      });
    }

    if (editable && capabilities.symbology) {
      items.push({
        id: 'symbology',
        label: 'Symbology',
        icon: Palette,
        separatorBefore: true,
        onSelect: () => onOpenSymbology?.(layer.id),
      });
    }

    /* Viewer-side labels switch. Only offered where an admin actually
       configured a label field — otherwise it would be a button that changes
       nothing. Deliberately NOT gated on `editable`: it changes what this
       session sees, not the saved configuration. */
    if (onToggleLabels && capabilities.labeling && labelingIsConfigured(layer.labeling)) {
      const on = labelingIsActive(layer.labeling);
      items.push({
        id: 'labels-toggle',
        label: on ? 'Hide labels' : 'Show labels',
        icon: Type,
        separatorBefore: true,
        tooltip: on ? 'Turn this layer’s labels off' : 'Turn this layer’s labels on',
        onSelect: () => onToggleLabels(layer.id),
      });
    }

    // Labeling sits beside Symbology but is a separate configuration: opening
    // one never touches the other's settings.
    if (editable && capabilities.labeling) {
      items.push({
        id: 'labeling',
        label: 'Labeling',
        icon: Type,
        separatorBefore: !capabilities.symbology,
        onSelect: () => onOpenLabeling?.(layer.id),
      });
    }

    /* Pop-up configuration — the third member of the same family, and equally
       independent: it reads and writes only `layer.popup`. Offered only where
       a click can actually return attributes (see capabilities.popup). */
    if (editable && capabilities.popup && onOpenPopup) {
      items.push({
        id: 'popup',
        label: 'Configure pop-up',
        icon: MessageSquare,
        tooltip: 'Choose which attributes a click on this layer shows, and how',
        onSelect: () => onOpenPopup(layer.id),
      });
    }

    if (editable && capabilities.rename) {
      items.push({
        id: 'rename',
        label: 'Rename layer',
        icon: PenLine,
        separatorBefore: true,
        tooltip: 'Renames the layer in this map only — the service is untouched',
        onSelect: () => {
          setRenameDraft(getLayerDisplayName(layer));
          setRenamingLayerId(layer.id);
        },
      });
    }

    // NOTE: "Hide in map" and "Hide from legend" were intentionally removed
    // from this menu. Visibility remains available via the layer checkbox.

    /* Deliberately not gated on `editable` alone — see `onOpacityPreview`'s
       doc comment. An end user gets the same slider through it instead of
       `onLayerChange`, so this is offered whenever EITHER path can write
       somewhere; the slider below decides which one it actually calls. */
    if (capabilities.opacity && (editable || onOpacityPreview)) {
      items.push({
        id: 'opacity',
        label: 'Set transparency',
        icon: Droplet,
        onSelect: () => setOpacityLayerId(layer.id),
      });
    }

    if (editable && capabilities.visibleRange) {
      items.push({
        id: 'visibleRange',
        label: 'Set visibility range',
        icon: SlidersHorizontal,
        tooltip: 'Choose the map scale range this layer draws at — hidden outside it',
        onSelect: () => setVisibleRangeLayerId(layer.id),
      });
    }

    // Organisation, not capability — offered whenever there is a folder to
    // join, or one to leave, regardless of what kind of layer this is.
    if (editable && groups.length > 0 && !layer.groupId) {
      items.push({
        id: 'move-to-group',
        label: 'Move to group',
        icon: FolderPlus,
        onSelect: () => setGroupPickerLayerId(layer.id),
      });
    }
    if (editable && layer.groupId) {
      items.push({
        id: 'remove-from-group',
        label: 'Remove from group',
        icon: FolderMinus,
        onSelect: () => onLayerChange?.(layer.id, { groupId: undefined }),
      });
    }

    // Not offered for a grouped child: `index` here is its position among
    // TOP-LEVEL rows only (see topLevelIds below), which a nested layer is
    // not part of, and reordering within a group is a separate thing this
    // does not attempt yet.
    if (editable && capabilities.reorder && !layer.groupId) {
      items.push({
        id: 'up',
        label: 'Move up',
        icon: ArrowUp,
        separatorBefore: true,
        disabled: index === 0,
        tooltip: index === 0 ? 'Already the first layer' : undefined,
        onSelect: () => onLayerMove?.(layer.id, -1),
      });
      items.push({
        id: 'down',
        label: 'Move down',
        icon: ArrowDown,
        disabled: index === topLevelIds.length - 1,
        tooltip: index === topLevelIds.length - 1 ? 'Already the last layer' : undefined,
        onSelect: () => onLayerMove?.(layer.id, 1),
      });
    }

    if (editable && capabilities.duplicate) {
      items.push({
        id: 'duplicate',
        label: 'Duplicate layer',
        icon: Copy,
        onSelect: () => onLayerDuplicate?.(layer.id),
      });
    }

    /* The layer's OWNER may share it — not just an admin, unlike sharing the
       whole module (see LayerShare's own doc comment on the server for why).
       Hidden for a scratch layer: it exists only in this browser until Save,
       so there is nothing on the server yet for a share grant to point at. */
    if (
      canManageLayers &&
      !layer.temporary &&
      module &&
      currentUser &&
      (module.ownerId === currentUser.id || currentUser.role === 'admin')
    ) {
      items.push({
        id: 'share',
        label: 'Share this layer',
        icon: Share2,
        onSelect: () => setSharingLayerId(layer.id),
      });
    }

    if (capabilities.refresh) {
      items.push({
        id: 'refresh',
        label: 'Refresh layer',
        icon: RefreshCw,
        separatorBefore: true,
        onSelect: () => {
          onLayerRefresh?.();
          setLayerNotice({ kind: 'ok', text: `${getLayerDisplayName(layer)} refreshed` });
        },
      });
    }

    if (capabilities.info) {
      items.push({
        id: 'info',
        label: 'Layer information',
        icon: Info,
        onSelect: () => void openLayerInfo(layer),
      });
    }

    /* The Draw tool's own scratch layer only — a quick way to take sketches
       out of the browser without going to the separate Export KML tool and
       picking this layer from its own list. Not offered for any other
       layer: a service-backed one already has the general-purpose tool for
       this, and offering it here too would just be the same action twice. */
    if (layer.temporary && layer.temporarySource === 'Draw' && (layer.localData?.features?.length ?? 0) > 0) {
      items.push({
        id: 'export-kml',
        label: 'Export KML',
        icon: Download,
        tooltip: 'Download these drawings as a .kml file',
        onSelect: () => {
          const name = getLayerDisplayName(layer);
          downloadKml(name, featuresToKml(name, layer.localData!.features ?? []));
        },
      });
    }

    if (editable && capabilities.remove) {
      items.push({
        id: 'remove',
        label: 'Remove layer',
        icon: Trash2,
        separatorBefore: true,
        danger: true,
        onSelect: () => onLayerRemove?.(layer.id),
      });
    }

    return items;
  };

  /** Zoom-to uses stored bounds, then asks the service adapter if absent. */
  const zoomToLayer = async (layer: Layer) => {
    if (layer.bounds) {
      onZoomToLayer(layer);
      return;
    }
    if (!module) return;
    try {
      const bounds = await getLayerAdapter(layer, module).fetchExtent(layer, module);
      if (!bounds) {
        setLayerNotice({ kind: 'error', text: 'Layer extent is not available from the service.' });
        return;
      }
      onLayerChange?.(layer.id, { bounds });
      onZoomToLayer({ ...layer, bounds });
    } catch (error) {
      console.error('[layers] zoom-to failed', error);
      setLayerNotice({ kind: 'error', text: 'Could not read the layer extent.' });
    }
  };

  const openLayerInfo = async (layer: Layer) => {
    if (!module) return;
    setInfoLayer(layer);
    setInfoRows(null);
    setInfoEdits(layer.sharedLayerId ? null : undefined);
    try {
      const details = await getLayerAdapter(layer, module).fetchDetails(layer, module);
      setInfoRows(details.rows);
    } catch (error) {
      console.error('[layers] details failed', error);
      setInfoRows([{ label: 'Error', value: 'Layer details could not be loaded.' }]);
    }
    if (layer.sharedLayerId) {
      try {
        setInfoEdits(await layerLibraryService.fetchSharedLayerEdits(module.id, layer.id));
      } catch {
        setInfoEdits([]);
      }
    }
  };

  const commitRename = (layer: Layer) => {
    const trimmed = renameDraft.trim();
    // Empty clears the override and falls back to the real service name.
    onLayerChange?.(layer.id, { displayName: trimmed || undefined });
    setRenamingLayerId(null);
  };

  const getLayerById = (id: string) => layers.find((layer) => layer.id === id) || null;
  /* Replaced by per-tool scoping below: one shared list meant a Search
     configured for two layers still offered all of them. */
  const layerOptionsFor = (toolId: string) =>
    scopedTo(toolId).map((layer) => ({ id: layer.id, name: getLayerDisplayName(layer) }));

  /**
   * Field names for the layer a tool has been pointed at.
   *
   * Two bugs lived in the version this replaces.
   *
   * It was GeoServer-only: it bailed on `!geoServerUrl`, then built a WFS
   * request out of the GeoServer URL regardless of what actually backed the
   * layer. Point Filter or Search at an ArcGIS layer and the field list stayed
   * empty, which is why the Field dropdown rendered blank while the Tool
   * Library — which already went through `fetchLayerFieldNames` — listed the
   * same layer's fields correctly.
   *
   * And it wrote to BOTH tools' lists from one call, so choosing a layer in
   * Filter silently replaced Search's field list with a different layer's
   * fields. Each tool now owns its own.
   */
  const fetchLayerFields = async (toolId: 'filter' | 'query', layerId: string) => {
    const setFields = toolId === 'filter' ? setFilterLayerFields : setQueryLayerFields;
    const setBusy = toolId === 'filter' ? setFilterFieldsLoading : setQueryFieldsLoading;

    const layer = getLayerById(layerId);
    if (!layer) {
      setFields([]);
      return;
    }

    setBusy(true);
    try {
      const source = module ?? ({ geoServerUrl, arcgisUrl, layers } as unknown as AppModule);
      // Asks whichever service actually backs the layer — ArcGIS metadata,
      // WFS DescribeFeatureType, or the file's own features.
      const names = await fetchLayerFieldNames(layer, source);

      /* Types come too, because a filter's quoting depends on them and cannot
         be guessed from what someone typed. See fetchNumericFieldNames.
         Filter-only: `numericFields` is a single, SHARED piece of state, and
         Query never reads it (matchesClauses works out numeric-vs-text per
         VALUE, not per declared field). Fetching it for Query too meant
         picking a layer there could win a race against Filter's own fetch
         and silently overwrite Filter's numeric fields with an unrelated
         layer's — quoting a genuinely numeric field as a string, which
         breaks that one clause (and the whole AND/OR expression it's part
         of) with a service 400 no one sees. A request token also guards
         against the same thing happening from switching the Filter layer
         itself twice in quick succession. */
      if (toolId === 'filter') {
        const requestId = ++numericFieldsRequestRef.current;
        void fetchNumericFieldNames(layer, source).then((fields) => {
          if (numericFieldsRequestRef.current === requestId) setNumericFields(fields);
        });
      }

      /* Narrowed to the fields the admin ticked for THIS tool. Empty means
         every field, the same rule the layer scoping follows. */
      const configured = settings(toolId).fields;
      const chosen = Array.isArray(configured) ? (configured as string[]) : [];
      const scoped = chosen.length > 0 ? names.filter((name) => chosen.includes(name)) : names;

      setFields(scoped);
    } finally {
      setBusy(false);
    }
  };

  /**
   * A layer's features, whichever service backs it.
   *
   * The Query and Filter panels both used to build a WFS request out of
   * `geoServerUrl` regardless of the layer's actual source, which is why both
   * came up empty on an ArcGIS layer.
   */
  const loadLayerFeatures = useCallback(
    async (layer: Layer, limit = 10000): Promise<GeoJSON.Feature[]> => {
      // Local data is already fully in the browser — slicing it to `limit`
      // bought nothing (there is no network fetch it saves) and silently
      // capped BOTH halves of "Invert Where Clause" to whatever fell in the
      // first `limit` features, so a layer larger than that could never show
      // its true complement no matter how it was queried.
      if (layer.localData) return layer.localData.features ?? [];

      if (layer.arcgisLayerId !== undefined) {
        // The layer's OWN service, when it differs from the module's
        // primary `arcgisUrl` — see `arcgisServiceUrlFor`.
        const effectiveUrl = arcgisServiceUrlFor(layer, { arcgisUrl });
        const baseUrl = getArcgisBaseUrl(effectiveUrl);
        if (!baseUrl) return [];
        const params = new URLSearchParams({
          where: '1=1',
          outFields: '*',
          outSR: '4326',
          f: 'geojson',
          resultRecordCount: String(limit),
        });
        const response = await fetch(
          withArcgisToken(`${baseUrl}/${layer.arcgisLayerId}/query?${params.toString()}`, effectiveUrl),
        );
        if (!response.ok) return [];
        return (await response.json())?.features ?? [];
      }

      if (!geoServerUrl) return [];
      const response = await fetch(getWfsUrl(geoServerUrl, layer.name, limit));
      if (!response.ok) return [];
      return (await response.json())?.features ?? [];
    },
    [arcgisUrl, geoServerUrl],
  );

  /**
   * The values a field actually holds, offered as suggestions.
   *
   * Was GeoServer-only — it bailed on `!geoServerUrl` and then built a WFS
   * request from that URL whatever service backed the layer — so pointing the
   * Query tool at an ArcGIS layer produced no suggestions at all. Same fault
   * the Filter tool's field list had.
   */
  const fetchFieldValues = async (layerId: string, field: string, clauseId: number) => {
    const layer = getLayerById(layerId);
    if (!layer || !field) return;

    setQueryFieldValuesLoading((current) => ({ ...current, [clauseId]: true }));
    try {
      const features = await loadLayerFeatures(layer);
      const values = Array.from(
        new Set(
          features
            .map((feature) => feature.properties?.[field])
            .filter((value) => value !== null && value !== undefined && String(value).trim() !== '')
            .map((value) => String(value)),
        ),
      )
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        // A field with thousands of distinct values is a text box, not a list.
        .slice(0, 200);
      setQueryFieldValues((current) => ({ ...current, [clauseId]: values }));
    } catch {
      setQueryFieldValues((current) => ({ ...current, [clauseId]: [] }));
    } finally {
      setQueryFieldValuesLoading((current) => ({ ...current, [clauseId]: false }));
    }
  };


  /** A GeoJSON feature in the shape the selection handlers speak. */
  const featureAsRow = (feature: GeoJSON.Feature, index: number): AttributeFeature => ({
    ...(feature.properties ?? {}),
    id: String(feature.id ?? index),
    // Scoped to the layer the query ran against, so these keys cannot match
    // another layer's rows that happen to share an OBJECTID.
    key: featureKey(feature, queryLayer),
    layerId: queryLayer,
    properties: (feature.properties ?? {}) as Record<string, unknown>,
    geometry: feature.geometry ?? undefined,
  });

  /** The panel's operator labels, in the evaluator's vocabulary. */
  const QUERY_OPERATORS: Record<string, AttributeOperator> = {
    'is equal to': 'equals',
    'is not equal to': 'notEquals',
    'is greater than': 'greater',
    'is less than': 'less',
    contains: 'contains',
    'starts with': 'startsWith',
    'is blank': 'isBlank',
    'is not blank': 'isNotBlank',
  };

  /**
   * Runs the query.
   *
   * The version this replaces could not match anything. It built a readable
   * expression — `ROADNAME = 'Webster'` — lower-cased the WHOLE STRING, and
   * then asked whether any attribute value CONTAINED that text. No feature's
   * name is ever the literal words "roadname = 'webster'", so the result was
   * always empty; and with no clauses filled in the same code returned every
   * row. Typing a value by hand could not work either, for the same reason.
   *
   * Clauses are now evaluated by `matchesClauses`, the tested predicate the
   * attribute table's Select by attribute already uses — which is what
   * attributeQuery.ts was written for ("reused by the Query and Filter tools
   * later"). It also compares numerically when both sides are numbers, so
   * `diameter > 63` behaves on a service that publishes diameters as strings.
   */
  const handleApplyQuery = async () => {
    const layer = getLayerById(queryLayer);
    if (!layer) return;

    setQueryLoading(true);
    try {
      const features = await loadLayerFeatures(layer);
      // `loadLayerFeatures` returning exactly `limit` rows is this layer's
      // only signal that there may be more it didn't fetch — local layers
      // never hit this (they return everything), so it only ever fires for
      // a service-backed layer genuinely larger than the cap.
      setQueryTruncated(features.length >= 10000);

      const clauses: AttributeClause[] = queryClauses
        .filter((clause) => clause.field)
        .map((clause) => ({
          id: String(clause.id),
          field: clause.field,
          operator: QUERY_OPERATORS[clause.operator] ?? 'equals',
          value: clause.value ?? '',
          conjunction: (clause.conjunction === 'Or' ? 'or' : 'and') as 'and' | 'or',
        }))
        .filter(clauseIsComplete);

      const matches = (feature: GeoJSON.Feature) =>
        clauses.length === 0
          ? true // no usable clause means no filter, which is every feature
          : matchesClauses((feature.properties ?? {}) as Record<string, unknown>, clauses);

      // "Invert where clause" is the panel's own switch — the true complement
      // of the match, not a second pass over the matched subset (which only
      // ever answers "did THIS object pass the first filter," the same
      // question asked twice).
      const found =
        invertWhere && clauses.length > 0
          ? features.filter((feature) => !matches(feature))
          : features.filter(matches);

      setQueryResults(found);
      setSelectedRows([]);

      /* The result set IS the selection. That single line is what makes the
         map highlight it, the attribute table open with those rows ticked, and
         "Zoom to selected" frame them — rather than three features each
         keeping their own idea of what is chosen. */
      const rows = found.map((feature, index) => featureAsRow(feature, index));
      onSelectionChange?.(rows);

      // An answer you have to go looking for is half an answer: the map moves
      // to what was found, and the highlight blinks so it is obvious which
      // shapes those are.
      if (rows.length > 0) onZoomToSelection?.(rows);
    } catch {
      setQueryResults([]);
    } finally {
      setQueryLoading(false);
    }
  };

  /**
   * Builds the print sheet.
   *
   * The version this replaces waited 600ms and said "Print preview generated"
   * in an alert. Nothing was rendered, nothing could be saved.
   *
   * Both outputs come from ONE sheet, so the PDF and the PNG cannot disagree.
   */
  const buildSheet = useCallback(async () => {
    const container = document.querySelector<HTMLElement>('.ol-viewport');
    if (!container) throw new Error('The map is not on screen.');

    /* Capture exactly the red extent-preview box, not the whole on-screen
       viewport — that mismatch (whatever shape the browser window happened
       to be vs. the page's own map-frame shape) used to be silently resolved
       by `renderPrintSheet`'s own crop-to-fit, so the printed area was never
       quite what the screen showed. The box is rendered by MapViewOL at
       exactly the ratio `renderPrintSheet` will frame the capture into (see
       `printFrameAspectRatio`), so cropping to its own on-screen bounds here
       makes the two agree exactly. Falls back to the whole viewport if the
       Print tab somehow isn't open (the box doesn't exist yet to read). */
    const containerBounds = container.getBoundingClientRect();
    const frameEl = document.querySelector<HTMLElement>('[data-print-frame]');
    const cropRect = frameEl
      ? (() => {
          const frameBounds = frameEl.getBoundingClientRect();
          return {
            x: frameBounds.left - containerBounds.left,
            y: frameBounds.top - containerBounds.top,
            width: frameBounds.width,
            height: frameBounds.height,
          };
        })()
      : undefined;

    const { canvas, missingTiles } = await captureMapOL(container, 2, cropRect);

    /* Ground metres per pixel of the CAPTURED image, which is twice the
       screen's density — the scale bar has to describe the picture it is
       printed on, not the screen it came from. */
    const screenMetresPerPixel = resolutionAt(mapCenter[0], mapZoom);
    const metresPerPixel = (screenMetresPerPixel * (cropRect?.width ?? container.clientWidth)) / canvas.width;

    /* Only what is ACTUALLY on the map. Ticked in the table of contents is not
       the same as drawn: a layer outside its scale band, or turned down to no
       opacity, is switched on and invisible. Printing a key for a symbol that
       appears nowhere on the sheet sends the reader looking for it. */
    const candidateLayers = layers.filter(
      (layer) =>
        !layer.isGroup &&
        layer.checked &&
        (layer.showInLegend ?? true) &&
        isVisibleAtZoom(layer, mapZoom) &&
        (layer.opacity ?? 1) > 0,
    );

    let legendGroups: Awaited<ReturnType<typeof collectLegend>> = [];
    if (printIncludeLegend && candidateLayers.length > 0) {
      /* Narrowed further to what the PRINT EXTENT itself shows — a layer with
         fifteen unique-value classes used to print all fifteen even when the
         chosen extent only ever showed three of them, and a layer with
         nothing at all inside the box still got a row. The extent is the red
         box's own geographic footprint: it is centred on the map's current
         centre by construction (MapViewOL centres it in the viewport, and OL
         always renders the view's own centre at the viewport's centre), so a
         flat metres-per-degree conversion around that one point is accurate
         enough for a box this size — the same approximation `resolutionAt`
         itself already makes for the scale bar. */
      const extentWidthPx = cropRect?.width ?? container.clientWidth;
      const extentHeightPx = cropRect?.height ?? container.clientHeight;
      const halfWidthM = (extentWidthPx / 2) * screenMetresPerPixel;
      const halfHeightM = (extentHeightPx / 2) * screenMetresPerPixel;
      const metresPerDegLat = 111320;
      const metresPerDegLng = Math.max(metresPerDegLat * Math.cos((mapCenter[0] * Math.PI) / 180), 1e-6);
      const printExtent: Bbox = {
        minX: mapCenter[1] - halfWidthM / metresPerDegLng,
        maxX: mapCenter[1] + halfWidthM / metresPerDegLng,
        minY: mapCenter[0] - halfHeightM / metresPerDegLat,
        maxY: mapCenter[0] + halfHeightM / metresPerDegLat,
      };

      const printCtx = toolContextFor('print');
      const { visibleLayers, presentCategoryValues } = await filterLegendToPrintExtent(
        candidateLayers,
        printExtent,
        (layer) => loadFeatures(layer, printCtx, { limit: 2000, bbox: printExtent }),
      );

      legendGroups = await collectLegend(visibleLayers, {
        arcgisBaseUrl: getArcgisBaseUrl(arcgisUrl) || undefined,
        geoServerUrl: geoServerUrl || undefined,
        displayName: getLayerDisplayName,
        presentCategoryValues,
      });
    }

    /* The app's own header logo, exactly as it appears on the live map —
       absent draws no logo mark at all rather than inventing one. */
    const logoUrl = module?.headerTheme?.logoUrl;
    const showLogo = module?.headerTheme?.showLogo ?? true;
    const logoImage = logoUrl && showLogo ? await loadCorsImage(logoUrl) : null;

    const sheet = renderPrintSheet({
      pageSize: printPageSize,
      landscape: printOrientation === 'Landscape',
      title: printTitle.trim() || module?.title || 'Map',
      subtitle: printSubtitle.trim() || undefined,
      map: canvas,
      metresPerPixel,
      logoImage,
      legend: legendGroups,
      showLegend: printIncludeLegend,
      showScaleBar: printScaleBar,
      showNorthArrow: printNorthArrow,
      showDate: printDate,
      userName: currentUser?.name,
      attribution: 'Basemap © OpenStreetMap contributors · Data from the connected services',
    });

    return { sheet, missingTiles };
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    printPageSize, printOrientation, printTitle, printSubtitle, printIncludeLegend,
    printScaleBar, printNorthArrow, printDate, layers, mapCenter, mapZoom,
    arcgisUrl, geoServerUrl, module, currentUser,
  ]);

  const runPrint = async (mode: 'pdf' | 'png') => {
    setPrintGenerating(true);
    setPrintNote(null);
    try {
      const { sheet, missingTiles } = await buildSheet();
      const name = (printTitle.trim() || module?.title || 'map').replace(/[^\w-]+/g, '-');

      if (mode === 'png') downloadSheet(sheet, name);
      else printSheet(sheet, printTitle.trim() || 'Map');

      setPrintNote(
        missingTiles > 0
          ? `${missingTiles} tile${missingTiles === 1 ? '' : 's'} could not be included — that server does not allow its images to be read for export.`
          : null,
      );
    } catch (error) {
      setPrintNote(error instanceof Error ? error.message : 'The sheet could not be produced.');
    } finally {
      setPrintGenerating(false);
    }
  };

  /* ── Measure ─────────────────────────────────────────────────────────
     "Start Measuring" used to flip a flag nothing read: `isMeasuring` never
     reached the map, and no code path ever ADDED a measurement. The sketching
     is the Draw tool's, reused — the difference is only what happens to the
     finished shape. */
  const startMeasurement = () => {
    // One sketch at a time. Drawing and measuring both own the map's clicks,
    // so starting either has to stop the other.
    onStopDrawing?.();
    setIsMeasuring(true);
  };
  const stopMeasurement = () => setIsMeasuring(false);
  const clearMeasurements = () => setMeasurements([]);

  /* The Select tool wants the same sketching, for a third purpose: Draw keeps
     the shape, Measure turns it into a number, Select uses it to pick features
     out. One controller, three destinations. */
  useEffect(() => {
    // The layer travels with the mode: the host does the hit-testing and has
    // no other way to know which layer the box is asking about.
    onSelectModeChange?.(selectionActive ? selectMode : null, selectLayerIds, selectCombine);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionActive, selectMode, selectLayerIds, selectCombine]);

  /* The Print tool's extent-preview box — see MapViewOL's own comment on
     `printFrameRatio`. Reported only while the Print tab is actually open,
     so the box (and the crop it drives) disappears the moment the admin
     navigates away, the same way the Select tool's sketch mode does. */
  useEffect(() => {
    onPrintFrameRatioChange?.(
      activeTab === 'print'
        ? printFrameAspectRatio(
            printPageSize,
            printOrientation === 'Landscape',
            Boolean(printSubtitle.trim()),
            printIncludeLegend,
          )
        : null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, printPageSize, printOrientation, printSubtitle, printIncludeLegend]);

  /* Now that a selection session can stay armed across several shapes (see
     the "stays armed" note below), it has to be stopped explicitly when the
     admin navigates to a DIFFERENT tab without pressing Stop first — the tab
     buttons just switch `activeTab`, they do not call `handleCloseTool`, so
     without this the Select tool would keep listening for one more shape in
     the background while a different tool's tab is the one actually open. */
  useEffect(() => {
    if (activeTab !== 'select' && selectionActive) {
      onStopDrawing?.();
      setSelectionActive(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  /* The same gap, for Measure: switching tabs away from it without pressing
     "Stop Measuring" first left `isMeasuring` (and the `measureSketchMode`
     it drives) stuck on. `sketchMode` below picks `measureSketchMode` BEFORE
     `selectSketchMode` (`isDrawing ? drawType : measureSketchMode ??
     selectSketchMode ?? editCreateShape`), so a still-armed Measure silently
     out-ranked Select every time afterwards — the Select tab would open,
     "Start selection" would flip to "Stop" as if it had armed, but every
     click on the map kept being read as a measurement instead of a
     selection. Discovered because Select's own cleanup above had exactly
     this shape already; Measure never got the same treatment when it was
     added. */
  useEffect(() => {
    if (activeTab !== 'measure' && isMeasuring) {
      stopMeasurement();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  /* Features the drawn shape caught, handed back for the panel to count.
     Deliberately does NOT turn `selectionActive` off: see `handleSketch`'s
     own "Stays armed" comment — drawing more than one shape (especially in
     Add/Remove mode) is the normal case, and this used to force every shape
     back to one-shot, so Add/Remove needed a fresh "Start selection" click
     before each and every box. */
  const selectedTokenRef = useRef(0);
  useEffect(() => {
    if (!selectedFeatures || selectedFeatures.token === selectedTokenRef.current) return;
    selectedTokenRef.current = selectedFeatures.token;
    setSelectedRows(selectedFeatures.rows.map((_, index) => index));
  }, [selectedFeatures]);

  /** Tells the host what the Measure tool is waiting for the user to sketch. */
  useEffect(() => {
    onMeasureModeChange?.(
      isMeasuring ? measureType : null,
      measureUnit === 'feet' || measureUnit === 'miles',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMeasuring, measureType, measureUnit]);

  /* The map keeps the finished shapes; the panel keeps the readings. Both come
     from the same list, so removing a row also removes it from the map. */
  useEffect(() => {
    onMeasurementsChange?.(
      measurements
        .filter((entry) => entry.geometry)
        .map((entry) => ({
          type: 'Feature' as const,
          properties: { value: entry.value },
          geometry: entry.geometry as GeoJSON.Geometry,
        })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measurements]);

  /* Turns a finished sketch into a number. Keyed on the token rather than the
     feature, so measuring the same shape twice records it twice. */
  const measuredTokenRef = useRef(0);
  useEffect(() => {
    if (!measuredFeature || measuredFeature.token === measuredTokenRef.current) return;
    measuredTokenRef.current = measuredFeature.token;

    const measured = measureGeometry(measuredFeature.feature.geometry);
    if (!measured) return;

    const value =
      measured.kind === 'Distance'
        ? formatLength(measured.metres, measureUnit === 'feet' || measureUnit === 'miles' ? 'imperial' : 'metric')
        : formatArea(
            measured.metres,
            measureAreaUnit === 'hectares' ? 'hectare' : measureAreaUnit === 'acres' ? 'acre' : 'sqm',
          );

    setMeasurements((current) => [
      ...current,
      {
        id: Date.now() + current.length,
        type: measured.kind,
        value,
        geometry: measuredFeature.feature.geometry,
      },
    ]);
  }, [measuredFeature, measureUnit, measureAreaUnit]);

  const handleSaveBookmark = () => {
    const trimmed = bookmarkName.trim();
    if (!trimmed) return;
    setBookmarks((prev) => [
      ...prev,
      {
        id: Date.now(),
        name: trimmed,
        date: new Date().toLocaleString(),
        center: mapCenter,
        zoom: mapZoom,
      },
    ]);
    setBookmarkName('');
  };

  const handleStartRenameBookmark = (id: number, currentName: string) => {
    setRenamingBookmarkId(id);
    setRenameBookmarkValue(currentName);
  };

  const handleCancelRenameBookmark = () => {
    setRenamingBookmarkId(null);
    setRenameBookmarkValue('');
  };

  const handleConfirmRenameBookmark = () => {
    const trimmed = renameBookmarkValue.trim();
    if (!trimmed || renamingBookmarkId === null) {
      handleCancelRenameBookmark();
      return;
    }
    setBookmarks((prev) =>
      prev.map((bookmark) => (bookmark.id === renamingBookmarkId ? { ...bookmark, name: trimmed } : bookmark)),
    );
    handleCancelRenameBookmark();
  };

  const handleDeleteBookmark = (id: number) => {
    setBookmarks((prev) => prev.filter((bookmark) => bookmark.id !== id));
    if (renamingBookmarkId === id) handleCancelRenameBookmark();
  };

  /**
   * Finds what is actually within the radius.
   *
   * The version this replaces returned three sentences it had written itself
   * — "Asset 1 near …", "Asset 2 within 500m" — after a setTimeout pretending
   * to be a request. Nothing was ever searched.
   *
   * Distance is geodesic and measured to the NEAREST point of each feature,
   * not its centre: "is this pipeline within 50 m" is a question about the
   * pipe, and a long run whose midpoint is a kilometre away can still pass
   * through the incident.
   */

  /** Accepts what people paste: "lat, lng" or "lat lng". */
  const applyTypedIncident = () => {
    const parts = incidentTyped
      .split(/[,\s]+/)
      .map((part) => Number(part.trim()))
      .filter((value) => !Number.isNaN(value));
    if (parts.length < 2 || Math.abs(parts[0]) > 90 || Math.abs(parts[1]) > 180) {
      setAssetSearchError('Enter a latitude and longitude, for example 28.50408, 77.04163.');
      return;
    }
    setAssetSearchError(null);
    setIncidentPoint({ lat: parts[0], lng: parts[1] });
    setIncidentTyped('');
  };

  /* A map click drops the flag while picking. Guarded on the coordinate, not a
     counter: `pointer` re-renders this panel for its own reasons, and a
     re-render is not a new click. */
  useEffect(() => {
    if (!pickingIncident || !pointer) return;
    const signature = `${pointer.lat},${pointer.lng}`;
    if (signature === incidentConsumedRef.current) return;
    incidentConsumedRef.current = signature;
    setIncidentPoint({ lat: pointer.lat, lng: pointer.lng });
    setPickingIncident(false);
    setAssetSearchError(null);
  }, [pickingIncident, pointer]);

  /* The flag and its radius on the map. One source — the panel's state —
     drawn twice, so the ring always matches the number in the box. */
  useEffect(() => {
    if (activeTab !== 'assetSearch' || !incidentPoint) {
      if (activeTab === 'assetSearch') onOverlayChange?.(null);
      return;
    }
    onOverlayChange?.({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [geodesicCircle(incidentPoint, Math.max(1, assetSearchRadius))],
          },
        },
        {
          type: 'Feature',
          properties: { marker: '!', label: `${incidentPoint.lat.toFixed(5)}, ${incidentPoint.lng.toFixed(5)}` },
          geometry: { type: 'Point', coordinates: [incidentPoint.lng, incidentPoint.lat] },
        },
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentPoint, assetSearchRadius, activeTab]);

  const handleAssetSearch = async () => {
    if (!incidentPoint) {
      setAssetSearchError('Place the incident point first.');
      return;
    }
    const targets = scopedTo('assetSearch').filter((layer) => layer.checked);
    if (targets.length === 0) {
      setAssetSearchError('No layers are switched on to search.');
      return;
    }

    setAssetSearchError(null);
    setAssetSearchLoading(true);
    try {
      const perLayer = await Promise.all(
        targets.map(async (layer) => {
          const features = await loadLayerFeatures(layer);
          return features
            .map((feature, index) => {
              const positions = nearestPositions(feature.geometry);
              if (positions.length === 0) return null;
              let closest = Infinity;
              let at: [number, number] = positions[0];
              for (const position of positions) {
                const metres = distanceBetween(position, [incidentPoint.lng, incidentPoint.lat]);
                if (metres < closest) {
                  closest = metres;
                  at = position;
                }
              }
              if (closest > assetSearchRadius) return null;
              const properties = (feature.properties ?? {}) as Record<string, unknown>;
              const named = Object.entries(properties).find(
                ([key, value]) =>
                  /^(name|label|title|address|description)$/i.test(key) &&
                  String(value ?? '').trim() !== '',
              );
              return {
                label: named ? String(named[1]) : `Feature ${index + 1}`,
                layerName: getLayerDisplayName(layer),
                layerId: layer.id,
                metres: closest,
                lat: at[1],
                lng: at[0],
                feature,
              };
            })
            .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
        }),
      );

      // Nearest first — the whole point of asking "what is near me".
      setAssetSearchResults(perLayer.flat().sort((a, b) => a.metres - b.metres));
      setExpandedNearMeLayers(new Set());
    } catch {
      setAssetSearchError('Those layers could not be searched.');
    } finally {
      setAssetSearchLoading(false);
    }
  };

  /** Near Me's results, grouped by the layer they came from — each group
   *  keeps the nearest-first order `handleAssetSearch` already sorted. */
  const assetSearchGroups = useMemo(() => {
    // A plain record, not a generic Map: `Map` here resolves to the
    // lucide-react icon imported under that name (see selectionByLayer's own
    // identical note above), not the built-in constructor.
    const groups: Record<string, { layerId: string; layerName: string; results: typeof assetSearchResults }> = {};
    for (const result of assetSearchResults) {
      const group = (groups[result.layerId] ||= {
        layerId: result.layerId,
        layerName: result.layerName,
        results: [],
      });
      group.results.push(result);
    }
    // Groups themselves nearest-first too, by their own closest result.
    return Object.values(groups).sort((a, b) => a.results[0].metres - b.results[0].metres);
  }, [assetSearchResults]);

  /**
   * One CSV per layer, zipped together — "layer-wise sheets" without a
   * spreadsheet library: a CSV cannot hold multiple sheets, so one file per
   * layer in one archive is the direct equivalent, and every column a
   * feature actually has (not just label/distance) travels with it.
   */
  const exportNearMeResults = () => {
    const files = assetSearchGroups.map((group) => {
      const columns = Array.from(
        new Set(group.results.flatMap((result) => Object.keys(result.feature.properties ?? {}))),
      );
      columns.push('Distance (m)');
      const rows = group.results.map((result) => ({
        ...(result.feature.properties ?? {}),
        'Distance (m)': Math.round(result.metres),
      }));
      const safeName = group.layerName.replace(/[\\/:*?"<>|]/g, '_').slice(0, 120) || 'layer';
      return { name: `${safeName}.csv`, data: new TextEncoder().encode(toCsv(columns, rows)) };
    });
    if (files.length === 0) return;

    const blob = writeZip(files);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'near-me-results.zip';
    link.click();
    URL.revokeObjectURL(url);
  };

  /**
   * The context every registry-backed tool panel is handed.
   *
   * Built once per render rather than per panel, and memoised on what it
   * actually reads — a panel that re-mounted on every map move would lose the
   * feature list it just fetched.
   */
  const toolContextFor = useCallback(
    (toolId: string): ToolContext => ({
      config: toolConfigWithDefaults(toolId, toolConfigs[toolId]),
      layers,
      module: module ?? ({ id: '', geoServerUrl, arcgisUrl, layers } as unknown as AppModule),
      geoServerUrl,
      arcgisUrl,
      mapCenter,
      mapZoom,
      basemap,
      pointer,
      onGoToLocation,
      onToggleLayer,
      onZoomToLayer,
      onLayerChange,
      onOpenAttributeTable,
      onBasemapChange,
      onAddData,
      onOverlayChange,
      onCreateTempLayer,
      onCreateTempRasterLayer,
      onSelectionChange,
      onZoomToSelection,
      getSelection,
      onSwipeChange,
      swipeLayerId,
      swipeVertical,
      onStartFeatureSketch: onEditModeChange,
      onCancelFeatureSketch: onEditModeChange ? () => onEditModeChange(null) : undefined,
      onFinishFeatureSketch: onFinishShape,
      sketchVertexCount: sketchVertices,
      sketchedFeature: editSketchResult,
      onDraftFeature: onDraftGeometryChange,
      onMoveFeature: onMoveGeometryChange,
      movedFeature: moveResult,
      snapEnabled,
      onSnapEnabledChange,
      onClose: () => handleCloseTool(toolId),
      canManageLayers,
      // `data:edit` is a strict superset of `data:edit-attributes` — see
      // ToolContext's own doc comment. Computed here, once, from the
      // signed-in user's own role (not a route-level constant like
      // `canManageLayers`), so Builder and Viewer both get the same,
      // correct answer through this one shared construction site.
      canEditGeometry: can('data:edit'),
      canEditAttributes: can('data:edit') || can('data:edit-attributes'),
      directionsSeed,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      toolConfigs, layers, module, geoServerUrl, arcgisUrl, mapCenter, mapZoom, basemap, pointer,
      swipeLayerId, swipeVertical, canManageLayers, onEditModeChange, onFinishShape, sketchVertices,
      editSketchResult, onDraftGeometryChange, onMoveGeometryChange, moveResult,
      snapEnabled, onSnapEnabledChange, can, directionsSeed, onSelectionChange, onZoomToSelection,
    ],
  );

  /**
   * The eight inline panels' settings.
   *
   * Same source as the dispatcher's context — `toolConfigs` merged with the
   * registry defaults. Read here so a setting an admin filled in actually
   * reaches the panel: until now every one of them was saved and then ignored,
   * which made the whole Tool Library form decorative.
   */
  const settings = useCallback(
    (toolId: string) => toolConfigWithDefaults(toolId, toolConfigs[toolId]),
    [toolConfigs],
  );

  /** Layers a tool is scoped to; empty means every layer, as the hints promise. */
  const scopedTo = useCallback(
    (toolId: string) => {
      const chosen = settings(toolId).layers;
      const ids = Array.isArray(chosen) ? (chosen as string[]) : [];
      if (ids.length === 0) return layers;
      const scoped = layers.filter((layer) => ids.includes(layer.id));
      return scoped.length > 0 ? scoped : layers;
    },
    [settings, layers],
  );

  const handleCloseTool = (toolId: string) => {
    if (onToolClose) {
      onToolClose(toolId);
    } else {
      setInternalVisibleTools((current) => current.filter((id) => id !== toolId));
    }

    setResolvedActiveTab('layers');
    if (toolId === 'draw') {
      onCloseDrawPanel?.();
    }
    if (toolId === 'measure') {
      setIsMeasuring(false);
    }
    if (toolId === 'select') {
      setSelectionActive(false);
      setSelectedRows([]);
    }
    if (toolId === 'assetSearch') {
      // The flag and its ring belong to the tool, not to the map.
      setIncidentPoint(null);
      setIncidentTyped('');
      setPickingIncident(false);
      setAssetSearchResults([]);
      setAssetSearchError(null);
      onOverlayChange?.(null);
    }
    if (toolId === 'filter') {
      /* Closing the tool undoes what it did. A filter left behind by a closed
         panel is invisible: the map is hiding features and nothing on screen
         says why, or offers a way to stop. */
      clearToolFilters();
      setFilterLayer('');
      setFilterClauses([
        { id: nextClauseId(), field: '', operator: 'is equal to', value: '', conjunction: 'And' },
      ]);
      setFilterInvertWhere(false);
      setFilterError(null);
    }
  };

  return (
    // Glass TOC surface. The panel itself is the only blurred element here —
    // layer rows use .gl-row (tint only), so a long layer list stays fast.
    <div className="gl-panel gl-surface--strong flex min-h-0 flex-1 flex-col rounded-none border-0">
      {/* flex-shrink-0: without it, a flex column this height-constrained
          lets the header compress itself before the scrollable content
          below gives up any space — the tab row (Layers/Legend/whichever
          tools are open) ending up squashed instead of the content area
          scrolling, as it's meant to. */}
      <div className="gl-header flex flex-shrink-0 items-center gap-2 px-2">
        <button
          type="button"
          onClick={() => tabScrollRef.current?.scrollBy({ left: -180, behavior: 'smooth' })}
          disabled={!showTabScrollLeft}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700 disabled:cursor-default disabled:opacity-40"
          aria-label="Scroll tabs left"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div
          ref={tabScrollRef}
          className="toc-tab-scroll flex min-w-0 flex-1 overflow-x-auto [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {(['layers', 'legend', ...visibleTools] as string[]).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setResolvedActiveTab(tab)}
              className={`relative flex-shrink-0 px-4 py-3 text-sm font-medium capitalize ${activeTab === tab ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
            >
              {TOOL_LABELS[tab] ?? tab}
              {activeTab === tab && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-blue-600" />}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => tabScrollRef.current?.scrollBy({ left: 180, behavior: 'smooth' })}
          disabled={!showTabScrollRight}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700 disabled:cursor-default disabled:opacity-40"
          aria-label="Scroll tabs right"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto py-2">
        <div style={{ display: activeTab === 'layers' ? 'block' : 'none' }}>
          {/* Folders an admin organises the list into. Nothing here draws on
              the map by itself — it exists only to group other layers, so it
              is offered wherever the layer list itself may be edited. */}
          {canManageLayers && Boolean(onCreateGroup) && (
            <div className="px-3 pb-1.5">
              {addingGroup ? (
                <div className="flex items-center gap-1.5">
                  <input
                    autoFocus
                    value={newGroupName}
                    onChange={(event) => setNewGroupName(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') createGroup();
                      if (event.key === 'Escape') {
                        setAddingGroup(false);
                        setNewGroupName('');
                      }
                    }}
                    placeholder="Group name"
                    aria-label="New group name"
                    className="min-w-0 flex-1 rounded-lg border border-blue-300 px-2 py-1.5 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-blue-100"
                  />
                  <button
                    type="button"
                    onClick={createGroup}
                    disabled={!newGroupName.trim()}
                    className="rounded-lg bg-blue-600 px-2.5 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAddingGroup(false);
                      setNewGroupName('');
                    }}
                    className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setAddingGroup(true)}
                  className="flex items-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-500 transition hover:border-blue-300 hover:text-blue-600"
                >
                  <FolderPlus className="h-3.5 w-3.5" />
                  New group
                </button>
              )}
            </div>
          )}

          {layers.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-slate-400">No layers added yet</p>
          ) : (
            layerRows.map(({ layer, nested }) => {
              const layerIndex = topLevelIds.indexOf(layer.id);

              // A group is a folder, not a layer — its own small row, with
              // its own tiny menu (see buildLayerMenu's early return for
              // `isGroup`). None of the layer-only chrome below applies to
              // it: no transparency, no scale range, no service badges.
              if (layer.isGroup) {
                const memberCount = childrenOf(layer.id).length;
                return (
                  <div key={layer.id} className="relative border-b border-slate-100 px-3 py-0.5 last:border-b-0">
                    <div
                      className={`gl-row flex items-center gap-2 px-1.5 py-2 ${
                        menuLayerId === layer.id ? 'gl-row--selected' : ''
                      }`}
                    >
                      <button
                        onClick={() => toggleGroup(layer)}
                        className={`flex h-5 w-5 items-center justify-center rounded border ${layer.checked ? 'border-blue-600 bg-blue-600' : 'border-slate-300 bg-white'}`}
                        aria-label={`Toggle group ${layer.name}`}
                      >
                        {layer.checked && <Check className="h-3.5 w-3.5 text-white" />}
                      </button>
                      <Layers className="h-4 w-4 text-slate-400" />
                      {renamingGroupId === layer.id ? (
                        <input
                          autoFocus
                          value={groupRenameDraft}
                          onChange={(event) => setGroupRenameDraft(event.target.value)}
                          onBlur={() => commitGroupRename(layer)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') commitGroupRename(layer);
                            if (event.key === 'Escape') setRenamingGroupId(null);
                          }}
                          aria-label={`Rename group ${layer.name}`}
                          className="min-w-0 flex-1 rounded border border-blue-300 px-1.5 py-0.5 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-blue-100"
                        />
                      ) : (
                        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-700">
                          {layer.name}
                        </span>
                      )}
                      <span className="flex-shrink-0 text-[10px] font-medium text-slate-400">
                        {memberCount}
                      </span>
                      <button
                        onClick={() => onToggleExpand(layer.id)}
                        className="p-1 text-slate-500"
                        aria-label={`${layer.expanded ? 'Collapse' : 'Expand'} ${layer.name}`}
                      >
                        {layer.expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                      <Tooltip content="Rename or ungroup">
                        <button
                          ref={(node) => {
                            menuAnchorRefs.current[layer.id] = node;
                          }}
                          onClick={() => setMenuLayerId((currentId) => (currentId === layer.id ? null : layer.id))}
                          aria-haspopup="menu"
                          aria-expanded={menuLayerId === layer.id}
                          className="rounded p-1 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
                          aria-label={`Group actions for ${layer.name}`}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </Tooltip>
                    </div>
                    {menuLayerId === layer.id && (
                      <LayerOptionsMenu
                        items={buildLayerMenu(layer, layerIndex)}
                        anchorRef={{ current: menuAnchorRefs.current[layer.id] ?? null }}
                        onClose={() => setMenuLayerId(null)}
                      />
                    )}
                  </div>
                );
              }

              return (
              <div key={layer.id} className={`relative border-b border-slate-100 px-3 py-0.5 last:border-b-0 ${nested ? 'ml-5 border-l pl-2' : ''}`}>
                <div
                  className={`gl-row flex items-center gap-2 px-1.5 py-2 ${
                    menuLayerId === layer.id ? 'gl-row--selected' : ''
                  }`}
                >
                  <button onClick={() => onToggleLayer(layer.id)} className={`flex h-5 w-5 items-center justify-center rounded border ${layer.checked ? 'border-blue-600 bg-blue-600' : 'border-slate-300 bg-white'}`} aria-label={`Toggle ${layer.name}`}>
                    {layer.checked && <Check className="h-3.5 w-3.5 text-white" />}
                  </button>
                  {renamingLayerId === layer.id ? (
                    <input
                      autoFocus
                      value={renameDraft}
                      onChange={(event) => setRenameDraft(event.target.value)}
                      onBlur={() => commitRename(layer)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') commitRename(layer);
                        if (event.key === 'Escape') setRenamingLayerId(null);
                      }}
                      aria-label={`Rename ${layer.name}`}
                      className="min-w-0 flex-1 rounded border border-blue-300 px-1.5 py-0.5 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  ) : (
                    <span
                      className="min-w-0 flex-1 truncate text-sm font-medium text-slate-700"
                      title={
                        layer.displayName ? `${layer.displayName} (service: ${layer.name})` : layer.name
                      }
                    >
                      {getLayerDisplayName(layer)}
                    </span>
                  )}
                  {/* A scratch layer is not part of the map everyone else sees,
                      and nothing in a TOC row would otherwise say so. */}
                  {layer.temporary && (
                    <Tooltip
                      title="Your temporary layer"
                      content={`Created by ${layer.temporarySource ?? 'a tool'}. It stays until you remove it, is never saved into this map, and nobody else can see it.`}
                    >
                      <span
                        tabIndex={0}
                        className="flex h-4 flex-shrink-0 items-center rounded bg-violet-100 px-1 text-[9px] font-bold uppercase tracking-wide text-violet-700"
                      >
                        temp
                      </span>
                    </Tooltip>
                  )}
                  {/* The service refuses to draw this layer at this zoom, so
                      the map is empty for a reason the TOC would otherwise keep
                      to itself. Clicking jumps to a zoom inside the band. */}
                  {layer.checked && !isVisibleAtZoom(layer, mapZoom) && (
                    <Tooltip title="Not visible at this zoom" content={scaleBandHint(layer)}>
                      <button
                        type="button"
                        onClick={() => {
                          const view = getLayerView(layer);
                          onGoToLocation(view?.center ?? mapCenter, view?.zoom ?? mapZoom);
                        }}
                        aria-label={`${getLayerDisplayName(layer)} is not visible at this zoom — go to a zoom where it draws`}
                        className="flex h-4 flex-shrink-0 items-center rounded bg-amber-100 px-1 text-[9px] font-bold uppercase tracking-wide text-amber-700 transition hover:bg-amber-200"
                      >
                        scale
                      </button>
                    </Tooltip>
                  )}
                  {/* Quiet indicator that this layer is labelled, so the state
                      is visible without opening the menu. */}
                  {labelingIsActive(layer.labeling) && (
                    <Tooltip
                      title="Labels enabled"
                      content="This layer draws labels on the map. Configure them from the 3-dot menu."
                    >
                      <span
                        aria-label="Labels enabled"
                        tabIndex={0}
                        className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded bg-slate-100 text-[9px] font-bold text-slate-500"
                      >
                        A
                      </span>
                    </Tooltip>
                  )}
                  {layer.sublayers.length > 0 && <button onClick={() => onToggleExpand(layer.id)} className="p-1 text-slate-500" aria-label={`Expand ${layer.name}`}>{layer.expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</button>}
                  <Tooltip content="Zoom, attributes, symbology, labels and more for this layer">
                    <button
                      ref={(node) => {
                        menuAnchorRefs.current[layer.id] = node;
                      }}
                      onClick={() => setMenuLayerId((currentId) => (currentId === layer.id ? null : layer.id))}
                      aria-haspopup="menu"
                      aria-expanded={menuLayerId === layer.id}
                      className="rounded p-1 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
                      aria-label={`Layer actions for ${getLayerDisplayName(layer)}`}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                  </Tooltip>
                </div>
                {layer.expanded && layer.sublayers.map((sublayer) => <p key={sublayer} className="ml-12 py-1 text-xs text-slate-500">{sublayer}</p>)}

                {/* Inline transparency control */}
                {opacityLayerId === layer.id && (
                  <div className="mb-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-semibold text-slate-600">Transparency</span>
                      <button
                        type="button"
                        onClick={() => setOpacityLayerId(null)}
                        className="text-[11px] text-slate-400 hover:text-slate-700"
                      >
                        Done
                      </button>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={Math.round((layer.opacity ?? 1) * 100)}
                      onChange={(event) => {
                        const opacity = Number(event.target.value) / 100;
                        // Same rule buildLayerMenu's `editable` uses: an
                        // Admin's edit writes to the saved module, an end
                        // user's writes to this session only.
                        const canEditSaved =
                          (canManageLayers || Boolean(layer.temporary)) && Boolean(onLayerChange);
                        if (canEditSaved) onLayerChange?.(layer.id, { opacity });
                        else onOpacityPreview?.(layer.id, opacity);
                      }}
                      aria-label={`Opacity for ${getLayerDisplayName(layer)}`}
                      className="mt-1.5 w-full accent-blue-600"
                    />
                    <p className="text-right text-[11px] text-slate-500">
                      {Math.round((layer.opacity ?? 1) * 100)}%
                    </p>
                  </div>
                )}

                {/* Inline visibility-range control — same card as Transparency,
                    a scale band instead of a percentage. Off by default: an
                    admin who never opens this sees the layer draw exactly as
                    it always did. */}
                {visibleRangeLayerId === layer.id && (
                  <div className="mb-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-semibold text-slate-600">Visibility range</span>
                      <button
                        type="button"
                        onClick={() => setVisibleRangeLayerId(null)}
                        className="text-[11px] text-slate-400 hover:text-slate-700"
                      >
                        Done
                      </button>
                    </div>

                    <label className="mt-1.5 flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={layer.visibleScale?.enabled ?? false}
                        onChange={(event) =>
                          onLayerChange?.(layer.id, {
                            visibleScale: {
                              enabled: event.target.checked,
                              minScale: layer.visibleScale?.minScale ?? 0,
                              maxScale: layer.visibleScale?.maxScale ?? 0,
                            },
                          })
                        }
                        className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600"
                      />
                      <span className="text-[11.5px] text-slate-600">Only draw within a scale range</span>
                    </label>

                    {layer.visibleScale?.enabled && (
                      <div className="mt-2 space-y-2">
                        <label className="block">
                          <span className="text-[10.5px] text-slate-500">Preset</span>
                          <select
                            value=""
                            onChange={(event) => {
                              const preset = SCALE_PRESETS.find((item) => item.id === event.target.value);
                              if (!preset) return;
                              onLayerChange?.(layer.id, {
                                visibleScale: {
                                  enabled: true,
                                  minScale: preset.minScale,
                                  maxScale: preset.maxScale,
                                },
                              });
                            }}
                            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11.5px] outline-none focus:border-blue-400"
                          >
                            <option value="">-- Choose a preset --</option>
                            {SCALE_PRESETS.map((preset) => (
                              <option key={preset.id} value={preset.id}>
                                {preset.label} ({formatScale(preset.minScale)} –{' '}
                                {formatScale(preset.maxScale)})
                              </option>
                            ))}
                          </select>
                          <span className="mt-1 block text-[10px] text-slate-400">
                            A preset only fills the two values below; both stay editable.
                          </span>
                        </label>

                        <ScaleBoundInput
                          label="Minimum scale (zoomed out)"
                          value={layer.visibleScale.minScale}
                          currentScale={scaleForZoom(mapZoom)}
                          onChange={(minScale) =>
                            onLayerChange?.(layer.id, {
                              visibleScale: { ...layer.visibleScale!, minScale },
                            })
                          }
                        />
                        <ScaleBoundInput
                          label="Maximum scale (zoomed in)"
                          value={layer.visibleScale.maxScale}
                          currentScale={scaleForZoom(mapZoom)}
                          onChange={(maxScale) =>
                            onLayerChange?.(layer.id, {
                              visibleScale: { ...layer.visibleScale!, maxScale },
                            })
                          }
                        />
                        <p className="text-[10px] leading-snug text-slate-400">
                          Draws between these two scales — outside the range the layer is hidden on
                          the map entirely, the same as unticking it. Leave a box blank for no limit
                          on that side.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Inline "move to group" picker, opened from the 3-dot menu. */}
                {groupPickerLayerId === layer.id && (
                  <div className="mb-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-semibold text-slate-600">Move to group</span>
                      <button
                        type="button"
                        onClick={() => setGroupPickerLayerId(null)}
                        className="text-[11px] text-slate-400 hover:text-slate-700"
                      >
                        Cancel
                      </button>
                    </div>
                    <div className="mt-1.5 space-y-1">
                      {groups.map((group) => (
                        <button
                          key={group.id}
                          type="button"
                          onClick={() => moveToGroup(layer.id, group.id)}
                          className="flex w-full items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-left text-[12px] text-slate-700 transition hover:border-blue-300 hover:bg-blue-50"
                        >
                          <Layers className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                          <span className="min-w-0 flex-1 truncate">{group.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {menuLayerId === layer.id && (
                  <LayerOptionsMenu
                    items={buildLayerMenu(layer, layerIndex)}
                    anchorRef={{ current: menuAnchorRefs.current[layer.id] ?? null }}
                    onClose={() => setMenuLayerId(null)}
                  />
                )}
              </div>
              );
            })
          )}
        </div>

        <div style={{ display: activeTab === 'legend' ? 'block' : 'none' }}>
          {/* The same rule the printed legend follows: what is ACTUALLY on the
              map. Ticked is not the same as drawn — a layer outside its scale
              band, or turned down to no opacity, is switched on and invisible,
              and a key for a symbol that appears nowhere sends the reader
              looking for it. `hiddenByZoom` is why the panel can say so
              instead of just going quiet. */}
          <LegendPanel
            layers={legendLayers}
            geoServerUrl={geoServerUrl}
            arcgisBaseUrl={getArcgisBaseUrl(arcgisUrl)}
            displayName={getLayerDisplayName}
            hiddenByZoom={legendHiddenByZoom}
          />
        </div>

        <div style={{ display: activeTab === 'draw' ? 'block' : 'none' }} className="space-y-3 px-3 pb-4">
          <ToolPanelHeader
            title="Drawing Tools"
            description="Pick a draw type and start sketching directly on the map."
            onClose={() => handleCloseTool('draw')}
          />
          {(() => {
            // The sketch pad is just another layer in `layers` (see
            // addDrawnFeature) — reading it back out here, rather than
            // threading the feature list down as its own prop, means this
            // list can never drift from what is actually on the map.
            const drawingsLayer = layers.find(
              (layer) => layer.temporary && layer.temporarySource === 'Draw',
            );
            const drawnFeatures = drawingsLayer?.localData?.features ?? [];
            if (drawnFeatures.length === 0) return null;

            const shapeLabel = (feature: GeoJSON.Feature, index: number) => {
              const kind =
                feature.geometry?.type === 'Point'
                  ? 'Point'
                  : feature.geometry?.type === 'LineString'
                    ? 'Line'
                    : feature.geometry?.type === 'Polygon'
                      ? 'Polygon'
                      : feature.geometry?.type ?? 'Shape';
              return `${kind} ${index + 1}`;
            };

            return (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Drawn features
                  </span>
                  <span className="text-[11px] tabular-nums text-slate-400">{drawnFeatures.length}</span>
                </div>
                <div className="max-h-[220px] space-y-1 overflow-y-auto pr-0.5">
                  {drawnFeatures.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1.5"
                    >
                      <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-slate-700">
                        {shapeLabel(feature, index)}
                      </span>
                      <button
                        type="button"
                        onClick={() => onZoomToDrawnFeature?.(feature)}
                        title="Zoom to this feature"
                        aria-label="Zoom to this feature"
                        className="flex-shrink-0 rounded p-1 text-slate-400 transition hover:bg-blue-50 hover:text-blue-600"
                      >
                        <Crosshair className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDeleteDrawnFeature?.(index)}
                        title="Delete this feature"
                        aria-label="Delete this feature"
                        className="flex-shrink-0 rounded p-1 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
                {/* Builder-only: a reader's drawings stay their own scratch
                    layer (already auto-saved to their browser) rather than
                    becoming part of the shared map. */}
                {canManageLayers && onSaveDrawings && (
                  <button
                    type="button"
                    onClick={onSaveDrawings}
                    className="gl-btn gl-btn--secondary w-full whitespace-nowrap px-3 py-1.5 text-[12.5px]"
                  >
                    <Save className="h-3.5 w-3.5" />
                    Save to map
                  </button>
                )}
              </div>
            );
          })()}
          <div className="space-y-3">
            <label htmlFor="draw-type" className="block text-[12px] font-medium text-slate-600">Draw Type</label>
            <select
              id="draw-type"
              value={selectedDrawType}
              onChange={(event) => onDrawTypeChange?.(event.target.value as 'point' | 'line' | 'polygon' | 'circle')}
              className="gl-input w-full px-2.5 py-1.5 text-[12.5px]"
            >
              {drawShapes.map((shape) => (
                <option key={shape} value={shape}>
                  {shape.charAt(0).toUpperCase() + shape.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <ToolActions>
            <button
              type="button"
              onClick={() => {
                setIsMeasuring(false);
                onStartDrawing?.();
              }}
              className="gl-btn gl-btn--primary whitespace-nowrap px-3 py-1.5 text-[12.5px]"
            >
              {currentlyDrawing ? 'Continue Drawing' : 'Start Drawing'}
            </button>
            {/* Stops sketching without touching anything already drawn —
                "Clear All" deletes every finished shape, which is a much
                bigger hammer than just wanting out of an in-progress sketch.
                Any uncommitted vertices of the CURRENT shape are abandoned,
                the same as pressing Escape elsewhere in this app; shapes
                already finished stay exactly as they were. */}
            {currentlyDrawing && onStopDrawing && (
              <button
                type="button"
                onClick={onStopDrawing}
                className="gl-btn gl-btn--secondary whitespace-nowrap px-3 py-1.5 text-[12.5px]"
              >
                <Square className="h-3.5 w-3.5" />
                Stop
              </button>
            )}
            {/* An explicit way out of a line or polygon. Double-click is the
                gesture people know, but it is also the one that can silently
                fail — a button cannot. Only shown once the shape has enough
                vertices to be finishable. */}
            {onFinishShape && sketchVertices >= (selectedDrawType === 'polygon' ? 3 : 2) && (
              <button
                type="button"
                onClick={onFinishShape}
                className="gl-btn gl-btn--primary whitespace-nowrap px-3 py-1.5 text-[12.5px]"
              >
                <Check className="h-3.5 w-3.5" />
                Finish shape
              </button>
            )}
            <button
              type="button"
              onClick={() => onClearAll?.()}
              className="gl-btn gl-btn--danger whitespace-nowrap px-3 py-1.5 text-[12.5px]"
            >
              Clear All
            </button>
          </ToolActions>
          {currentlyDrawing && (
            <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-[12px] leading-snug text-blue-700">
              {selectedDrawType === 'point'
                ? 'Click the map to drop a point.'
                : selectedDrawType === 'circle'
                  ? 'Click the centre, then click again to set the radius.'
                  : `Click each corner. Finish with the button above, by pressing Enter, by clicking the first point again, or by double-clicking.`}
              <span className="mt-0.5 block text-[11px] text-blue-600/80">
                {sketchVertices > 0
                  ? `${sketchVertices} point${sketchVertices === 1 ? '' : 's'} placed · Esc abandons this shape`
                  : 'Shapes collect in a temporary “Drawings” layer.'}
              </span>
            </div>
          )}
        </div>

        <div style={{ display: activeTab === 'print' ? 'block' : 'none' }} className="space-y-3 px-3 pb-4">
          <ToolPanelHeader
            title="Print Map"
            description="Lay the map out on a page, then print it or save it."
            onClose={() => handleCloseTool('print')}
          />
          <div className="space-y-2.5">
            <label className="block space-y-1 text-[12px] font-medium text-slate-600">
              <span>Page size</span>
              <select
                value={printPageSize}
                onChange={(event) => setPrintPageSize(event.target.value as 'A4' | 'A3' | 'Letter')}
                className="gl-input w-full px-2.5 py-1.5 text-[12.5px]"
              >
                <option value="A4">A4</option>
                <option value="A3">A3</option>
                <option value="Letter">Letter</option>
              </select>
            </label>
            <label className="block space-y-1 text-[12px] font-medium text-slate-600">
              <span>Orientation</span>
              <select
                value={printOrientation}
                onChange={(event) => setPrintOrientation(event.target.value as 'Portrait' | 'Landscape')}
                className="gl-input w-full px-2.5 py-1.5 text-[12.5px]"
              >
                <option value="Portrait">Portrait</option>
                <option value="Landscape">Landscape</option>
              </select>
            </label>
          </div>
          <label className="block text-[12px] font-medium text-slate-600">
            <span className="mb-1 block">Title</span>
            <input
              value={printTitle}
              onChange={(event) => setPrintTitle(event.target.value)}
              className="gl-input w-full px-2.5 py-1.5 text-[12.5px]"
              placeholder={module?.title ?? 'Map'}
            />
          </label>
          <label className="block text-[12px] font-medium text-slate-600">
            <span className="mb-1 block">Subtitle</span>
            <input
              value={printSubtitle}
              onChange={(event) => setPrintSubtitle(event.target.value)}
              className="gl-input w-full px-2.5 py-1.5 text-[12.5px]"
              placeholder="Optional — a date, a client, a sheet number"
            />
          </label>

          {/* The furniture that makes a map sheet trustworthy: what it is,
              how big things are, which way is up, and when it was made. */}
          <div className="space-y-1 rounded-lg border border-slate-200 bg-slate-50 p-2">
            {([
              { id: 'legend' as const, label: 'Legend', on: printIncludeLegend, set: setPrintIncludeLegend },
              { id: 'scale' as const, label: 'Scale bar', on: printScaleBar, set: setPrintScaleBar },
              { id: 'north' as const, label: 'North arrow', on: printNorthArrow, set: setPrintNorthArrow },
              { id: 'date' as const, label: 'Date printed', on: printDate, set: setPrintDate },
            ]).map((entry) => (
              <label
                key={entry.id}
                className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-white"
              >
                <input
                  type="checkbox"
                  checked={entry.on}
                  onChange={(event) => entry.set(event.target.checked)}
                  className="h-3.5 w-3.5 accent-blue-600"
                />
                <span className="text-[12px] text-slate-700">{entry.label}</span>
              </label>
            ))}
          </div>

          <ToolActions>
            <button
              type="button"
              onClick={() => void runPrint('pdf')}
              disabled={printGenerating}
              className="gl-btn gl-btn--primary whitespace-nowrap px-3 py-1.5 text-[12.5px]"
            >
              <Printer className="h-3.5 w-3.5" />
              {printGenerating ? 'Building…' : 'Print / PDF'}
            </button>
            <button
              type="button"
              onClick={() => void runPrint('png')}
              disabled={printGenerating}
              className="gl-btn gl-btn--secondary whitespace-nowrap px-3 py-1.5 text-[12.5px]"
            >
              <Download className="h-3.5 w-3.5" />
              PNG
            </button>
          </ToolActions>

          {printNote && (
            <p className="rounded-lg bg-amber-50 px-2.5 py-2 text-[11.5px] leading-snug text-amber-800">
              {printNote}
            </p>
          )}

          <p className="text-[10.5px] leading-snug text-slate-400">
            The PDF and the PNG are the same sheet — “Print / PDF” opens your browser's print
            dialog, where “Save as PDF” is the destination.
          </p>
        </div>

        <div style={{ display: activeTab === 'measure' ? 'block' : 'none' }} className="space-y-3 px-3 pb-4">
          <ToolPanelHeader
            title="Measurement Tools"
            description="Track distance or area measurements in the map."
            onClose={() => handleCloseTool('measure')}
          />
          <div className="space-y-3">
            <label className="block space-y-1 text-[12px] font-medium text-slate-600">
              <span>Measurement Type</span>
              <select
                value={measureType}
                onChange={(event) => {
                  setMeasureType(event.target.value as 'line' | 'polygon');
                  setIsMeasuring(false);
                }}
                className="gl-input w-full px-2.5 py-1.5 text-[12.5px]"
              >
                <option value="line">Distance</option>
                <option value="polygon">Area</option>
              </select>
            </label>
            {measureType === 'line' ? (
              <label className="block space-y-1 text-[12px] font-medium text-slate-600">
                <span>Distance Unit</span>
                <select
                  value={measureUnit}
                  onChange={(event) => setMeasureUnit(event.target.value as 'meters' | 'kilometers' | 'feet' | 'miles')}
                  className="gl-input w-full px-2.5 py-1.5 text-[12.5px]"
                >
                  <option value="meters">Meters</option>
                  <option value="kilometers">Kilometers</option>
                  <option value="feet">Feet</option>
                  <option value="miles">Miles</option>
                </select>
              </label>
            ) : (
              <label className="block space-y-1 text-[12px] font-medium text-slate-600">
                <span>Area Unit</span>
                <select
                  value={measureAreaUnit}
                  onChange={(event) => setMeasureAreaUnit(event.target.value as 'sqmeters' | 'sqkilometers' | 'hectares' | 'acres')}
                  className="gl-input w-full px-2.5 py-1.5 text-[12.5px]"
                >
                  <option value="sqmeters">Square Meters</option>
                  <option value="sqkilometers">Square Kilometers</option>
                  <option value="hectares">Hectares</option>
                  <option value="acres">Acres</option>
                </select>
              </label>
            )}
          </div>
          <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-[12px] leading-snug text-blue-700">
            {isMeasuring ? (
              <>
                {measureType === 'line'
                  ? 'Click along the route you want measured.'
                  : 'Click around the area you want measured.'}
                <span className="mt-0.5 block text-[11px] text-blue-600/80">
                  {sketchVertices > 0
                    ? `${sketchVertices} point${sketchVertices === 1 ? '' : 's'} placed · finish below, with Enter, or by double-clicking`
                    : 'Finish with the button below, with Enter, or by double-clicking.'}
                </span>
              </>
            ) : (
              'Choose what to measure, then start. Results collect below.'
            )}
          </div>
          <div className="space-y-3">
            <button
              type="button"
              onClick={isMeasuring ? stopMeasurement : startMeasurement}
              className={`gl-btn w-full whitespace-nowrap px-3 py-1.5 text-[12.5px] ${isMeasuring ? 'gl-btn--danger' : 'gl-btn--primary'}`}
            >
              {isMeasuring ? 'Stop Measuring' : 'Start Measuring'}
            </button>
            <ToolActions>
              {/* Same four ways out as the Draw tool, because it is the same
                  sketching code — a measurement that could only be ended by
                  double-clicking would inherit the one gesture that can fail. */}
              {isMeasuring &&
                onFinishShape &&
                sketchVertices >= (measureType === 'polygon' ? 3 : 2) && (
                  <button
                    type="button"
                    onClick={onFinishShape}
                    className="gl-btn gl-btn--primary whitespace-nowrap px-3 py-1.5 text-[12.5px]"
                  >
                    <Check className="h-3.5 w-3.5" />
                    Finish &amp; measure
                  </button>
                )}
              <button
                type="button"
                onClick={clearMeasurements}
                disabled={measurements.length === 0}
                className="gl-btn gl-btn--danger whitespace-nowrap px-3 py-1.5 text-[12.5px]"
              >
                Clear All
              </button>
            </ToolActions>
          </div>
          <div className="space-y-2">
            <p className="text-[12px] font-semibold text-slate-600">Measurements ({measurements.length})</p>
            {measurements.length === 0 ? (
              <p className="text-[12px] text-slate-400">No measurements recorded yet.</p>
            ) : (
              measurements.map((measurement) => (
                <div key={measurement.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5">
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{measurement.type}</p>
                    <p className="truncate text-[13px] font-semibold tabular-nums text-slate-700">{measurement.value}</p>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-1">
                    {measurement.geometry && (
                      <button
                        type="button"
                        onClick={() => {
                          const boxes = getGeometryBounds(measurement.geometry);
                          const union = unionBounds(boxes);
                          const view = union ? getLayerZoom(union) : null;
                          if (view) onGoToLocation(view.center, view.zoom);
                        }}
                        className="rounded px-1.5 py-0.5 text-[11px] font-semibold text-blue-600 transition hover:bg-blue-50"
                      >
                        Zoom
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setMeasurements((prev) => prev.filter((item) => item.id !== measurement.id))}
                      className="rounded px-1.5 py-0.5 text-[11px] font-semibold text-red-600 transition hover:bg-red-50"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div style={{ display: activeTab === 'filter' ? 'block' : 'none' }} className="space-y-3 px-3 pb-4">
          <ToolPanelHeader
            title="Filter Features"
            description="Create a filter expression for the selected layer."
            onClose={() => handleCloseTool('filter')}
          />
          <div className="space-y-3">
            <label className="block space-y-1 text-[12px] font-medium text-slate-600">
              <span>Select Layer</span>
              <select
                value={filterLayer}
                onChange={(event) => {
                  setFilterLayer(event.target.value);
                  void fetchLayerFields('filter', event.target.value);
                }}
                className="gl-input w-full px-2.5 py-1.5 text-[12.5px]"
              >
                <option value="">-- Select Layer --</option>
                {layerOptionsFor('filter').map((layer) => (
                  <option key={layer.id} value={layer.id}>{layer.name}</option>
                ))}
              </select>
            </label>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5">
              {filterClauses.map((clause, idx) => (
                <div key={clause.id} className="mb-2 flex flex-wrap items-center gap-1.5">
                  <div className="min-w-[72px] text-xs font-semibold text-slate-600">{idx === 0 ? 'Where' : ''}</div>
                  {idx !== 0 && (
                    <select
                      value={clause.conjunction}
                      onChange={(event) => {
                        const conjunction = event.target.value;
                        setFilterClauses((prev) =>
                          prev.map((item, i) => (i === idx ? { ...item, conjunction } : item)),
                        );
                      }}
                      className="gl-input w-full max-w-[72px] px-2 py-1.5 text-[12.5px]"
                    >
                      <option value="And">And</option>
                      <option value="Or">Or</option>
                    </select>
                  )}
                  <select
                    value={clause.field}
                    onChange={(event) => {
                      const field = event.target.value;
                      setFilterClauses((prev) =>
                        prev.map((item, i) => (i === idx ? { ...item, field, value: '' } : item)),
                      );
                      // Same suggestions the Query tool offers — one loader,
                      // both panels, keyed on the clause so two clauses on
                      // different fields do not share a list.
                      if (field) void fetchFieldValues(filterLayer, field, clause.id);
                    }}
                    className="gl-input w-full px-2.5 py-1.5 text-[12.5px]"
                  >
                    <option value="">
                      {filterFieldsLoading
                        ? 'Loading fields…'
                        : !filterLayer
                          ? 'Choose a layer first'
                          : filterLayerFields.length === 0
                            ? 'No fields available'
                            : 'Field'}
                    </option>
                    {filterLayerFields.map((field) => (
                      <option key={field} value={field}>{field}</option>
                    ))}
                  </select>
                  <select
                    value={clause.operator}
                    onChange={(event) => {
                      const operator = event.target.value;
                      setFilterClauses((prev) =>
                        prev.map((item, i) => (i === idx ? { ...item, operator } : item)),
                      );
                    }}
                    className="gl-input w-full px-2.5 py-1.5 text-[12.5px]"
                  >
                    {[
                      'is equal to',
                      'is not equal to',
                      'is greater than',
                      'is less than',
                      'contains',
                      'starts with',
                      'is blank',
                      'is not blank',
                    ].map((operator) => (
                      <option key={operator} value={operator}>{operator}</option>
                    ))}
                  </select>
                  <input
                    value={clause.value}
                    onChange={(event) => {
                      const value = event.target.value;
                      setFilterClauses((prev) =>
                        prev.map((item, i) => (i === idx ? { ...item, value } : item)),
                      );
                    }}
                    list={`filter-values-${clause.id}`}
                    disabled={clause.operator === 'is blank' || clause.operator === 'is not blank'}
                    placeholder={
                      queryFieldValuesLoading[clause.id]
                        ? 'Loading values…'
                        : !clause.field
                          ? 'Pick a field first'
                          : (queryFieldValues[clause.id]?.length ?? 0) > 0
                            ? `Value — ${queryFieldValues[clause.id].length} found`
                            : 'Value'
                    }
                    className="gl-input w-full px-2.5 py-1.5 text-[12.5px] disabled:bg-slate-100 disabled:text-slate-400"
                  />
                  <datalist id={`filter-values-${clause.id}`}>
                    {(queryFieldValues[clause.id] ?? []).map((value) => (
                      <option key={value} value={value} />
                    ))}
                  </datalist>
                  <button
                    type="button"
                    onClick={() => {
                      if (filterClauses.length > 1) {
                        setFilterClauses((prev) => prev.filter((item) => item.id !== clause.id));
                      }
                    }}
                    disabled={filterClauses.length === 1}
                    className="gl-btn gl-btn--danger whitespace-nowrap px-2.5 py-1.5 text-[12px]"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  setFilterClauses((prev) => [
                    ...prev,
                    { id: nextClauseId(), field: '', operator: 'is equal to', value: '', conjunction: 'And' },
                  ])
                }
                className="text-[12px] font-semibold text-blue-600 transition hover:text-blue-800"
              >
                + Add Clause
              </button>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={filterInvertWhere}
                onChange={(event) => setFilterInvertWhere(event.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-blue-600"
              />
              <span className="text-[12px] text-slate-600">Invert Where Clause</span>
            </div>
            {filterError && (
              <p role="alert" className="rounded-lg bg-red-50 px-2.5 py-2 text-[11.5px] text-red-700">
                {filterError}
              </p>
            )}
            {/* Read STRAIGHT off the layers, not from a list kept beside them.
                The old chip list was its own record of what had been applied,
                so it could — and did — show a filter the map was not using. */}
            {filteredLayers.length > 0 && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2.5">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                    Active filters ({filteredLayers.length})
                  </span>
                  <span className="text-[10px] text-emerald-600">Cleared when this tool closes</span>
                </div>
                <div className="space-y-2">
                  {filteredLayers.map((layer) => (
                    <div key={layer.id} className="rounded-lg border border-emerald-100 bg-white p-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="min-w-0 truncate text-[12.5px] font-medium text-emerald-900">
                          {getLayerDisplayName(layer)}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            onLayerChange?.(layer.id, {
                              filter: undefined,
                              filterClauses: undefined,
                              filterInvert: undefined,
                            });
                            filterAppliedRef.current = filterAppliedRef.current.filter(
                              (id) => id !== layer.id,
                            );
                          }}
                          className="flex-shrink-0 rounded px-1.5 py-0.5 text-[11px] font-semibold text-red-600 transition hover:bg-red-50"
                        >
                          Remove
                        </button>
                      </div>
                      <p className="mt-1.5 whitespace-pre-wrap break-words font-mono text-[11px] text-slate-600">
                        {layer.filter}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="space-y-2.5">
            <button
              type="button"
              onClick={() => {
                const layer = getLayerById(filterLayer);
                if (!layer) {
                  setFilterError('Choose a layer first.');
                  return;
                }

                const clauses: AttributeClause[] = filterClauses
                  .filter((clause) => clause.field)
                  .map((clause) => ({
                    id: String(clause.id),
                    field: clause.field,
                    operator: QUERY_OPERATORS[clause.operator] ?? 'equals',
                    value: clause.value ?? '',
                    conjunction: (clause.conjunction === 'Or' ? 'or' : 'and') as 'and' | 'or',
                  }))
                  .filter(clauseIsComplete);

                if (clauses.length === 0) {
                  setFilterError('Fill in at least one condition.');
                  return;
                }

                /* GeoServer speaks CQL, ArcGIS speaks SQL. The clauses are
                   structured precisely so the same expression can become
                   either — see attributeQuery.ts. */
                const dialect = layer.arcgisLayerId === undefined ? 'cql' : 'sql';
                const expression = buildFilterExpression(
                  clauses,
                  dialect,
                  filterInvertWhere,
                  numericFields,
                );

                setFilterError(null);

                /* Written to the LAYER, which is what the map actually reads —
                   `filter` reaches the WMS request as CQL_FILTER, the
                   FeatureServer query as `where`, and the MapServer export as
                   layerDefs. The old handler only pushed the text into a local
                   chip list, so "Apply Filter" listed an expression and changed
                   nothing on the map. */
                /* The clauses travel with the expression. A file-backed
                   layer has no service to hand the string to, so the browser
                   evaluates these instead — see layers/localFilter.ts. */
                onLayerChange?.(layer.id, {
                  filter: expression,
                  filterClauses: clauses,
                  filterInvert: filterInvertWhere,
                });
                if (!filterAppliedRef.current.includes(layer.id)) {
                  filterAppliedRef.current = [...filterAppliedRef.current, layer.id];
                }
              }}
              className="gl-btn gl-btn--primary whitespace-nowrap px-3 py-1.5 text-[12.5px]"
            >
              Apply Filter
            </button>
            <button
              type="button"
              onClick={() => {
                /* Every layer the tool filtered, not just whichever one the
                   dropdown happens to show. Filter layer A, switch the
                   dropdown to B, press Clear — the old version cleared B and
                   left A filtered with nothing on screen admitting it. */
                clearToolFilters();
                setFilterLayer('');
                setFilterError(null);
                setFilterClauses([
                  { id: nextClauseId(), field: '', operator: 'is equal to', value: '', conjunction: 'And' },
                ]);
                setFilterInvertWhere(false);
                setFilterLayerFields([]);
                // Same rule as the Query tool: clearing the expression clears
                // what it picked out.
                onSelectionChange?.([]);
              }}
              className="gl-btn gl-btn--danger whitespace-nowrap px-3 py-1.5 text-[12.5px]"
            >
              Clear
            </button>
          </div>
        </div>

        <div style={{ display: activeTab === 'assetSearch' ? 'block' : 'none' }} className="space-y-3 px-3 pb-4">
          <ToolPanelHeader
            title="Asset Search"
            description="Drop a flag, then find what lies within a distance of it."
            onClose={() => handleCloseTool('assetSearch')}
          />
          {/* The incident point: dropped on the map, or typed. Both end up as
              the same flag, because they are the same thing said two ways. */}
          <div className="space-y-1.5">
            <span className="block text-[12px] font-medium text-slate-600">Incident point</span>

            {incidentPoint ? (
              <div className="flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5">
                <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-white">
                  !
                </span>
                <span className="min-w-0 flex-1 truncate font-mono text-[11.5px] tabular-nums text-amber-900">
                  {incidentPoint.lat.toFixed(5)}, {incidentPoint.lng.toFixed(5)}
                </span>
                <button
                  type="button"
                  onClick={() => onGoToLocation([incidentPoint.lat, incidentPoint.lng], Math.max(mapZoom, 16))}
                  className="flex-shrink-0 rounded px-1.5 py-0.5 text-[10.5px] font-semibold text-amber-700 transition hover:bg-amber-100"
                >
                  Zoom
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIncidentPoint(null);
                    setAssetSearchResults([]);
                  }}
                  className="flex-shrink-0 rounded px-1.5 py-0.5 text-[10.5px] font-semibold text-red-600 transition hover:bg-red-50"
                >
                  Remove
                </button>
              </div>
            ) : (
              <p className="rounded-lg bg-slate-50 px-2.5 py-2 text-[11.5px] leading-snug text-slate-500">
                Drop a flag where the incident is, or type its coordinates.
              </p>
            )}

            <ToolActions>
              <button
                type="button"
                onClick={() => setPickingIncident((on) => !on)}
                className={`gl-btn whitespace-nowrap px-3 py-1.5 text-[12.5px] ${
                  pickingIncident ? 'gl-btn--danger' : 'gl-btn--primary'
                }`}
              >
                <LocateFixed className="h-3.5 w-3.5" />
                {pickingIncident ? 'Cancel' : incidentPoint ? 'Move flag' : 'Place flag'}
              </button>
            </ToolActions>
            {pickingIncident && <p className="text-[11px] text-blue-600">Click the map to drop the flag.</p>}

            <div className="flex gap-1.5">
              <input
                value={incidentTyped}
                onChange={(event) => setIncidentTyped(event.target.value)}
                onKeyDown={(event) => event.key === 'Enter' && applyTypedIncident()}
                placeholder="or 28.50408, 77.04163"
                className="gl-input w-full px-2.5 py-1.5 text-[12.5px]"
              />
              <button
                type="button"
                onClick={applyTypedIncident}
                className="gl-btn gl-btn--secondary whitespace-nowrap px-3 py-1.5 text-[12.5px]"
              >
                Set
              </button>
            </div>
          </div>
          <label className="block space-y-1 text-[12px] font-medium text-slate-600">
            <span>Search radius (meters)</span>
            <input
              type="number"
              min={50}
              value={assetSearchRadius}
              onChange={(event) => setAssetSearchRadius(Number(event.target.value))}
              className="gl-input w-full px-2.5 py-1.5 text-[12.5px]"
            />
          </label>
          <div className="space-y-2.5">
            <button
              type="button"
              onClick={handleAssetSearch}
              disabled={!incidentPoint || assetSearchLoading}
              className="gl-btn gl-btn--primary whitespace-nowrap px-3 py-1.5 text-[12.5px]"
            >
              {assetSearchLoading ? 'Searching…' : 'Search'}
            </button>
            <button
              type="button"
              onClick={() => {
                setIncidentPoint(null);
                setIncidentTyped('');
                setPickingIncident(false);
                setAssetSearchResults([]);
                setAssetSearchError(null);
                setExpandedNearMeLayers(new Set());
                onOverlayChange?.(null);
              }}
              className="gl-btn gl-btn--secondary whitespace-nowrap px-3 py-1.5 text-[12.5px]"
            >
              Clear
            </button>
          </div>
          {assetSearchError && (
            <p role="alert" className="rounded-lg bg-red-50 px-2.5 py-2 text-[11.5px] text-red-700">
              {assetSearchError}
            </p>
          )}
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Within {assetSearchRadius} m{' '}
                <span className="tabular-nums text-slate-400">({assetSearchResults.length})</span>
              </p>
              {assetSearchResults.length > 0 && (
                <button
                  type="button"
                  onClick={exportNearMeResults}
                  className="text-[11px] font-semibold text-blue-600 transition hover:text-blue-800"
                >
                  Export CSV
                </button>
              )}
            </div>
            {assetSearchResults.length === 0 ? (
              <p className="rounded-lg bg-slate-50 px-2.5 py-2 text-[11.5px] text-slate-500">
                {incidentPoint ? 'Nothing found yet — press Search.' : 'Place the incident point first.'}
              </p>
            ) : (
              <div className="max-h-72 space-y-1.5 overflow-y-auto pr-0.5">
                {assetSearchGroups.map((group) => {
                  const expanded = expandedNearMeLayers.has(group.layerId);
                  return (
                    <div key={group.layerId} className="overflow-hidden rounded-lg border border-slate-200">
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedNearMeLayers((current) => {
                            const next = new Set(current);
                            if (next.has(group.layerId)) next.delete(group.layerId);
                            else next.add(group.layerId);
                            return next;
                          })
                        }
                        aria-expanded={expanded}
                        className="flex w-full items-center justify-between gap-2 bg-slate-50 px-2.5 py-1.5 text-left transition hover:bg-slate-100"
                      >
                        <span className="flex min-w-0 items-center gap-1.5">
                          {expanded ? (
                            <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                          )}
                          <span className="truncate text-[12px] font-medium text-slate-700">
                            {group.layerName}
                          </span>
                        </span>
                        <span className="flex-shrink-0 rounded-full bg-slate-200 px-1.5 text-[10.5px] font-bold leading-[18px] tabular-nums text-slate-600">
                          {group.results.length}
                        </span>
                      </button>
                      {expanded && (
                        <div className="space-y-1 p-1.5">
                          {group.results.map((result, index) => (
                            <div key={index} className="flex w-full items-stretch gap-1">
                              <button
                                type="button"
                                onClick={() => {
                                  // Selects (the map highlight, same as a
                                  // Query/Select result) AND zooms to the one
                                  // fixed scale every other "zoom to" in this
                                  // app lands at — not just a plain pan, which
                                  // left the result unselected and at whatever
                                  // zoom the map already happened to be.
                                  const row = {
                                    ...(result.feature.properties ?? {}),
                                    id: String(result.feature.id ?? index),
                                    key: featureKey(result.feature, result.layerId),
                                    layerId: result.layerId,
                                    properties: (result.feature.properties ?? {}) as Record<string, unknown>,
                                    geometry: result.feature.geometry ?? undefined,
                                  };
                                  onSelectionChange?.([row]);
                                  onZoomToSelection?.([row]);
                                }}
                                className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-left transition hover:border-cyan-300 hover:bg-cyan-50"
                              >
                                <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-slate-700">
                                  {result.label}
                                </span>
                                <span className="flex-shrink-0 text-[11.5px] font-semibold tabular-nums text-slate-600">
                                  {result.metres < 1000
                                    ? `${Math.round(result.metres)} m`
                                    : `${(result.metres / 1000).toFixed(2)} km`}
                                </span>
                              </button>
                              {/* Opens Directions pre-filled: A is always this
                                  search's own incident point, B is this
                                  result — the one pairing "directions to a
                                  Near Me hit" can mean. Hidden when the
                                  Directions tool is not part of this module,
                                  same as every other cross-tool affordance. */}
                              {tools.includes('direction') && (
                                <button
                                  type="button"
                                  title={`Directions from the incident point to ${result.label}`}
                                  aria-label={`Directions from the incident point to ${result.label}`}
                                  onClick={() => {
                                    if (!incidentPoint) return;
                                    directionsSeedTokenRef.current += 1;
                                    setDirectionsSeed({
                                      token: directionsSeedTokenRef.current,
                                      from: {
                                        label: 'Incident point',
                                        lat: incidentPoint.lat,
                                        lng: incidentPoint.lng,
                                      },
                                      to: { label: result.label, lat: result.lat, lng: result.lng },
                                    });
                                    onToolOpen?.('direction');
                                  }}
                                  className="flex flex-shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white px-2 text-slate-500 transition hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-700"
                                >
                                  <Navigation className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: activeTab === 'query' ? 'block' : 'none' }} className="space-y-3 px-3 pb-4">
          <ToolPanelHeader
            title="Query Features"
            description="Build a query against feature attributes."
            onClose={() => handleCloseTool('query')}
          />
          <label className="block space-y-1 text-[12px] font-medium text-slate-600">
            <span>Select Layer</span>
            <select
              value={queryLayer}
              onChange={(event) => {
                setQueryLayer(event.target.value);
                void fetchLayerFields('query', event.target.value);
              }}
              className="gl-input w-full px-2.5 py-1.5 text-[12.5px]"
            >
              <option value="">-- Select Layer --</option>
              {layerOptionsFor('query').map((layer) => (
                <option key={layer.id} value={layer.id}>{layer.name}</option>
              ))}
            </select>
          </label>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5">
            {queryClauses.map((clause, idx) => (
              <div key={clause.id} className="mb-2 flex flex-wrap items-center gap-1.5">
                <div className="min-w-[72px] text-xs font-semibold text-slate-600">{idx === 0 ? 'Where' : ''}</div>
                {idx !== 0 && (
                  <select
                    value={clause.conjunction}
                    onChange={(event) => {
                      const conjunction = event.target.value;
                      setQueryClauses((prev) =>
                        prev.map((item, i) => (i === idx ? { ...item, conjunction } : item)),
                      );
                    }}
                    className="gl-input w-full max-w-[72px] px-2 py-1.5 text-[12.5px]"
                  >
                    <option value="And">And</option>
                    <option value="Or">Or</option>
                  </select>
                )}
                <select
                  value={clause.field}
                  onChange={(event) => {
                    const field = event.target.value;
                    // A changed field makes the old value meaningless.
                    setQueryClauses((prev) =>
                      prev.map((item, i) => (i === idx ? { ...item, field, value: '' } : item)),
                    );
                    /* Nothing used to call this. `fetchFieldValues` existed,
                       `queryFieldValues` existed, and no line of JSX ever read
                       either — the suggestions feature was written and never
                       connected. */
                    if (field) void fetchFieldValues(queryLayer, field, clause.id);
                  }}
                  className="gl-input w-full px-2.5 py-1.5 text-[12.5px]"
                >
                  <option value="">
                    {queryFieldsLoading
                      ? 'Loading fields…'
                      : !queryLayer
                        ? 'Choose a layer first'
                        : queryLayerFields.length === 0
                          ? 'No fields available'
                          : 'Field'}
                  </option>
                  {queryLayerFields.map((field) => (
                    <option key={field} value={field}>{field}</option>
                  ))}
                </select>
                <select
                  value={clause.operator}
                  onChange={(event) => {
                    const operator = event.target.value;
                    setQueryClauses((prev) =>
                      prev.map((item, i) => (i === idx ? { ...item, operator } : item)),
                    );
                  }}
                  className="gl-input w-full px-2.5 py-1.5 text-[12.5px]"
                >
                  {[
                    'is equal to',
                    'is not equal to',
                    'is greater than',
                    'is less than',
                    'contains',
                    'starts with',
                    'is blank',
                    'is not blank',
                  ].map((operator) => (
                    <option key={operator} value={operator}>{operator}</option>
                  ))}
                </select>
                {/* A combo box, not a dropdown: the operators include
                    "contains" and "starts with", where the useful input is a
                    fragment that appears in no value on its own. The list
                    suggests; it never restricts. */}
                <div className="relative w-full">
                  <input
                    value={clause.value}
                    list={`query-values-${clause.id}`}
                    disabled={clause.operator === 'is blank' || clause.operator === 'is not blank'}
                    onChange={(event) => {
                      const value = event.target.value;
                      setQueryClauses((prev) =>
                        prev.map((item, i) => (i === idx ? { ...item, value } : item)),
                      );
                    }}
                    placeholder={
                      queryFieldValuesLoading[clause.id]
                        ? 'Loading values…'
                        : !clause.field
                          ? 'Pick a field first'
                          : (queryFieldValues[clause.id]?.length ?? 0) > 0
                            ? `Value — ${queryFieldValues[clause.id].length} found`
                            : 'Value'
                    }
                    className="gl-input w-full px-2.5 py-1.5 text-[12.5px] disabled:bg-slate-100 disabled:text-slate-400"
                  />
                  <datalist id={`query-values-${clause.id}`}>
                    {(queryFieldValues[clause.id] ?? []).map((value) => (
                      <option key={value} value={value} />
                    ))}
                  </datalist>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (queryClauses.length > 1) {
                      setQueryClauses((prev) => prev.filter((item) => item.id !== clause.id));
                    }
                  }}
                  disabled={queryClauses.length === 1}
                  className="gl-btn gl-btn--danger whitespace-nowrap px-2.5 py-1.5 text-[12px]"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setQueryClauses((prev) => [
                  ...prev,
                  { id: nextClauseId(), field: '', operator: 'is equal to', value: '', conjunction: 'And' },
                ])
              }
              className="text-[12px] font-semibold text-blue-600 transition hover:text-blue-800"
            >
              + Add Clause
            </button>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={invertWhere}
              onChange={(event) => setInvertWhere(event.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-blue-600"
            />
            <span className="text-[12px] text-slate-600">Invert Where Clause</span>
          </div>
          <div className="space-y-2.5">
            <button
              type="button"
              onClick={handleApplyQuery}
              disabled={!queryLayer}
              className="gl-btn gl-btn--primary whitespace-nowrap px-3 py-1.5 text-[12.5px]"
            >
              {queryLoading ? 'Running query…' : 'Apply'}
            </button>
            <button
              type="button"
              onClick={() => {
                setQueryLayer('');
                setQueryLayerFields([]);
                setQueryClauses([{ id: nextClauseId(), field: '', operator: 'is equal to', value: '', conjunction: 'And' }]);
                setInvertWhere(false);
                setQueryResults([]);
                setQueryTruncated(false);
                setSelectedRows([]);
                // The query OWNS this selection, so clearing the query has to
                // take the map highlight and the table's ticks with it —
                // otherwise the map keeps pointing at an answer that is gone.
                onSelectionChange?.([]);
              }}
              className="gl-btn gl-btn--danger whitespace-nowrap px-3 py-1.5 text-[12.5px]"
            >
              Clear
            </button>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[12px] font-semibold text-slate-600">
                Results ({queryResults.length})
              </span>
              {queryResults.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    const layer = getLayerById(queryLayer);
                    if (layer) onOpenAttributeTable?.(layer);
                  }}
                  className="text-[11px] font-semibold text-blue-600 transition hover:text-blue-800"
                >
                  Open in table
                </button>
              )}
            </div>
            {queryTruncated && (
              <p className="rounded bg-amber-50 px-2 py-1.5 text-[11px] leading-relaxed text-amber-800">
                This layer has more features than could be read at once — results{' '}
                {invertWhere ? '(including Invert)' : ''} only cover the first 10,000.
              </p>
            )}
            {queryResults.length === 0 ? (
              <p className="text-[12px] text-slate-400">No query results yet.</p>
            ) : (
              <div className="max-h-56 space-y-2 overflow-y-auto pr-0.5">
                {queryResults.slice(0, 50).map((feature, index) => (
                  /* Clickable, because a result you can read but not reach is
                     half an answer. Zooming also flashes the map highlight, so
                     the row and the shape point at each other. */
                  <button
                    key={index}
                    type="button"
                    onClick={() => onZoomToSelection?.([featureAsRow(feature, index)])}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-left text-[12.5px] text-slate-700 transition hover:border-cyan-300 hover:bg-cyan-50"
                  >
                    {Object.entries((feature.properties || {})).slice(0, 3).map(([key, value]) => (
                      <div key={key} className="flex justify-between gap-3 text-xs">
                        <span className="font-semibold text-slate-600">{key}</span>
                        <span className="truncate">{String(value ?? '')}</span>
                      </div>
                    ))}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: activeTab === 'select' ? 'block' : 'none' }} className="space-y-3 px-3 pb-4">
          <ToolPanelHeader
            title="Select Features"
            description="Drag a shape over the map to pick features out of a layer."
            onClose={() => handleCloseTool('select')}
          />
          {/* New / Add / Remove — the three selection verbs, so more than one
              shape can build up a selection instead of replacing it. */}
          <div className="space-y-1">
            <span className="block text-[12px] font-medium text-slate-600">When I draw a shape</span>
            <div className="grid grid-cols-3 gap-1 rounded-lg bg-slate-100 p-1">
              {([
                { id: 'new' as const, label: 'Replace' },
                { id: 'add' as const, label: 'Add' },
                { id: 'remove' as const, label: 'Remove' },
              ]).map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  onClick={() => setSelectCombine(entry.id)}
                  aria-pressed={selectCombine === entry.id}
                  className={`rounded-md px-2 py-1 text-[11.5px] font-medium transition ${
                    selectCombine === entry.id
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {entry.label}
                </button>
              ))}
            </div>
          </div>
          <label className="block space-y-1 text-[12px] font-medium text-slate-600">
            <span>Selection shape</span>
            <select
              value={selectMode}
              onChange={(event) =>
                setSelectMode(event.target.value as 'rectangle' | 'polygon' | 'circle')
              }
              className="gl-input w-full px-2.5 py-1.5 text-[12.5px]"
            >
              <option value="rectangle">Rectangle</option>
              <option value="polygon">Polygon</option>
              <option value="circle">Circle</option>
            </select>
          </label>
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[12px] font-medium text-slate-600">Layers to search</span>
              {layerOptionsFor('select').length > 1 && (
                <button
                  type="button"
                  onClick={() =>
                    setSelectLayerIds((current) =>
                      current.length > 0 ? [] : layerOptionsFor('select').map((entry) => entry.id),
                    )
                  }
                  className="text-[11px] font-semibold text-blue-600 transition hover:text-blue-800"
                >
                  {selectLayerIds.length > 0 ? 'Clear all' : 'Select all'}
                </button>
              )}
            </div>
            <div className="max-h-32 space-y-0.5 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-1.5">
              {layerOptionsFor('select').length === 0 && (
                <p className="px-1 py-1 text-[11px] text-slate-400">No layers on this map yet.</p>
              )}
              {layerOptionsFor('select').map((entry) => {
                const checked = selectLayerIds.includes(entry.id);
                return (
                  <label
                    key={entry.id}
                    className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-white"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        setSelectLayerIds((current) =>
                          checked
                            ? current.filter((id) => id !== entry.id)
                            : [...current, entry.id],
                        )
                      }
                      className="h-3.5 w-3.5 accent-blue-600"
                    />
                    <span className="min-w-0 flex-1 truncate text-[12px] text-slate-700">
                      {entry.name}
                    </span>
                    {/* What this layer contributed to the current selection,
                        read right where the layer is ticked. The card below
                        answers "what did I catch"; this answers "was ticking
                        THIS one worth it" — which is the question you are
                        holding while looking at the list. */}
                    {caughtPerLayer[entry.id] > 0 && (
                      <span className="flex-shrink-0 rounded-full bg-cyan-600 px-1.5 text-[9.5px] font-bold leading-[16px] tabular-nums text-white">
                        {caughtPerLayer[entry.id].toLocaleString()}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
          <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-[12px] leading-snug text-blue-700">
            {selectionActive ? (
              <>
                {selectMode === 'rectangle'
                  ? 'Click one corner, then the opposite corner.'
                  : selectMode === 'circle'
                    ? 'Click the centre, then click again to set the radius.'
                    : 'Click around the area, then finish with Enter or a double-click.'}
                <span className="mt-0.5 block text-[11px] text-blue-600/80">
                  Anything the shape touches is selected. Esc abandons it.
                </span>
              </>
            ) : (
              'Pick a layer and a shape, then start. What you catch is highlighted on the map.'
            )}
          </div>
          <ToolActions>
            <button
              type="button"
              onClick={() => {
                onStopDrawing?.();
                setSelectionActive((current) => !current);
              }}
              disabled={selectLayerIds.length === 0}
              className={`gl-btn whitespace-nowrap px-3 py-1.5 text-[12.5px] ${
                selectionActive ? 'gl-btn--danger' : 'gl-btn--primary'
              }`}
            >
              {selectionActive ? 'Stop' : 'Start selection'}
            </button>
            <button
              type="button"
              onClick={() => {
                setSelectedRows([]);
                setSelectionActive(false);
                // The map highlight and the table's ticks go with it — one
                // selection, cleared in one place.
                onSelectionChange?.([]);
              }}
              disabled={selectedRows.length === 0}
              className="gl-btn gl-btn--secondary whitespace-nowrap px-3 py-1.5 text-[12.5px]"
            >
              Clear
            </button>
          </ToolActions>
          <div className="space-y-1.5 rounded-lg border border-slate-200 bg-slate-50 p-2.5">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Selected features
              </p>
              <p className="text-[15px] font-semibold tabular-nums text-slate-700">
                {selectedRows.length.toLocaleString()}
              </p>
            </div>

            {selectedRows.length === 0 ? (
              <p className="text-[10.5px] leading-snug text-slate-400">Nothing caught yet.</p>
            ) : (
              /* Grouped BY LAYER. A box over a dense network catches hundreds
                 of features; listing them one per row is a wall nobody reads,
                 and the useful unit is "which layers did I hit, and how much
                 of each". Reading one feature is what the pop-up is for. */
              <div className="max-h-52 space-y-1 overflow-y-auto pr-0.5">
                {selectionByLayer.map((group) => (
                  /* Name, action, number — and the number is the ROW'S right
                     edge rather than a badge on the left. Counts down a column
                     compare at a glance; scattered in front of names of
                     different lengths they do not. Dropping the badge and the
                     Zoom button also gives the name the room it was being
                     truncated out of. */
                  <div
                    key={group.layerId}
                    className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5"
                  >
                    <span
                      className="min-w-0 flex-1 truncate text-[12.5px] font-medium text-slate-700"
                      title={group.layerName}
                    >
                      {group.layerName}
                    </span>
                    {Boolean(settings('select').showTable ?? true) && (
                      <button
                        type="button"
                        onClick={() => {
                          const layer = getLayerById(group.layerId);
                          if (layer) onOpenAttributeTable?.(layer);
                        }}
                        className="flex-shrink-0 rounded px-1.5 py-0.5 text-[10.5px] font-semibold text-blue-600 transition hover:bg-blue-50"
                      >
                        Attributes
                      </button>
                    )}
                    <span className="w-12 flex-shrink-0 text-right text-[13px] font-semibold tabular-nums text-slate-700">
                      {group.count.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: activeTab === 'bookmark' ? 'block' : 'none' }} className="space-y-3 px-3 pb-4">
          <ToolPanelHeader
            title="Bookmarks"
            description="Save and return to important map views."
            onClose={() => handleCloseTool('bookmark')}
          />
          {/* An app can publish a fixed set of views and let nobody add to it,
              which is what "Allow end users to add bookmarks" turns off. */}
          {Boolean(settings('bookmark').allowUserBookmarks ?? true) ? (
            <>
              <label className="block space-y-1 text-[12px] font-medium text-slate-600">
                <span>Bookmark name</span>
                <input
                  value={bookmarkName}
                  onChange={(event) => setBookmarkName(event.target.value)}
                  onKeyDown={(event) => event.key === 'Enter' && handleSaveBookmark()}
                  placeholder="Name this view"
                  className="gl-input w-full px-2.5 py-1.5 text-[12.5px]"
                />
              </label>
              <ToolActions>
                <button
                  type="button"
                  onClick={handleSaveBookmark}
                  disabled={bookmarkName.trim() === ''}
                  className="gl-btn gl-btn--primary whitespace-nowrap px-3 py-1.5 text-[12.5px]"
                >
                  Save this view
                </button>
              </ToolActions>
            </>
          ) : (
            <p className="rounded-lg bg-slate-50 px-2.5 py-2 text-[11.5px] leading-snug text-slate-500">
              This map publishes a fixed set of views. Pick one below.
            </p>
          )}
          <div className="space-y-3">
            {bookmarks.length === 0 ? (
              <p className="text-[12px] text-slate-400">No bookmarks saved yet.</p>
            ) : (
              bookmarks.map((bookmark) =>
                renamingBookmarkId === bookmark.id ? (
                  <div key={bookmark.id} className="rounded-lg border border-slate-200 bg-slate-50 p-2.5">
                    <div className="flex items-center gap-2">
                      <input
                        autoFocus
                        value={renameBookmarkValue}
                        onChange={(event) => setRenameBookmarkValue(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') handleConfirmRenameBookmark();
                          if (event.key === 'Escape') handleCancelRenameBookmark();
                        }}
                        className="gl-input w-full px-2.5 py-1.5 text-[12.5px]"
                      />
                      <button
                        type="button"
                        title="Save name"
                        onClick={handleConfirmRenameBookmark}
                        disabled={renameBookmarkValue.trim() === ''}
                        className="gl-btn gl-btn--secondary shrink-0 p-1.5"
                      >
                        <Check size={14} />
                      </button>
                      <button
                        type="button"
                        title="Cancel"
                        onClick={handleCancelRenameBookmark}
                        className="gl-btn gl-btn--secondary shrink-0 p-1.5"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div key={bookmark.id} className="rounded-lg border border-slate-200 bg-slate-50 p-2.5">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-medium text-slate-800">{bookmark.name}</p>
                        <p className="text-xs text-slate-500">{bookmark.date}</p>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          title="Rename"
                          onClick={() => handleStartRenameBookmark(bookmark.id, bookmark.name)}
                          className="gl-btn gl-btn--secondary p-1.5"
                        >
                          <PenLine size={14} />
                        </button>
                        <button
                          type="button"
                          title="Delete"
                          onClick={() => {
                            if (window.confirm(`Delete bookmark "${bookmark.name}"?`)) handleDeleteBookmark(bookmark.id);
                          }}
                          className="gl-btn gl-btn--secondary p-1.5 text-red-600 hover:text-red-700"
                        >
                          <Trash2 size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => onGoToLocation(bookmark.center, bookmark.zoom)}
                          className="gl-btn gl-btn--secondary whitespace-nowrap px-3 py-1.5 text-[12.5px]"
                        >
                          Go
                        </button>
                      </div>
                    </div>
                  </div>
                ),
              )
            )}
          </div>
        </div>

        {/* Everything else in the registry. Rendered from the dispatcher rather
            than inline, so adding the next tool is a file plus one line — not
            another 60-line block wedged into this component. Mounted only while
            selected, because these panels fetch: keeping twelve of them alive
            behind `display: none` would query on every map move. */}
        {visibleTools.map((toolId) => {
          if (activeTab !== toolId) return null;
          const panel = renderToolPanel(toolId, toolContextFor(toolId));
          if (!panel) return null;
          return (
            <div key={toolId} className="space-y-3 px-3 pb-4">
              {panel}
            </div>
          );
        })}
      </div>

      {/* Layer information — portalled to <body>: `fixed` inside the glass TOC
          would otherwise be clipped into the TOC's box. */}
      {infoLayer && createPortal(
        <div
          className="gl-modal-backdrop fixed inset-0 z-[7000] flex items-center justify-center px-4"
          onClick={() => setInfoLayer(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Layer information for ${getLayerDisplayName(infoLayer)}`}
            className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-600">
                  Layer information
                </p>
                <h3 className="mt-0.5 truncate text-base font-semibold text-slate-900">
                  {getLayerDisplayName(infoLayer)}
                </h3>
                {module && (
                  <p className="mt-0.5 text-[11px] text-slate-400">
                    {getLayerCapabilities(infoLayer, module).sourceLabel}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setInfoLayer(null)}
                aria-label="Close layer information"
                className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
              {infoRows === null ? (
                <p className="py-6 text-center text-sm text-slate-400">Loading details…</p>
              ) : (
                <dl className="space-y-2">
                  {infoRows.map((row) => (
                    <div key={row.label} className="grid grid-cols-[130px_1fr] gap-3 rounded-lg bg-slate-50 px-3 py-2">
                      <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        {row.label}
                      </dt>
                      <dd className="break-words text-xs text-slate-700">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              )}

              {infoEdits !== undefined && (
                <div className="mt-4 border-t border-slate-100 pt-3">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Recent edits
                  </p>
                  {infoEdits === null ? (
                    <p className="text-xs text-slate-400">Loading…</p>
                  ) : infoEdits.length === 0 ? (
                    <p className="text-xs text-slate-400">
                      No edits yet. This layer is shared, so every change here is recorded — who,
                      what, and when.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {infoEdits.map((edit) => (
                        <li key={edit.id} className="rounded-lg bg-slate-50 px-3 py-2 text-xs">
                          <p className="font-medium text-slate-700">
                            {edit.userName ?? 'A former user'}{' '}
                            <span className="font-normal text-slate-400">
                              — {new Date(edit.editedAt).toLocaleString()}
                            </span>
                          </p>
                          <p className="mt-1 text-slate-500">
                            {'__created__' in edit.changes
                              ? 'Created this feature.'
                              : '__deleted__' in edit.changes
                                ? 'Deleted this feature.'
                                : Object.entries(edit.changes)
                                  .map(
                                    ([field, change]) =>
                                      `${field}: ${String(change.old ?? '(blank)')} → ${String(change.new ?? '(blank)')}`,
                                  )
                                  .join('; ')}
                          </p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}

      {sharingLayerId && module && (() => {
        const shareLayer = layers.find((entry) => entry.id === sharingLayerId);
        if (!shareLayer) return null;
        return createPortal(
          <LayerShareDialog
            moduleId={module.id}
            layerId={sharingLayerId}
            layerName={getLayerDisplayName(shareLayer)}
            onClose={() => setSharingLayerId(null)}
          />,
          document.body,
        );
      })()}

      {/* Transient success / error feedback for layer operations */}
      {layerNotice && createPortal(
        <div
          role="status"
          className={`fixed bottom-6 left-1/2 z-[7100] -translate-x-1/2 rounded-xl px-4 py-2.5 text-sm font-medium shadow-2xl ${
            layerNotice.kind === 'ok' ? 'bg-slate-900 text-white' : 'bg-red-600 text-white'
          }`}
          onAnimationEnd={() => setLayerNotice(null)}
        >
          {layerNotice.text}
          <button
            type="button"
            onClick={() => setLayerNotice(null)}
            aria-label="Dismiss"
            className="ml-3 text-white/70 hover:text-white"
          >
            ✕
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
}

const SEED_MODULES: AppModule[] = [
  {
    id: 'mod_seed_1',
    name: 'Map View',
    title: 'Haryana City Gas Network',
    built: true,
    layers: freshLayers(),
    tools: ['draw', 'measure', 'query'],
    geoServerUrl: '',
    geoServerLayers: [],
    homeCenter: [28.7041, 77.1025],
    homeZoom: 12,
    ownerId: ADMIN_OWNER_ID,
    visibility: 'admin-workspace',
  },
  {
    id: 'mod_seed_2',
    name: 'Dashboard',
    title: 'Gas Distribution KPIs',
    built: true,
    layers: freshLayers(),
    tools: ['filter'],
    geoServerUrl: '',
    geoServerLayers: [],
    homeCenter: [28.7041, 77.1025],
    homeZoom: 12,
    ownerId: ADMIN_OWNER_ID,
    visibility: 'admin-workspace',
  },
  {
    id: 'mod_seed_3',
    name: 'User Management',
    title: 'Portal Users',
    built: false,
    layers: freshLayers(),
    tools: [],
    geoServerUrl: '',
    geoServerLayers: [],
    homeCenter: [28.7041, 77.1025],
    homeZoom: 12,
    ownerId: ADMIN_OWNER_ID,
    visibility: 'admin-workspace',
  },
];

function StatusPill({ built }: { built: boolean }) {
  return (
    <span
      className={`text-xs font-medium px-2.5 py-1 rounded-full ${
        built ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
      }`}
    >
      {built ? 'Live' : 'Draft'}
    </span>
  );
}

function Landing({
  modules,
  homeConfig,
  onOpen,
  onEdit,
  onDelete,
  onShare,
  onAdd,
  onOpenSettings,
  onOpenProfile,
  onHomeConfigSaved,
}: {
  modules: AppModule[];
  homeConfig: HomePageConfig;
  onOpen: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onShare: (id: string) => void;
  onAdd: (name: string, kind: AppKind) => void;
  /** Opens the admin Settings console, optionally jumping to one section. */
  onOpenSettings: (section?: string) => void;
  onOpenProfile: () => void;
  onHomeConfigSaved: (config: HomePageConfig) => void;
}) {
  const { currentUser, logout } = useAuth();
  const { can } = usePermissions();
  const canEditHome = can('home:edit');
  const canShareModules = can('modules:share');
  const accent = homeConfig.accentColor || DEFAULT_HOME_CONFIG.accentColor;
  const overlay = homeConfig.backgroundOverlay ?? DEFAULT_HOME_CONFIG.backgroundOverlay ?? 0.8;
  // Optional admin overrides; unset keeps the original look.
  const eyebrowColor = homeConfig.eyebrowColor || accent;
  const titleColor = homeConfig.titleColor || DEFAULT_TITLE_COLOR;
  const subtitleColor = homeConfig.subtitleColor || DEFAULT_SUBTITLE_COLOR;
  const headerColor = homeConfig.headerColor || DEFAULT_HEADER_COLOR;

  const [showModal, setShowModal] = useState(false);
  const [name, setName] = useState('');
  // Add App is a two-stage flow: pick the application type, then name it.
  // `null` means the type has not been chosen yet.
  const [newAppKind, setNewAppKind] = useState<AppKind | null>(null);
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);
  /** Application search text. Filters ONLY the already permission-scoped
   *  `modules` prop, so an unauthorised app can never surface. */
  const [appQuery, setAppQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Inline title editing is a shortcut into the same HomePageConfig the Settings
  // editor writes, so there is a single source of truth. Admin-only.
  const portalTitle = homeConfig.title;
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(portalTitle);

  const startTitleEdit = () => {
    if (!canEditHome) return;
    setTitleDraft(portalTitle);
    setEditingTitle(true);
    setTimeout(() => titleInputRef.current?.select(), 50);
  };

  /* Saying so when it does not save.
     This was a localStorage write that essentially always succeeded, so a
     rejection was never handled. It is a network call now — an expired
     session, a dropped connection, or a role that is no longer admin all end
     up here — and without this the editor would simply stay open forever with
     no explanation and the old title still on screen. */
  const [titleError, setTitleError] = useState<string | null>(null);

  const saveTitle = async () => {
    const nextTitle = titleDraft.trim() || DEFAULT_HOME_CONFIG.title;
    setTitleError(null);
    try {
      const saved = await settingsService.updateHomeConfig({ ...homeConfig, title: nextTitle });
      onHomeConfigSaved(saved);
      setEditingTitle(false);
    } catch (error) {
      // Left in edit mode on purpose: the text they typed is still there to
      // retry with, rather than discarded along with the failure.
      setTitleError(error instanceof Error ? error.message : 'The title could not be saved.');
    }
  };

  const cancelTitleEdit = () => {
    setTitleDraft(portalTitle);
    setTitleError(null);
    setEditingTitle(false);
  };

  const orderedModules = useMemo(() => {
    // "Add to Home Page" (the App Gallery, in Settings) is what this checks —
    // absent means true, so every module saved before that toggle existed
    // keeps showing up exactly where it always did.
    const onHome = modules.filter((module) => module.showOnHome !== false);
    // A module could in principle appear twice in `modules` (e.g. shared to
    // the current user AND owned by them, depending on how the list was
    // assembled upstream) — de-duplicated by id, first occurrence wins,
    // before anything else touches the list.
    const deduped = onHome.filter(
      (module, index) => onHome.findIndex((item) => item.id === module.id) === index,
    );
    // Home page order mirrors the App Gallery's own order exactly — see
    // AppGalleryTab's Move up/down and utils/moduleOrder.ts.
    return sortByHomeOrder(deduped);
  }, [modules]);

  // Focus the name field only once a type has been chosen — the first stage of
  // the dialog has no text input.
  useEffect(() => {
    if (showModal && newAppKind) setTimeout(() => inputRef.current?.focus(), 50);
  }, [showModal, newAppKind]);

  useEffect(() => {
    const container = containerRef.current;
    const activeCard = container?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    if (activeCard) {
      activeCard.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [activeIndex, orderedModules.length]);

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed || !newAppKind) return;
    onAdd(trimmed, newAppKind);
    closeAddDialog();
  };

  const closeAddDialog = () => {
    setName('');
    setNewAppKind(null);
    setShowModal(false);
  };

  const handlePrev = () => setActiveIndex((current) => Math.max(current - 1, 0));
  const handleNext = () =>
    setActiveIndex((current) => Math.min(current + 1, Math.max(orderedModules.length - 1, 0)));

  // `modules` is already scoped to this user by moduleService.listModulesForUser;
  // search narrows that list further and never widens it.
  const cardItems = useMemo(
    () => applicationSearchService.filter(orderedModules, appQuery),
    [orderedModules, appQuery],
  );

  // A shrinking result set can leave the carousel pointing past the end.
  useEffect(() => {
    setActiveIndex((current) => Math.min(current, Math.max(cardItems.length - 1, 0)));
  }, [cardItems.length]);

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        maxHeight: '100vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        background:
          'radial-gradient(ellipse at 12% 0%, rgba(29,110,190,0.28), transparent 55%), radial-gradient(ellipse at 88% 10%, rgba(13,148,136,0.18), transparent 50%), linear-gradient(180deg, #071A33 0%, #0C2947 50%, #103A63 100%)',
        backgroundAttachment: 'fixed',
        // This box scrolls (not the document), so ITS scrollbar is what a
        // visitor sees — a bare OS scrollbar is light grey and sits right
        // beside the navy header, reading as an uncovered strip rather than
        // a control. Coloured to match the header glass instead of left
        // default, on every engine that supports styling it.
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(148,197,255,0.35) rgba(7,26,51,0.6)',
        '&::-webkit-scrollbar': { width: 10 },
        '&::-webkit-scrollbar-track': { background: 'rgba(7,26,51,0.6)' },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(148,197,255,0.35)',
          borderRadius: 6,
        },
        '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(148,197,255,0.55)' },
      }}
    >
      {/* Contours, over the gradient and under everything else.
          Shown only when no background image is set — an uploaded photo is a
          deliberate choice and must not have a pattern drawn across it. The
          accent colour follows the home page's own, so the ornament belongs to
          whatever this deployment is branded as. */}
      {!homeConfig.backgroundImageUrl && (
        <Box
          aria-hidden="true"
          sx={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}
        >
          <ContourBackground
            colour="#38BDF8"
            accent={homeConfig.accentColor || '#0D9488'}
          />
        </Box>
      )}

      {/* Optional background image, drawn under everything with a dimming scrim
          so the white heading stays readable over light photos. */}
      {homeConfig.backgroundImageUrl && (
        <Box
          aria-hidden="true"
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
            backgroundImage: `linear-gradient(rgba(7,26,51,${overlay}), rgba(12,41,71,${overlay})), url("${homeConfig.backgroundImageUrl}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
      )}

      <Box
        component="svg"
        viewBox="0 0 1200 800"
        preserveAspectRatio="none"
        sx={{
          position: 'fixed',
          inset: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
          opacity: 1,
          pointerEvents: 'none',
        }}
      >
        {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((ring) => (
          <path
            key={`contour-${ring}`}
            d={`M-40 ${232 + ring * 38} C 180 ${168 + ring * 38}, 320 ${322 + ring * 38}, 520 ${256 + ring * 38} S 900 ${148 + ring * 38}, 1240 ${226 + ring * 38}`}
            stroke={ring % 4 === 0 ? 'rgba(148,197,255,0.20)' : 'rgba(148,197,255,0.10)'}
            strokeWidth={ring % 4 === 0 ? 1.1 : 0.7}
            fill="none"
          />
        ))}
        {[0, 1, 2, 3, 4, 5, 6].map((ring) => (
          <path
            key={`contour-low-${ring}`}
            d={`M-40 ${596 + ring * 34} C 220 ${552 + ring * 34}, 420 ${658 + ring * 34}, 660 ${600 + ring * 34} S 980 ${516 + ring * 34}, 1240 ${578 + ring * 34}`}
            stroke={ring % 3 === 0 ? 'rgba(94,234,212,0.16)' : 'rgba(94,234,212,0.08)'}
            strokeWidth={ring % 3 === 0 ? 1 : 0.7}
            fill="none"
          />
        ))}
        {[180, 470, 760, 1040].map((cx) => (
          <circle key={cx} cx={cx} cy={(cx / 8) * 1.1 + 120} r="2.5" fill="rgba(45,212,191,0.45)" />
        ))}
      </Box>

      {/* ── HEADER ────────────────────────────────────────────────────────
          One real header row rather than three separately-positioned corner
          boxes: organisation logo (left), fixed Blue/View mark (centre), and
          the signed-in user + settings (right). Sticky so it stays put while
          the page scrolls. */}
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 6,
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          gap: 2,
          px: { xs: 2, md: 3 },
          py: { xs: 1.25, md: 1.5 },
          borderBottom: '1px solid rgba(148,197,255,0.16)',
          bgcolor: headerColor,
          backdropFilter: 'blur(10px)',
        }}
      >
        {/* Left: organisation logo. Only an admin (home:edit) sees the
            add/change control. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
            '&:hover .portal-logo-edit': { opacity: 1 },
          }}
        >
        {homeConfig.logoUrl ? (
          <Box
            component="img"
            src={homeConfig.logoUrl}
            alt="Organisation logo"
            sx={{
              maxHeight: { xs: 34, md: 44 },
              maxWidth: { xs: 130, md: 190 },
              objectFit: 'contain',
              display: 'block',
            }}
            // A broken/removed URL should not leave a broken-image icon.
            onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
              event.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          canEditHome && (
            <Button
              onClick={() => onOpenSettings('home')}
              startIcon={<Plus className="w-3.5 h-3.5" />}
              sx={{
                textTransform: 'none',
                fontSize: '0.72rem',
                fontWeight: 600,
                color: 'rgba(203,225,250,0.75)',
                border: '1px dashed rgba(148,197,255,0.35)',
                borderRadius: '10px',
                px: 1.5,
                py: 0.75,
                '&:hover': {
                  color: '#F8FAFC',
                  borderColor: 'rgba(148,197,255,0.7)',
                  bgcolor: 'rgba(255,255,255,0.06)',
                },
              }}
            >
              Add logo
            </Button>
          )
        )}

        {homeConfig.logoUrl && canEditHome && (
          <IconButton
            className="portal-logo-edit"
            onClick={() => onOpenSettings('home')}
            aria-label="Change logo"
            title="Change logo"
            size="small"
            sx={{
              opacity: 0,
              transition: 'opacity 0.2s ease',
              color: 'rgba(203,225,250,0.75)',
              bgcolor: 'rgba(255,255,255,0.08)',
              '&:hover': { bgcolor: 'rgba(45,212,191,0.22)', color: '#5EEAD4' },
              '&:focus-visible': { opacity: 1 },
            }}
          >
            <PenLine className="w-3.5 h-3.5" />
          </IconButton>
        )}
        </Box>

        {/* Centre: FIXED Blue/View brand mark. Still hardcoded and never
            editable via Settings by any role — only its rendering changed from
            two text boxes to the BlueViewMark lockup. */}
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <BlueViewMark height={44} />
        </Box>

        {/* Right: signed-in user — photo/initials + name, opening Profile /
            Settings / Log out. Every signed-in user now has a (narrower)
            Settings console — see SettingsPage's `everyone`-gated sections —
            so this is no longer conditioned on `isAdmin`. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end', minWidth: 0 }}>
          {currentUser && (
            <>
              <NotificationBell />
              <UserMenu
                user={currentUser}
                showSettings
                onOpenProfile={onOpenProfile}
                onOpenSettings={() => onOpenSettings()}
                onLogout={logout}
              />
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ position: 'relative', zIndex: 2, px: { xs: 4, md: 10 }, pt: { xs: 3, md: 4 }, pb: { xs: 2, md: 3 } }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25 }}>
            {/* Brand mark now lives in the header above. */}
            <Typography
              variant="overline"
              // Admin-configurable; falls back to the accent colour when unset.
              sx={{ color: eyebrowColor, letterSpacing: 2.5, fontWeight: 700, fontSize: '0.7rem' }}
            >
              {homeConfig.eyebrow}
            </Typography>
            {editingTitle ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  component="input"
                  ref={titleInputRef}
                  value={titleDraft}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTitleDraft(event.target.value)}
                  onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (event.key === 'Enter') void saveTitle();
                    if (event.key === 'Escape') cancelTitleEdit();
                  }}
                  autoFocus
                  maxLength={48}
                  aria-label="Portal title"
                  sx={{
                    color: titleColor,
                    fontWeight: 600,
                    letterSpacing: '-0.02em',
                    textAlign: 'center',
                    lineHeight: 1.05,
                    fontSize: { xs: '2rem', md: '2.75rem' },
                    fontFamily: 'inherit',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(45,212,191,0.55)',
                    borderRadius: '10px',
                    outline: 'none',
                    px: 2,
                    py: 0.5,
                    maxWidth: 620,
                    width: `${Math.max(titleDraft.length + 1, 10)}ch`,
                  }}
                />
                <IconButton
                  onClick={() => void saveTitle()}
                  aria-label="Save title"
                  sx={{
                    color: '#0B1F38',
                    bgcolor: '#2DD4BF',
                    '&:hover': { bgcolor: '#5EEAD4' },
                  }}
                >
                  <Check className="w-4 h-4" />
                </IconButton>
                <IconButton
                  onClick={cancelTitleEdit}
                  aria-label="Cancel editing title"
                  sx={{
                    color: 'rgba(203,225,250,0.8)',
                    bgcolor: 'rgba(255,255,255,0.08)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' },
                  }}
                >
                  <X className="w-4 h-4" />
                </IconButton>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover .portal-title-edit': { opacity: 1 },
                }}
              >
                <Typography
                  variant="h2"
                  onDoubleClick={startTitleEdit}
                  sx={{
                    // Admin-configurable; unset keeps the default near-white.
                    color: titleColor,
                    fontWeight: 600,
                    letterSpacing: '-0.02em',
                    textAlign: 'center',
                    lineHeight: 1.05,
                    maxWidth: 820,
                    fontSize: { xs: '2rem', md: '2.75rem' },
                    cursor: 'default',
                  }}
                >
                  {portalTitle}
                </Typography>
                {/* Inline title editing is an admin shortcut into HomePageConfig. */}
                {canEditHome && (
                  <IconButton
                    className="portal-title-edit"
                    onClick={startTitleEdit}
                    aria-label="Edit portal title"
                    title="Edit portal title"
                    size="small"
                    sx={{
                      opacity: 0,
                      transition: 'opacity 0.2s ease',
                      color: 'rgba(203,225,250,0.75)',
                      bgcolor: 'rgba(255,255,255,0.08)',
                      '&:hover': { bgcolor: 'rgba(45,212,191,0.22)', color: '#5EEAD4' },
                      '&:focus-visible': { opacity: 1 },
                    }}
                  >
                    <PenLine className="w-4 h-4" />
                  </IconButton>
                )}
              </Box>
            )}
            {titleError && (
              <Typography
                role="alert"
                sx={{
                  color: '#FCA5A5',
                  textAlign: 'center',
                  maxWidth: 620,
                  fontSize: '0.8rem',
                  mt: 0.5,
                }}
              >
                {titleError}
              </Typography>
            )}
            <Typography
              sx={{
                color: subtitleColor,
                textAlign: 'center',
                maxWidth: 620,
                fontWeight: 400,
                fontSize: '0.95rem',
                lineHeight: 1.5,
              }}
            >
              {homeConfig.subtitle}
            </Typography>
          </Box>
        </motion.div>
      </Box>

      <Box sx={{ position: 'relative', zIndex: 2, px: { xs: 4, md: 10 }, pb: { xs: 2, md: 3 } }}>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15, ease: 'easeOut' }}
        >
          <Box sx={{ position: 'relative', pt: 0, pb: 0, overflow: 'visible' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
                mb: 1.5,
                px: { xs: 0, md: 2 },
              }}
            >
              <Typography
                variant="overline"
                sx={{ color: 'rgba(148,197,255,0.70)', fontWeight: 600, letterSpacing: 1.8 }}
              >
                {homeConfig.modulesSectionLabel}
              </Typography>

              {/* Application search — shown for EVERY role, not just admins. */}
              <Box sx={{ flex: '1 1 240px', maxWidth: 380, minWidth: 200 }}>
                <ApplicationSearch
                  value={appQuery}
                  onChange={setAppQuery}
                  resultCount={cardItems.length}
                  totalCount={orderedModules.length}
                />
              </Box>

              {/* Both roles may create modules ('modules:create'); a user's module
                  is owned by them and stays private unless an Admin assigns it. */}
              {can('modules:create') && (
                <Button
                  onClick={() => setShowModal(true)}
                  startIcon={<Plus className="w-4 h-4" />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    color: '#0B1F38',
                    bgcolor: accent,
                    borderRadius: '10px',
                    px: 2.25,
                    py: 1,
                    boxShadow: '0 10px 24px -10px rgba(45,212,191,0.8)',
                    '&:hover': { bgcolor: accent, filter: 'brightness(1.12)' },
                  }}
                >
                  {homeConfig.addModuleButtonLabel}
                </Button>
              )}
            </Box>

            {/* Arrows are FLEX SIBLINGS of the scroll track, not absolutely
                positioned over it. Padding on a scroll container only reserves
                space at the ends of its CONTENT — mid-scroll a card slides
                straight under an overlaid button, which is why the arrow was
                appearing on top of a module. As siblings they occupy their own
                column and can never overlap a card. */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1, md: 1.5 },
                px: { xs: 1, md: 2 },
                overflow: 'visible',
              }}
            >
            <IconButton
              onClick={handlePrev}
              aria-label="Previous applications"
              sx={{
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                  bgcolor: '#FFFFFF',
                  color: '#0F2942',
                  border: '1px solid rgba(15,41,66,0.10)',
                  boxShadow: '0 4px 12px -2px rgba(15,41,66,0.12)',
                  transition: 'background-color 160ms ease, color 160ms ease, transform 160ms ease',
                  '&:hover': {
                    bgcolor: '#0F2942',
                    color: '#FFFFFF',
                    borderColor: '#0F2942',
                    transform: 'scale(1.06)',
                  },
                }}
            >
              <ChevronLeft className="w-5 h-5" />
            </IconButton>

            <Box sx={{ flex: 1, minWidth: 0, overflow: 'visible' }}>
              <Box
                ref={containerRef}
                sx={{
                  display: 'flex',
                  gap: { xs: 2, sm: 3, md: 4 },
                  alignItems: 'stretch',
                  overflowX: 'auto',
                  overflowY: 'visible',
                  scrollSnapType: 'x mandatory',
                  scrollBehavior: 'smooth',
                  // Small inset only; the arrows have their own columns now.
                  // `py` gives the hover lift somewhere to go, because a
                  // horizontal scroller clips vertically too.
                  px: { xs: 1, md: 1.5 },
                  py: 3,
                  '&::-webkit-scrollbar': { display: 'none' },
                  msOverflowStyle: 'none',
                  scrollbarWidth: 'none',
                }}
              >
                {cardItems.length === 0 && (
                  <Box
                    sx={{
                      width: '100%',
                      py: 6,
                      textAlign: 'center',
                      border: '1px dashed rgba(148,197,255,0.28)',
                      borderRadius: '16px',
                    }}
                  >
                    <Typography sx={{ color: 'rgba(203,225,250,0.75)', fontSize: '0.9rem', fontWeight: 500 }}>
                      {appQuery.trim() ? 'No applications found' : 'No modules yet'}
                    </Typography>
                    <Typography sx={{ color: 'rgba(148,197,255,0.55)', fontSize: '0.78rem', mt: 0.5 }}>
                      {appQuery.trim()
                        ? `Nothing matches “${appQuery.trim()}”.`
                        : can('modules:create')
                          ? `Use “${homeConfig.addModuleButtonLabel}” to create your first module.`
                          : 'An administrator has not assigned any modules to you yet.'}
                    </Typography>
                  </Box>
                )}
                {cardItems.map((item, index) => {
                  const active = index === activeIndex;
                  const hasHover = hoveredIndex !== null;
                  const isCentered = index === hoveredIndex || active;
                  /* Gentler than before: a 6% pop with neighbours shoved 16px
                     aside reads as a lurch. A smaller lift plus a smaller
                     nudge keeps the focus effect while the row stays calm. */
                  const scale = hoveredIndex === index ? 1.04 : active ? 1.015 : 1;
                  const translateX = hasHover
                    ? index === hoveredIndex
                      ? 0
                      : index < hoveredIndex
                        ? -8
                        : 8
                    : active
                      ? 0
                      : index < activeIndex
                        ? -4
                        : 4;

                  const module = item;
                  // Dashboards get their own icon so the two application types
                  // are distinguishable at a glance on the landing page.
                  const moduleIsDashboard = isDashboard(module);
                  const Icon = moduleIsDashboard ? LayoutGrid : pickIcon(module.title);
                  // Per-module permissions: owner vs shared-with vs admin.
                  const access = getModuleAccess(currentUser, module, canShareModules);
                  // The two card looks this component supports — see the
                  // thumbnail/no-thumbnail branches just below for why text
                  // and chrome colours flip between them rather than reading
                  // from the page's own dark-glass tokens.
                  const hasThumb = Boolean(module.thumbnailUrl);

                  return (
                    <motion.div
                      data-index={index}
                      key={module.id}
                      animate={{ scale, x: translateX, y: isCentered ? -6 : 0 }}
                      /* A spring settles instead of stopping dead at the end of
                         a fixed duration, which is what made the previous
                         `easeInOut` feel mechanical. Damped just short of
                         critical so it lands without visible bounce. */
                      transition={{ type: 'spring', stiffness: 300, damping: 30, mass: 0.7 }}
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      /* `--animated` hands `transform` to framer-motion; see
                         glass.css for why sharing it with a CSS transition
                         stutters.
                         `gl-surface--strong` is deliberately NOT here: with a
                         thumbnail the photo itself is the background (a tint
                         over it would dull the image the admin chose), and
                         without one the light contour gradient below replaces
                         it — both are set inline instead. */
                      className="gl-card gl-card--interactive gl-card--animated min-w-[240px] max-w-[300px] flex-shrink-0 cursor-pointer overflow-hidden relative"
                      style={{
                        padding: '22px 22px',
                        scrollSnapAlign: 'center',
                        transformOrigin: 'center bottom',
                        background: hasThumb
                          ? '#0F2942'
                          : 'linear-gradient(135deg, #E9F6F6 0%, #E4EEFB 55%, #EFE9FB 100%)',
                      }}
                      onClick={() => {
                        setActiveIndex(index);
                        onOpen(module.id);
                      }}
                    >
                      {hasThumb ? (
                        <>
                          {/* The admin's own photo (set from the App Gallery),
                              shown across the card rather than a small corner
                              icon — with a bottom scrim so white text stays
                              readable over whatever the photo looks like.
                              `scale-down` on purpose, not `cover`: a thumbnail
                              smaller than the card must never be stretched
                              past its own resolution to fill it — that's what
                              was turning ordinary screenshots to mush. This
                              shrinks an oversized photo to fit and otherwise
                              shows it at its real, crisp size, letterboxed on
                              the card's own navy background rather than
                              blown up. */}
                          <img
                            src={module.thumbnailUrl}
                            alt=""
                            className="absolute inset-0 h-full w-full object-scale-down object-center"
                            style={{ zIndex: 0 }}
                            onError={(event) => {
                              event.currentTarget.style.display = 'none';
                            }}
                          />
                          <Box
                            aria-hidden="true"
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              zIndex: 1,
                              background:
                                'linear-gradient(180deg, rgba(15,23,42,0) 0%, rgba(15,23,42,0.18) 45%, rgba(15,23,42,0.86) 100%)',
                            }}
                          />
                        </>
                      ) : (
                        // No photo yet: the same quiet topographic ornament the
                        // home page itself uses, at card scale, over a light
                        // gradient — the "before a thumbnail is added" look.
                        <Box aria-hidden="true" sx={{ position: 'absolute', inset: 0, zIndex: 0 }}>
                          <ContourBackground colour="#38BDF8" accent="#0D9488" scale={2.2} />
                        </Box>
                      )}

                      <Box sx={{ position: 'relative', zIndex: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '12px',
                            overflow: 'hidden',
                            background: hasThumb
                              ? 'rgba(255,255,255,0.18)'
                              : 'linear-gradient(140deg, #0F2942 0%, #16556E 60%, #0D9488 100%)',
                            backdropFilter: hasThumb ? 'blur(6px)' : undefined,
                            border: hasThumb ? '1px solid rgba(255,255,255,0.3)' : undefined,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 6px 16px -4px rgba(15,41,66,0.35)',
                          }}
                        >
                          {/* Always the map/dashboard icon now — a thumbnail,
                              once set, is the card's own background above, not
                              a stand-in for this badge. */}
                          <Icon className="w-6 h-6 text-white" />
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            setActionMenuId((current) => (current === module.id ? null : module.id));
                          }}
                          sx={{
                            color: hasThumb ? 'rgba(255,255,255,0.85)' : '#64748B',
                            '&:hover': {
                              backgroundColor: hasThumb ? 'rgba(255,255,255,0.15)' : 'rgba(15,23,42,0.06)',
                              color: hasThumb ? '#FFFFFF' : '#0F2942',
                            },
                          }}
                        >
                          <MoreVertical className="w-4 h-4" />
                        </IconButton>
                      </Box>
                      <Typography
                        variant="overline"
                        sx={{
                          display: 'block',
                          // The dashboard teal reads on both grounds; the map
                          // app's neutral flips with the card's own look.
                          color: moduleIsDashboard ? '#2DD4BF' : hasThumb ? 'rgba(255,255,255,0.85)' : '#64748B',
                          fontSize: '0.62rem',
                          fontWeight: 700,
                          letterSpacing: 1.1,
                          lineHeight: 1.6,
                        }}
                      >
                        {moduleIsDashboard ? 'Dashboard' : 'Map App'}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: hasThumb ? '#FFFFFF' : '#0F2942',
                          fontWeight: 600,
                          fontSize: '1.05rem',
                          letterSpacing: '-0.01em',
                          mb: 2,
                        }}
                      >
                        {module.title}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Tooltip
                          content={
                            module.built
                              ? 'Published. Opening this card launches it for end users.'
                              : 'Not published yet. Opening this card returns to the builder.'
                          }
                        >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.25,
                            py: 0.5,
                            borderRadius: '6px',
                            background: module.built ? 'rgba(13,148,136,0.08)' : 'rgba(122,140,160,0.10)',
                            border: `1px solid ${module.built ? 'rgba(13,148,136,0.22)' : 'rgba(122,140,160,0.22)'}`,
                          }}
                        >
                          <Box
                            sx={{
                              width: 5,
                              height: 5,
                              borderRadius: '50%',
                              bgcolor: module.built ? '#0D9488' : '#94A3B8',
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: module.built ? '#2DD4BF' : 'var(--glass-muted)',
                              fontWeight: 600,
                              fontSize: '0.68rem',
                              letterSpacing: 0.4,
                            }}
                          >
                            {module.built ? 'Live' : 'Draft'}
                          </Typography>
                        </Box>
                        </Tooltip>
                        {/* Shared modules are clearly identified; the receiving
                            user gets view/use only — no edit, rename, or delete. */}
                        {access.isShared && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              px: 1.25,
                              py: 0.5,
                              borderRadius: '6px',
                              background: 'rgba(37,99,235,0.08)',
                              border: '1px solid rgba(37,99,235,0.24)',
                            }}
                            title="Shared with you — view only"
                          >
                            <Share2 className="w-3 h-3 text-blue-700" />
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'var(--bv-shared-text)',
                                fontWeight: 600,
                                fontSize: '0.68rem',
                                letterSpacing: 0.4,
                              }}
                            >
                              Shared
                            </Typography>
                          </Box>
                        )}
                        <Typography
                          variant="caption"
                          sx={{ color: hasThumb ? 'rgba(255,255,255,0.85)' : '#64748B', fontSize: '0.72rem' }}
                        >
                          {access.isShared ? 'View only' : module.built ? 'Open' : 'Configure'}
                        </Typography>
                      </Box>
                      </Box>
                      {actionMenuId === module.id && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 80,
                            right: 24,
                            width: 180,
                            bgcolor: 'rgba(15,23,42,0.96)',
                            border: '1px solid rgba(255,255,255,0.12)',
                            borderRadius: 3,
                            boxShadow: '0 30px 80px rgba(4,9,27,0.38)',
                            overflow: 'hidden',
                            zIndex: 30,
                          }}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {/* Edit/Delete are hidden entirely for shared modules.
                              The service layer re-validates regardless. */}
                          {access.canEdit && (
                            <Button
                              fullWidth
                              onClick={() => {
                                onEdit(module.id);
                                setActionMenuId(null);
                              }}
                              sx={{ justifyContent: 'flex-start', color: '#F8FAFC', px: 2, py: 1.25, textTransform: 'none' }}
                            >
                              Edit
                            </Button>
                          )}
                          {access.canShare && (
                            <Button
                              fullWidth
                              onClick={() => {
                                onShare(module.id);
                                setActionMenuId(null);
                              }}
                              sx={{ justifyContent: 'flex-start', color: '#F8FAFC', px: 2, py: 1.25, textTransform: 'none' }}
                            >
                              Share…
                            </Button>
                          )}
                          {access.canDelete && (
                            <Button
                              fullWidth
                              onClick={() => {
                                onDelete(module.id);
                                setActionMenuId(null);
                              }}
                              sx={{ justifyContent: 'flex-start', color: '#F8FAFC', px: 2, py: 1.25, textTransform: 'none' }}
                            >
                              Delete
                            </Button>
                          )}
                          {!access.canEdit && !access.canDelete && !access.canShare && (
                            <Typography
                              sx={{ color: 'rgba(248,250,252,0.6)', px: 2, py: 1.5, fontSize: '0.75rem' }}
                            >
                              Shared with you — view only
                            </Typography>
                          )}
                        </Box>
                      )}
                    </motion.div>
                  );
                })}
              </Box>
            </Box>

            <IconButton
              onClick={handleNext}
              aria-label="Next applications"
              sx={{
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                  bgcolor: '#FFFFFF',
                  color: '#0F2942',
                  border: '1px solid rgba(15,41,66,0.10)',
                  boxShadow: '0 4px 12px -2px rgba(15,41,66,0.12)',
                  transition: 'background-color 160ms ease, color 160ms ease, transform 160ms ease',
                  '&:hover': {
                    bgcolor: '#0F2942',
                    color: '#FFFFFF',
                    borderColor: '#0F2942',
                    transform: 'scale(1.06)',
                  },
                }}
            >
              <ChevronRight className="w-5 h-5" />
            </IconButton>
            </Box>
          </Box>
        </motion.div>
      </Box>

      {/* The General settings' Description — an admin's own rich-text content,
          shown only when they have turned it on. Sanitised here, at the render
          site, rather than trusting whatever shape it was saved in. */}
      {homeConfig.showDescription && homeConfig.description && (
        <Box sx={{ position: 'relative', zIndex: 2, px: { xs: 4, md: 10 }, pb: { xs: 3, md: 4 } }}>
          <Box
            sx={{
              maxWidth: 860,
              mx: 'auto',
              borderRadius: '14px',
              border: '1px solid rgba(148,197,255,0.16)',
              bgcolor: 'rgba(255,255,255,0.04)',
              px: { xs: 3, md: 4 },
              py: { xs: 2.5, md: 3 },
              color: 'rgba(226,236,250,0.92)',
            }}
          >
            <div
              className="prose-sm text-sm leading-relaxed [&_a]:text-teal-300 [&_a]:underline [&_img]:max-w-full [&_img]:rounded [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
              // Admin-authored, and admin-only to write (`home:edit`,
              // server-enforced) — but every signed-in user renders this, so
              // it is sanitised here rather than trusted as already safe.
              dangerouslySetInnerHTML={{ __html: sanitizeRichText(homeConfig.description) }}
            />
          </Box>
        </Box>
      )}

      {/* FIXED FOOTER: "powered by MapGeoid" is a hardcoded brand element.
          It is never sourced from HomePageConfig and cannot be edited, restyled,
          or removed through Settings by any role. */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          pt: { xs: 3, md: 4 },
          pb: { xs: 3, md: 4 },
        }}
      >
        <Typography
          sx={{
            color: 'rgba(148,197,255,0.5)',
            fontSize: '0.68rem',
            fontWeight: 500,
            letterSpacing: 1.2,
          }}
        >
          {BRAND.poweredByPrefix}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            component="span"
            sx={{ color: '#F8FAFC', fontWeight: 800, fontSize: '1.35rem', letterSpacing: '-0.03em', lineHeight: 1 }}
          >
            Map
          </Typography>
          <Typography
            component="span"
            sx={{ color: '#4A90E2', fontWeight: 800, fontSize: '1.35rem', letterSpacing: '-0.03em', lineHeight: 1 }}
          >
            Ge
          </Typography>
          <Box sx={{ mx: '1px', display: 'flex', alignItems: 'center' }}>
            <MapGeoidGlobe size={20} />
          </Box>
          <Typography
            component="span"
            sx={{ color: '#4A90E2', fontWeight: 800, fontSize: '1.35rem', letterSpacing: '-0.03em', lineHeight: 1 }}
          >
            id
          </Typography>
        </Box>
      </Box>

      {showModal && (
        <Box
          // Light scrim + blur: the portal stays visible behind the dialog.
          className="gl-modal-backdrop"
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 3,
          }}
          onClick={closeAddDialog}
        >
          <Paper
            elevation={0}
            className="gl-modal gl-surface--strong"
            sx={{
              width: '100%',
              maxWidth: newAppKind ? 420 : 560,
              p: 4,
              position: 'relative',
              // MUI Paper paints its own surface and `sx` outranks the class,
              // so point it at the same tokens the glass system uses. The
              // radius must be restated too — MuiPaper-rounded would otherwise
              // clamp it back to 4px.
              borderRadius: 'var(--glass-radius-lg)',
              background: 'var(--glass-bg-strong)',
              backgroundImage: 'none',
              color: 'var(--glass-text)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {newAppKind ? `Name your ${newAppKind === 'dashboard' ? 'dashboard' : 'map app'}` : 'Add New Application'}
                </Typography>
                <IconButton onClick={closeAddDialog} aria-label="Close">
                  <X className="w-4 h-4" />
                </IconButton>
              </Box>

              {/* STAGE 1 — application type. The Map App branch keeps the
                  existing builder; Dashboard opens the new one. */}
              {!newAppKind ? (
                <>
                  <Typography variant="body2" sx={{ color: 'rgba(15,23,42,0.6)' }}>
                    Choose the kind of application you want to build.
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 2,
                    }}
                  >
                    {([
                      {
                        kind: 'map' as AppKind,
                        icon: Map,
                        label: 'Map App',
                        description: 'Create an interactive GIS map with layers, tools, and popups.',
                      },
                      {
                        kind: 'dashboard' as AppKind,
                        icon: LayoutGrid,
                        label: 'Dashboard',
                        description: 'Create a GIS dashboard with indicators, charts, and tables.',
                      },
                    ]).map((option) => {
                      const OptionIcon = option.icon;
                      return (
                        <Box
                          key={option.kind}
                          component="button"
                          type="button"
                          onClick={() => setNewAppKind(option.kind)}
                          sx={{
                            textAlign: 'left',
                            cursor: 'pointer',
                            font: 'inherit',
                            p: 2.5,
                            borderRadius: 3,
                            border: '1px solid rgba(15,41,66,0.14)',
                            bgcolor: '#FFFFFF',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: '#2DD4BF',
                              bgcolor: 'rgba(45,212,191,0.06)',
                              transform: 'translateY(-2px)',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: '12px',
                              background: 'linear-gradient(140deg, #0F2942 0%, #16556E 60%, #0D9488 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 1.5,
                            }}
                          >
                            <OptionIcon className="w-5 h-5 text-white" />
                          </Box>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#0F2942' }}>
                            {option.label}
                          </Typography>
                          <Typography sx={{ mt: 0.5, fontSize: '0.78rem', color: 'rgba(15,23,42,0.6)', lineHeight: 1.45 }}>
                            {option.description}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={closeAddDialog}
                    sx={{ borderRadius: '999px', textTransform: 'none', alignSelf: 'center', px: 4 }}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                /* STAGE 2 — name it, then continue to the matching builder. */
                <>
                  <Typography variant="body2" sx={{ color: 'rgba(15,23,42,0.6)' }}>
                    {newAppKind === 'dashboard'
                      ? 'Enter a name and continue to the Dashboard Builder.'
                      : 'Enter a name and continue to the Map App builder.'}
                  </Typography>
                  <input
                    ref={inputRef}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && submit()}
                    placeholder={
                      newAppKind === 'dashboard' ? 'e.g. Gas Distribution KPIs' : 'e.g. City Gas Network'
                    }
                    aria-label="Application name"
                    className="w-full rounded-3xl border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-200"
                  />
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => setNewAppKind(null)}
                      sx={{ borderRadius: '999px', textTransform: 'none' }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={submit}
                      disabled={!name.trim()}
                      sx={{ borderRadius: '999px', textTransform: 'none' }}
                    >
                      Create
                    </Button>
                  </Box>
                </>
              )}
            </Stack>
          </Paper>
        </Box>
      )}
    </Box>
  );
}

function Builder({
  module,
  onBack,
  onSave,
}: {
  module: AppModule;
  onBack: () => void;
  /** `launch: false` saves without navigating away from the builder. */
  onSave: (module: AppModule, options?: { launch?: boolean }) => void;
}) {
  const { currentUser } = useAuth();
  const [title, setTitle] = useState(module.title);
  /* The module's own layers, plus this person's scratch layers restored from
     their own storage. The two are held in ONE list so every layer operation —
     reorder, opacity, remove, symbology — works on both without a second code
     path; what separates them is the `temporary` flag, honoured on save. */
  const [layers, setLayers] = useState<Layer[]>(() => [
    ...module.layers.map((l) => ({ ...l })),
    ...temporaryLayerService.load(currentUser?.id, module.id),
  ]);
  const [tools, setTools] = useState<string[]>([...module.tools]);
  const [geoServerUrl, setGeoServerUrl] = useState(module.geoServerUrl || '');
  const [geoServerLayers, setGeoServerLayers] = useState<string[]>(module.geoServerLayers || []);
  const [homeCenter, setHomeCenter] = useState<[number, number]>(module.homeCenter || [28.7041, 77.1025]);
  const [homeZoom, setHomeZoom] = useState<number>(module.homeZoom ?? 12);
  const [center, setCenter] = useState<[number, number]>(module.homeCenter || [28.7041, 77.1025]);
  const [zoom, setZoom] = useState<number>(module.homeZoom ?? 12);
  /** Per-tool settings, keyed by tool id. See AppModule.toolConfigs. */
  const [toolConfigs, setToolConfigs] = useState<Record<string, Record<string, unknown>>>(
    module.toolConfigs ?? {},
  );
  const [showToolDialog, setShowToolDialog] = useState(false);
  /* Which tool the builder's own tool bar has opened. The viewer keeps the
     equivalent state as `openTools` / `activeToolTab`; the builder previously
     had none, because every tool was a permanently visible tab. */
  const [builderOpenTools, setBuilderOpenTools] = useState<string[]>([]);
  const [builderActiveTool, setBuilderActiveTool] = useState<string | null>(null);
  /** Opens (and switches to) a tool tab, adding it to the open list if it
   *  is not there yet — the ToolStrip icon click and Near Me's "Directions
   *  from here" button both need exactly this, not just an active-tab
   *  switch that bounces back to Layers because the tab was never opened. */
  const openToolTab = (toolId: string) => {
    setBuilderActiveTool(toolId);
    setBuilderOpenTools((current) => (current.includes(toolId) ? current : [...current, toolId]));
  };
  /* The strip starts open in the builder: an admin arriving to configure
     tools should see them without hunting for a toggle. */
  const [toolsOpen, setToolsOpen] = useState(true);

  /** This application's own header styling; see AppModule.headerTheme. */
  const [headerTheme, setHeaderTheme] = useState<NonNullable<AppModule['headerTheme']>>(
    module.headerTheme ?? {},
  );
  const [showHeaderDesigner, setShowHeaderDesigner] = useState(false);

  /** Per-application colour scheme; see AppModule.appearance. */
  const [appearance, setAppearance] = useState<'inherit' | 'dark'>(
    module.appearance === 'dark' ? 'dark' : 'inherit',
  );
  /** App-wide theme set from Header settings; see AppModule.colorTheme. */
  const [colorTheme, setColorTheme] = useState<AppModule['colorTheme']>(module.colorTheme);
  /** Builder preview: the real end-user viewer over the UNSAVED draft. */
  const [previewing, setPreviewing] = useState(false);
  /** Timestamp of the last plain Save, used only to flash a confirmation. */
  const [savedNote, setSavedNote] = useState<number | null>(null);

  const [availableGeoServerLayers, setAvailableGeoServerLayers] = useState<GeoServerLayer[]>([]);
  const [geoServerLoading, setGeoServerLoading] = useState(false);
  const [geoServerError, setGeoServerError] = useState<string | null>(null);

  const [serviceType, setServiceType] = useState<ServiceType>('geoserver');
  const [arcgisUrl, setArcgisUrl] = useState(module.arcgisUrl || '');
  const [arcgisLayers, setArcgisLayers] = useState<number[]>(module.arcgisLayers || []);
  const [availableArcgisLayers, setAvailableArcgisLayers] = useState<ArcgisLayer[]>([]);
  const [arcgisLoading, setArcgisLoading] = useState(false);
  const [arcgisError, setArcgisError] = useState<string | null>(null);
  /* Which saved connection (if any) `arcgisUrl` came from, and when its
     token needs renewing — see the renewal effect below and
     ModuleArcgisConnectionUpdate on the backend. Absent for a manually-typed
     URL, which nothing here ever needs to touch again. */
  const [arcgisConnectionId, setArcgisConnectionId] = useState(module.arcgisConnectionId);
  const [arcgisConnectionService, setArcgisConnectionService] = useState(module.arcgisConnectionService);
  const [arcgisTokenExpiresAt, setArcgisTokenExpiresAt] = useState(module.arcgisTokenExpiresAt);
  /** Bumped by "Refresh layer" to force the map to re-request its sources. */
  const [layerRefreshToken, setLayerRefreshToken] = useState(0);

  /* Saved connections (Settings › ArcGIS Servers / GeoServer), for "Add
     layers › Saved connection". Loaded once — the Add Layers dialog is the
     only consumer, and a connection added mid-session in another tab is rare
     enough that a manual dialog reopen already refreshes this list. */
  const [connections, setConnections] = useState<ServerConnection[]>([]);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [selectedConnectionId, setSelectedConnectionIdRaw] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  /* ArcGIS only: one folder's worth of the selected connection's services —
     null before "Connect" is pressed, or after switching connections, since
     a stale folder listing from a DIFFERENT server would be actively wrong
     rather than just outdated. */
  const [connectionBrowse, setConnectionBrowse] = useState<ServerConnectionBrowseResult | null>(null);
  const [connectionBrowseLoading, setConnectionBrowseLoading] = useState(false);
  const [connectionBrowseError, setConnectionBrowseError] = useState<string | null>(null);
  /**
   * Set only when "Add layers › Saved connection" resolved a DIFFERENT
   * ArcGIS service than the module's primary `arcgisUrl`, while the module
   * already had layers from that primary one — see `selectConnectionService`.
   * While this is set, the picker's Add/Remove/pending-count logic stamps
   * `Layer.arcgisServiceUrl` on new layers instead of touching the shared
   * `arcgisUrl`/`arcgisLayers` state, so the module can hold layers from
   * more than one ArcGIS service without any of them breaking the others.
   */
  const [secondaryArcgisServiceUrl, setSecondaryArcgisServiceUrl] = useState<string | null>(null);
  const setSelectedConnectionId = (id: string | null) => {
    setSelectedConnectionIdRaw(id);
    setConnectionBrowse(null);
    setConnectionBrowseError(null);
    setConnectError(null);
    setSecondaryArcgisServiceUrl(null);
  };

  useEffect(() => {
    let cancelled = false;
    setConnectionsLoading(true);
    Promise.all([
      serverConnectionsService.list('arcgis').catch(() => []),
      serverConnectionsService.list('geoserver').catch(() => []),
    ])
      .then(([arcgisConnections, geoserverConnections]) => {
        if (!cancelled) setConnections([...arcgisConnections, ...geoserverConnections]);
      })
      .finally(() => {
        if (!cancelled) setConnectionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Runs once on mount: silently renews this module's ArcGIS connection
  // token if it is close to expiring — see renewArcgisConnectionIfNeeded.
  useEffect(() => {
    let cancelled = false;
    void renewArcgisConnectionIfNeeded(
      { id: module.id, arcgisConnectionId, arcgisConnectionService, arcgisTokenExpiresAt },
      (nextArcgisUrl, nextExpiresAt) => {
        if (cancelled) return;
        setArcgisUrl(nextArcgisUrl);
        setArcgisTokenExpiresAt(nextExpiresAt);
      },
    );
    return () => {
      cancelled = true;
    };
    // Deliberately mount-only: the module this effect renews is fixed for
    // the Builder's lifetime, and `arcgisConnectionId`/`arcgisTokenExpiresAt`
    // are two of the very things it sets — depending on them would re-run it
    // right after its own write.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Field names per layer, for the tool configuration form.
   *
   * Resolved lazily and cached: the dialog needs "which fields can Search look
   * at", and that answer only exists on the service. Failures are swallowed —
   * an unreachable layer should leave the picker empty, not block the dialog.
   */
  const [toolFieldsByLayer, setToolFieldsByLayer] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (!showToolDialog) return;
    let cancelled = false;

    void (async () => {
      const resolved: Record<string, string[]> = {};
      for (const layer of layers) {
        if (toolFieldsByLayer[layer.id]) continue;
        try {
          const fields = await fetchLayerFieldNames(layer, {
            ...module,
            geoServerUrl,
            arcgisUrl,
            layers,
          });
          resolved[layer.id] = fields;
        } catch {
          resolved[layer.id] = [];
        }
      }
      if (!cancelled && Object.keys(resolved).length > 0) {
        setToolFieldsByLayer((current) => ({ ...current, ...resolved }));
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToolDialog, layers, geoServerUrl, arcgisUrl]);

  /** The module as it currently stands in the builder, unsaved. */
  const draftModule = (): AppModule => ({
    ...module,
    title,
    // Scratch layers are stripped here, not filtered in the UI: pressing Save
    // must never publish one person's buffer to everyone with this map.
    layers: withoutTemporary(layers),
    tools,
    geoServerUrl,
    geoServerLayers,
    arcgisUrl,
    arcgisLayers,
    arcgisConnectionId,
    arcgisConnectionService,
    arcgisTokenExpiresAt,
    homeCenter: center,
    homeZoom: zoom,
    // Saved alongside the home view: both are "how this map opens".
    basemap,
    appearance,
    colorTheme,
    toolConfigs,
    headerTheme,
  });

  /**
   * Saves, and moves the Home button to the extent that was just saved.
   *
   * `draftModule` already writes the LIVE map view out as `homeCenter` /
   * `homeZoom` — where the admin leaves the map is how the app opens. But the
   * builder's own Home button read a copy taken when the builder mounted, and
   * nothing ever wrote to it. So after saving, Home still flew back to the
   * PREVIOUS home — for a map that had never been saved, the placeholder
   * centre a new module is created with — which reads as "Home goes somewhere
   * I have never been".
   *
   * Saving is the only thing that sets the home view, so it is the only thing
   * that moves it: the button and the saved module can no longer disagree.
   */
  const saveDraft = (options?: { launch?: boolean }) => {
    const draft = draftModule();
    setHomeCenter(draft.homeCenter);
    setHomeZoom(draft.homeZoom);
    onSave(draft, options);
  };

  /* The "Saved" flash clears itself; leaving it up would imply the state is
     still pending. */
  useEffect(() => {
    if (savedNote === null) return undefined;
    const timer = window.setTimeout(() => setSavedNote(null), 1800);
    return () => window.clearTimeout(timer);
  }, [savedNote]);

  /** Right-side symbology panel target. */
  const [symbologyLayerId, setSymbologyLayerId] = useState<string | null>(null);
  /* Labeling has the same shape as symbology: an open layer id plus an
     uncommitted preview that overrides the stored config on the map only. */
  const [labelingLayerId, setLabelingLayerId] = useState<string | null>(null);
  const [labelingPreview, setLabelingPreview] = useState<LayerLabeling | null>(null);
  /* Pop-up panel target. No preview state: a pop-up only exists on click, so
     the panel previews it inside itself rather than over the map. */
  const [popupLayerId, setPopupLayerId] = useState<string | null>(null);
  /** Layer whose attribute table is open in the floating window, if any.
   *  Owned here (not in the TOC) so the window is a sibling of the map. */
  const [attributeLayer, setAttributeLayer] = useState<Layer | null>(null);
  /** Free-floating (default) vs docked to a genuine half of the map area. */
  const [attributeDock, setAttributeDock] = useState<'float' | 'right' | 'bottom'>('float');
  /** Docked size, as a fraction of the map area — dragging the docked
   *  panel's own divider (in FloatingAttributeTable) reports here. */
  const [attributeDockSize, setAttributeDockSize] = useState(0.5);

  /**
   * The LIVE layer the attribute table is showing.
   *
   * `attributeLayer` is the object captured when the window was opened — a
   * snapshot. Filter it afterwards and that snapshot still carries the old
   * (or no) filter, so the table would never re-request and would keep
   * answering the previous question. Looked up by id each render instead.
   */
  const attributeLayerLive = attributeLayer
    ? layers.find((entry) => entry.id === attributeLayer.id) ?? attributeLayer
    : null;

  /** Add-layers dialog. `mode` is remembered so the plain half of the split
   *  button reopens whichever source was used last. */
  const [addOpen, setAddOpen] = useState(false);
  /** Set when the ribbon's per-tool gear opened the Library. Null = gallery. */
  const [editToolId, setEditToolId] = useState<string | null>(null);
  const [addMode, setAddMode] = useState<AddLayersMode>('server');
  /** Uncommitted renderer previewed on the map while the panel is open. */
  const [rendererPreview, setRendererPreview] = useState<Layer['renderer'] | null>(null);
  const [basemap, setBasemap] = useState<BasemapId>(module.basemap ?? 'street');
  const [locating, setLocating] = useState(false);
  /** Set when `doLocate` fails; shown in place of the "Locating you…" chip. */
  const [locateError, setLocateError] = useState<string | null>(null);
  /* ── Tool state shared with the map ──────────────────────────────────
     Owned here rather than in the TOC because the MAP renders the results: a
     panel asks for a buffer, the map draws it. Keeping it in the panel would
     mean the overlay vanished whenever the tab changed. */
  const [toolPoint, setToolPoint] = useState<MapPoint | null>(null);
  const [toolOverlay, setToolOverlay] = useState<GeoJSON.FeatureCollection | null>(null);
  const [swipe, setSwipe] = useState<{ layerId: string | null; vertical: boolean }>({
    layerId: null,
    vertical: false,
  });

  /** The swiped layer translated into what MapView addresses layers by. */
  const swipeTarget = useMemo(() => {
    if (!swipe.layerId) return null;
    const layer = layers.find((entry) => entry.id === swipe.layerId);
    if (!layer) return null;
    // A GeoTIFF is neither an ArcGIS sublayer nor a WMS layer name — checked
    // first so it is never mistaken for the latter (any layer without an
    // `arcgisLayerId` used to fall into `wmsLayerName` by default).
    if (layer.localRaster) {
      return { localRasterLayerId: layer.id, vertical: swipe.vertical };
    }
    return {
      arcgisLayerId: layer.arcgisLayerId ?? null,
      wmsLayerName: layer.arcgisLayerId === undefined ? layer.name : null,
      vertical: swipe.vertical,
    };
  }, [swipe, layers]);

  /* What the attribute table has ticked, mirrored on the map, plus a counter
     that makes it blink. A counter and not a boolean: pressing Zoom to
     selected twice has to blink twice. */
  const [tableSelection, setTableSelection] = useState<GeoJSON.Feature[]>([]);
  const [selectionKeys, setSelectionKeys] = useState<string[]>([]);
  const [selectionFlash, setSelectionFlash] = useState(0);

  /**
   * Frames the ticked rows and draws attention to them.
   *
   * Zooming alone was the whole behaviour before, which left people hunting
   * for what had just been framed — especially with several features spread
   * across the view, where the map moves but nothing says which shapes
   * mattered.
   *
   * The actual framing happens in MapViewOL, not here: fitting a line or
   * polygon properly needs the real, on-screen pixel size of the map, which
   * only the OL view itself has. This just bumps the token MapViewOL's own
   * "fit to selection" effect watches (reading `tableSelection`, already set
   * by the `onSelectionChange` call every caller of this makes first) —
   * setting a plain `center`/`zoom` pair here the way this used to could
   * only ever express ONE fixed scale, which cropped anything bigger than
   * that scale could show.
   */
  const zoomToSelection = useCallback((features: AttributeFeature[]) => {
    // Blink whether or not the view moved: pressing the button with the map
    // already framed still has to point at something.
    setSelectionFlash((count) => count + 1);
  }, []);

  /* ── Draw ────────────────────────────────────────────────────────────
     Sketches accumulate into ONE temporary layer rather than a layer per
     shape, which is how a sketch pad behaves: the TOC would otherwise grow a
     row every time someone clicked. It is the same kind of layer the Buffer
     tool creates — this person's own, never saved into the shared map. */
  const [drawType, setDrawType] = useState<'point' | 'line' | 'polygon' | 'circle'>('point');
  const [isDrawing, setIsDrawing] = useState(false);
  /** Vertices placed in the shape being drawn right now, 0 when none. */
  const [sketchVertices, setSketchVertices] = useState(0);
  const [drawFinishToken, setDrawFinishToken] = useState(0);

  /* ── Edit: new feature / reshape ────────────────────────────────────────
     A fourth consumer of the same shared sketch controller Draw/Measure/
     Select already arm — the Edit tool's "New feature" flow. The finished
     shape is handed back as a tagged result rather than a callback (the
     Edit panel closes over neither directly), the same shape `measuredFeature`
     already uses for the Measure tool. */
  const [editCreateShape, setEditCreateShape] = useState<
    'point' | 'line' | 'polygon' | 'circle' | 'rectangle' | 'freehand-polygon' | 'freehand-line' | null
  >(null);
  const [editSketchResult, setEditSketchResult] = useState<{ token: number; feature: GeoJSON.Feature } | null>(
    null,
  );
  /** A feature's geometry, live on the map as a single draggable whole. */
  const [moveGeometry, setMoveGeometry] = useState<GeoJSON.Geometry | null>(null);
  const [moveResult, setMoveResult] = useState<{ token: number; geometry: GeoJSON.Geometry } | null>(null);
  /** The Edit tool's open record's own geometry, shown highlighted/dashed on
   *  the map so a not-yet-saved sketch, reshape, or move never just
   *  vanishes between finishing the edit and pressing Save. */
  const [draftGeometry, setDraftGeometry] = useState<GeoJSON.Geometry | null>(null);
  /** The Edit tool's own toggle — read by the map to drive ol/interaction/Snap. */
  const [snapEnabled, setSnapEnabled] = useState(false);

  /** The sketch pad is identified by its own tags, not a separately-tracked
   *  id — there is only ever one at a time, so `temporary && temporarySource
   *  === 'Draw'` finds it directly. (A previous version tracked its id in a
   *  ref written from inside this very setLayers updater — a real anti-
   *  pattern: an updater function is supposed to be pure, and a side effect
   *  inside one runs whenever/however many times React decides to call it,
   *  not necessarily once, synchronously, right here.) */
  const findDrawingsLayer = (current: Layer[]) =>
    current.find((layer) => layer.temporary && layer.temporarySource === 'Draw');

  const addDrawnFeature = useCallback((feature: GeoJSON.Feature) => {
    setLayers((current) => {
      const existing = findDrawingsLayer(current);

      if (existing) {
        const data: GeoJSON.FeatureCollection = {
          type: 'FeatureCollection',
          features: [...(existing.localData?.features ?? []), feature],
        };
        return current.map((layer) =>
          layer.id === existing.id
            ? { ...layer, localData: data, bounds: computeBounds(data) }
            : layer,
        );
      }

      const data: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [feature] };
      return [
        ...current,
        {
          id: createId('temp'),
          name: 'Drawings',
          sublayers: [],
          checked: true,
          expanded: false,
          localData: data,
          bounds: computeBounds(data),
          temporary: true,
          temporarySource: 'Draw',
          /* A sketch pad holds points, lines and polygons at once, so fill and
             stroke are set separately: a polygon wants a light fill you can
             read the map through, a line wants a solid stroke. */
          renderer: {
            type: 'single',
            color: '#F97316',
            fillOpacity: 0.25,
            lineOpacity: 1,
            outlineColor: '#C2410C',
            outlineWidth: 3,
            pointSize: 12,
          },
        } as Layer,
      ];
    });
  }, []);

  /** Empties the sketch pad without disturbing anything else on the map. */
  const clearDrawings = useCallback(() => {
    setLayers((current) => current.filter((layer) => !(layer.temporary && layer.temporarySource === 'Draw')));
  }, []);

  /** Removes one drawn feature, keeping the rest of the sketch pad intact. */
  const deleteDrawnFeature = useCallback((index: number) => {
    setLayers((current) => {
      const layer = findDrawingsLayer(current);
      const features = layer?.localData?.features;
      if (!layer || !features) return current;
      const remaining = features.filter((_, featureIndex) => featureIndex !== index);
      if (remaining.length === 0) {
        return current.filter((entry) => entry.id !== layer.id);
      }
      const data: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: remaining };
      return current.map((entry) =>
        entry.id === layer.id ? { ...entry, localData: data, bounds: computeBounds(data) } : entry,
      );
    });
  }, []);

  /** Frames one drawn feature — the same zoom-and-blink `zoomToSelection`
   *  gives the attribute table's "Zoom to selected". */
  const zoomToDrawnFeature = useCallback(
    (feature: GeoJSON.Feature) =>
      zoomToSelection([
        {
          id: String(feature.id ?? 'draw'),
          properties: (feature.properties ?? {}) as Record<string, unknown>,
          geometry: feature.geometry,
        },
      ]),
    [zoomToSelection],
  );

  /** Promotes the sketch pad from a private, temporary layer into a real one,
   *  so it is included the next time the module itself is saved. A fresh
   *  Drawings layer starts on the next shape — nothing drawn after Save
   *  quietly rides along into the layer that was just made permanent. */
  const saveDrawings = useCallback(() => {
    setLayers((current) =>
      current.map((entry) =>
        entry.temporary && entry.temporarySource === 'Draw'
          ? { ...entry, temporary: false, temporarySource: undefined }
          : entry,
      ),
    );
  }, []);

  /* ── Sketching, shared by Draw and Measure ───────────────────────────
     Both tools own the map's clicks, and both draw the same rubber band, so
     there is ONE sketch controller and the difference is what happens to the
     finished shape: Draw keeps it as a layer, Measure turns it into a number
     and throws it away. */
  const [measureSketchMode, setMeasureSketchMode] = useState<'line' | 'polygon' | null>(null);
  const [measureImperial, setMeasureImperial] = useState(false);
  const [measureShapes, setMeasureShapes] = useState<GeoJSON.Feature[]>([]);
  const [measuredFeature, setMeasuredFeature] = useState<
    { token: number; feature: GeoJSON.Feature } | null
  >(null);

  /** Written on every measurement label, in the unit the panel is set to. */
  const measureFormat = useCallback(
    (metres: number) => formatLength(metres, measureImperial ? 'imperial' : 'metric'),
    [measureImperial],
  );

  const handleMeasureMode = useCallback((mode: 'line' | 'polygon' | null, imperial?: boolean) => {
    setMeasureSketchMode(mode);
    setMeasureImperial(Boolean(imperial));
  }, []);


  /* ── Select ──────────────────────────────────────────────────────────
     The third destination for a sketch. The shape is not kept; it is used to
     ask which features it touched, and then discarded. */
  const [selectSketchMode, setSelectSketchMode] = useState<
    'rectangle' | 'polygon' | 'circle' | null
  >(null);
  const [selectHits, setSelectHits] = useState<{ token: number; rows: AttributeFeature[] } | null>(
    null,
  );

  /** The Print tool's extent-preview box — see MapViewOL's own doc comment
   *  on `printFrameRatio`. Null while the Print tab is closed. */
  const [printFrameRatio, setPrintFrameRatio] = useState<number | null>(null);
  const selectLayersRef = useRef<string[]>([]);
  /** How the next box combines with what is already selected. */
  const selectCombineRef = useRef<'new' | 'add' | 'remove'>('new');
  /** The current selection, for Add and Remove to build on. A ref because the
   *  sketch handler is a callback that must not go stale between boxes. */
  const selectionRowsRef = useRef<AttributeFeature[]>([]);
  /* Handed to the tool panels so one can combine with what is already
     selected. Reads the REF, so it is stable and always current: a tool that
     closed over the array instead would add to whatever happened to be
     selected when its tab was opened. */
  const readSelection = useCallback(() => selectionRowsRef.current, []);

  /** A layer's features for hit-testing, from whichever service backs it. */
  const loadFeaturesForSelect = useCallback(
    async (layer: Layer, limit = 2000): Promise<GeoJSON.Feature[]> => {
      // The layer's own filter applies here too, for the same reason the
      // ArcGIS branch below sends it: a box cannot select what is not drawn.
      if (layer.localData) return filteredLocalFeatures(layer).slice(0, limit);

      if (layer.arcgisLayerId !== undefined) {
        const effectiveUrl = arcgisServiceUrlFor(layer, { arcgisUrl });
        const baseUrl = getArcgisBaseUrl(effectiveUrl);
        if (!baseUrl) return [];
        const params = new URLSearchParams({
          // The layer's own filter still applies: a box cannot select what the
          // map is not showing.
          where: layer.filter?.trim() || '1=1',
          outFields: '*',
          outSR: '4326',
          f: 'geojson',
          resultRecordCount: String(limit),
        });
        const response = await fetch(
          withArcgisToken(`${baseUrl}/${layer.arcgisLayerId}/query?${params.toString()}`, effectiveUrl),
        );
        if (!response.ok) return [];
        return (await response.json())?.features ?? [];
      }

      if (!geoServerUrl) return [];
      const response = await fetch(getWfsUrl(geoServerUrl, layer.name, limit, layer.filter));
      if (!response.ok) return [];
      return (await response.json())?.features ?? [];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const sketchMode = isDrawing ? drawType : measureSketchMode ?? selectSketchMode ?? editCreateShape;

  const handleSketch = useCallback(
    async (feature: GeoJSON.Feature) => {
      if (measureSketchMode) {
        setMeasuredFeature((current) => ({ token: (current?.token ?? 0) + 1, feature }));
        return;
      }

      if (selectSketchMode) {
        /* The shape is a question, not a result: it asks which of the layer's
           features it touches, and is thrown away once answered.
           Uses the same intersect test as Select By Location (prepare/relate)
           rather than a vertex-only check — a vertex-only test missed a
           feature whose SEGMENT passed straight through the drawn shape
           without either endpoint landing inside it, which is exactly what
           happens drawing a small box over the middle of one long straight
           line (e.g. a single pipe between two junctions far apart) — the
           most common way to try to select just one line. */
        if (feature.geometry?.type !== 'Polygon') return;
        const shapeGeom = prepare(feature.geometry);
        const targets = layers.filter((entry) => selectLayersRef.current.includes(entry.id));
        if (targets.length === 0) return;

        /* Read in parallel — one slow layer must not hold up the others, and a
           box over four layers is four independent questions.
           Each catch is TAGGED with the layer it came from: the panel reports
           by layer, and a flattened list would have thrown that away. */
        const perLayer = await Promise.all(
          targets.map(async (target) => {
            const features = await loadFeaturesForSelect(target);
            return features
              .filter((entry) => relate(prepare(entry.geometry), shapeGeom, 'intersect', 0, 1))
              .map((entry) => ({ entry, layerId: target.id }));
          }),
        );
        const caught = perLayer.flat();

        /* New / Add / Remove — the three selection verbs every desktop GIS
           has, and what makes more than one box useful. Combined BY KEY, not
           by position: the two passes are separate reads of the service, so
           the same feature can arrive at a different index each time. */
        const mode = selectCombineRef.current;
        const previous = selectionRowsRef.current;
        const caughtRows: AttributeFeature[] = caught.map(({ entry, layerId }, index) => ({
          ...(entry.properties ?? {}),
          id: String(entry.id ?? index),
          key: featureKey(entry, layerId),
          // Which layer this came from, so the panel can report by layer.
          layerId,
          properties: (entry.properties ?? {}) as Record<string, unknown>,
          geometry: entry.geometry ?? undefined,
        }));

        let combined: AttributeFeature[] = caughtRows;
        if (mode === 'add') {
          const seen = new Set(previous.map((row) => row.key));
          combined = [...previous, ...caughtRows.filter((row) => !seen.has(row.key))];
        } else if (mode === 'remove') {
          const drop = new Set(caughtRows.map((row) => row.key));
          combined = previous.filter((row) => !drop.has(row.key));
        }

        setSelectHits((current) => ({ token: (current?.token ?? 0) + 1, rows: combined }));

        /* Stays armed. Selecting more than one thing is the normal case, and
           re-pressing Start between every box would make it the exception. */

        // The shared selection channel, so the map highlights them in cyan and
        // an open attribute table ticks the same rows.
        trackSelection(combined);
        return;
      }

      if (editCreateShape) {
        setEditSketchResult((current) => ({ token: (current?.token ?? 0) + 1, feature }));
        setEditCreateShape(null);
        return;
      }

      addDrawnFeature(feature);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [measureSketchMode, selectSketchMode, editCreateShape, addDrawnFeature, layers],
  );

  /** A feature was dragged as a whole on the map — the Edit tool's Move flow. */
  const onFeatureMove = useCallback((geometry: GeoJSON.Geometry) => {
    setMoveResult((current) => ({ token: (current?.token ?? 0) + 1, geometry }));
  }, []);

  /**
   * Table rows to something the map can draw.
   *
   * Rows without geometry are dropped, not passed through: ArcGIS happily
   * returns attribute-only records, and L.geoJSON throws "Invalid LatLng" on
   * the first one and drops the whole collection — the same trap
   * `hasDrawableGeometry` exists for on the layer side.
   */
  const trackSelection = useCallback((rows: AttributeFeature[]) => {
    // Kept alongside the geometry so a table opened LATER can tick the same
    // rows — it issues its own request, so row numbers would not line up.
    selectionRowsRef.current = rows;
    setSelectionKeys(rows.map((row) => row.key).filter((key): key is string => Boolean(key)));
    setTableSelection(
      rows
        .filter((row) => row.geometry)
        .map((row) => ({
          type: 'Feature' as const,
          properties: row.properties ?? {},
          geometry: row.geometry as GeoJSON.Geometry,
        })),
    );
  }, []);

  /* Scratch layers follow the person, not the map, so they are written to their
     own storage whenever the layer list changes — that is what "stays until
     they remove it" means across a reload. */
  useEffect(() => {
    temporaryLayerService.save(currentUser?.id, module.id, layers);
  }, [layers, currentUser?.id, module.id]);

  /**
   * Adds a tool's result to the TOC as this person's own layer.
   *
   * It arrives as `localData`, which is the same shape a file import produces —
   * so it renders, styles, labels and identifies through the existing
   * client-side path rather than a parallel one built for analysis output.
   */
  const addTemporaryLayer = useCallback(
    (
      name: string,
      data: GeoJSON.FeatureCollection,
      source: string,
      renderer?: Layer['renderer'],
      heatmap?: HeatmapConfig,
    ) => {
      const id = createId('temp');
      setLayers((current) => [
        ...current,
        {
          id,
          name,
          sublayers: [],
          checked: true,
          expanded: false,
          localData: data,
          bounds: computeBounds(data),
          // A heatmap result is a cloud of sample points, not the polygon
          // shape every other temp layer (Buffer, Draw) has produced so far.
          geometryType: heatmap ? 'point' : 'polygon',
          temporary: true,
          temporarySource: source,
          renderer,
          heatmap,
        },
      ]);
      return id;
    },
    [],
  );

  /**
   * The Georeference tool's raster twin of `addTemporaryLayer` above — a
   * tool panel had no way to add a brand-new RASTER layer before this,
   * only `onCreateTempLayer`'s vector `FeatureCollection`. Kept as a
   * separate function rather than widening `addTemporaryLayer` itself:
   * the two shapes (`localData` vs `localRaster`) don't overlap enough to
   * share one signature without optional-everything mush.
   */
  const addTemporaryRasterLayer = useCallback(
    (name: string, raster: LocalRasterData, bounds: [number, number, number, number], source: string) => {
      const id = createId('temp');
      setLayers((current) => [
        ...current,
        {
          id,
          name,
          sublayers: [],
          checked: true,
          expanded: false,
          localRaster: raster,
          bounds,
          temporary: true,
          temporarySource: source,
        },
      ]);
      return id;
    },
    [],
  );

  /**
   * Adds an empty TOC folder. Expanded from the start — an admin who just
   * named it is about to move layers into it, and a folder that opens onto
   * nothing looks the same whether it starts open or closed, so it may as
   * well start in the state that saves the next click.
   */
  const createLayerGroup = useCallback((name: string) => {
    const id = createId('group');
    setLayers((current) => [
      ...current,
      { id, name, sublayers: [], checked: true, expanded: true, isGroup: true },
    ]);
  }, []);

  /** Temporary marker for the last location search. */
  const [searchMarker, setSearchMarker] = useState<
    { latitude: number; longitude: number; label: string } | null
  >(null);

  /**
   * A layer inside a GROUP that is itself switched off draws nothing, no
   * matter what the layer's own `checked` says — the group checkbox is a
   * master switch, not something that overwrites each child's individual
   * state (see TableOfContents's own `toggleGroup`). An ungrouped layer (no
   * `groupId`) is always visible on this count, same as before groups
   * existed.
   */
  const groupVisible = useCallback(
    (layer: Layer) =>
      !layer.groupId || layers.find((entry) => entry.id === layer.groupId)?.checked !== false,
    [layers],
  );

  const visibleGeoServerLayers = useMemo(
    () =>
      layers
        .filter(
          (layer) =>
            layer.id.startsWith('geoserver_') &&
            layer.checked &&
            groupVisible(layer) &&
            isVisibleAtZoom(layer, zoom),
        )
        .map((layer) => layer.name),
    [layers, zoom, groupVisible]
  );

  const visibleArcgisLayers = useMemo(
    () =>
      layers
        // A secondary-service layer (Layer.arcgisServiceUrl set) is excluded:
        // this list feeds MapView's PRIMARY `arcgisLayers` prop, which is
        // rendered against the module's primary `arcgisUrl` — a secondary
        // layer's id belongs to `arcgisSecondaryLayers` instead.
        .filter(
          (layer) =>
            layer.checked &&
            groupVisible(layer) &&
            layer.arcgisLayerId !== undefined &&
            !layer.arcgisServiceUrl &&
            isVisibleAtZoom(layer, zoom),
        )
        .map((layer) => layer.arcgisLayerId as number),
    [layers, zoom, groupVisible]
  );

  /** Stored labeling for a layer, with the open panel's preview taking over. */
  const labelingFor = useCallback(
    (layer: Layer): LayerLabeling | undefined =>
      layer.id === labelingLayerId && labelingPreview ? labelingPreview : layer.labeling,
    [labelingLayerId, labelingPreview],
  );

  /**
   * Symbology / opacity / filter per ArcGIS layer, consumed by MapView.
   * While the symbology panel is open, the uncommitted preview overrides the
   * stored renderer so the map updates live without persisting anything.
   */
  const arcgisLayerStyles = useMemo(() => {
    const styles: Record<number, { renderer?: Layer['renderer']; opacity?: number; filter?: string }> = {};
    for (const layer of layers) {
      // A SECONDARY-service layer (Layer.arcgisServiceUrl set) is excluded
      // here on purpose, not just unused: its id can validly collide with a
      // PRIMARY layer's, and this dict is keyed by bare id — including it
      // would risk one service's style silently overwriting the other's.
      // See `arcgisSecondaryLayers` below for its own, per-service dict.
      if (layer.arcgisLayerId === undefined || layer.arcgisServiceUrl) continue;
      const isPreviewTarget = layer.id === symbologyLayerId && rendererPreview;
      styles[layer.arcgisLayerId] = {
        renderer: isPreviewTarget ? rendererPreview : layer.renderer,
        opacity: layer.opacity,
        filter: layer.filter,
      };
    }
    return styles;
  }, [layers, symbologyLayerId, rendererPreview]);

  /**
   * Layers from ArcGIS services OTHER than the module's primary one — see
   * `Layer.arcgisServiceUrl` and MapView's own `arcgisSecondaryLayers` doc.
   * Grouped by service so each group's styles dict only ever holds THAT
   * service's own layers, the same collision-avoidance reasoning as above.
   */
  const arcgisSecondaryLayers = useMemo(() => {
    // A plain record, not a generic Map: this build's TypeScript rejects
    // `new Map<K, V>()` here (TS2350), the same way it did in loadArcgisLayers.
    type Group = { serviceUrl: string; layerIds: number[]; styles: Record<number, { renderer?: Layer['renderer']; opacity?: number; filter?: string }> };
    const groups: Record<string, Group> = {};
    for (const layer of layers) {
      if (layer.arcgisLayerId === undefined || !layer.arcgisServiceUrl) continue;
      // Same visibility rule `visibleArcgisLayers` applies to the primary
      // service's layers — unchecked, group-hidden, or out of the layer's
      // own scale band draws neither.
      if (!layer.checked || !groupVisible(layer) || !isVisibleAtZoom(layer, zoom)) continue;
      const key = getArcgisBaseUrl(layer.arcgisServiceUrl);
      if (!key) continue;
      const group = (groups[key] ||= { serviceUrl: layer.arcgisServiceUrl, layerIds: [], styles: {} });
      group.layerIds.push(layer.arcgisLayerId);
      const isPreviewTarget = layer.id === symbologyLayerId && rendererPreview;
      group.styles[layer.arcgisLayerId] = {
        renderer: isPreviewTarget ? rendererPreview : layer.renderer,
        opacity: layer.opacity,
        filter: layer.filter,
      };
    }
    return Object.values(groups);
  }, [layers, symbologyLayerId, rendererPreview, zoom, groupVisible]);

  /** Same, for GeoServer WMS layers — keyed by service layer name. */
  const geoServerLayerStyles = useMemo(() => {
    const styles: Record<
      string,
      {
        renderer?: Layer['renderer'];
        opacity?: number;
        filter?: string;
        geometryType?: Layer['geometryType'];
        labeling?: LayerLabeling;
      }
    > = {};
    for (const layer of layers) {
      if (layer.arcgisLayerId !== undefined) continue;
      const isPreviewTarget = layer.id === symbologyLayerId && rendererPreview;
      styles[layer.name] = {
        renderer: isPreviewTarget ? rendererPreview : layer.renderer,
        opacity: layer.opacity,
        filter: layer.filter,
        geometryType: layer.geometryType,
        // GeoServer draws these as a separate transparent overlay.
        labeling: labelingFor(layer),
      };
    }
    return styles;
  }, [layers, symbologyLayerId, rendererPreview, labelingFor]);

  /** File-backed layers, with the same live symbology preview as services. */
  const localLayers = useMemo(
    () =>
      layers
        .filter((layer) => layer.checked && groupVisible(layer) && layer.localData && isVisibleAtZoom(layer, zoom))
        .map((layer) => ({
          id: layer.id,
          // Titles the pop-up card, the same way a service layer's name does.
          name: layer.displayName?.trim() || layer.name,
          // Filtered here, because there is no service to filter it for us.
          data: filteredLocalData(layer),
          style: {
            renderer:
              layer.id === symbologyLayerId && rendererPreview ? rendererPreview : layer.renderer,
            opacity: layer.opacity,
            heatmap: layer.heatmap,
          },
        })),
    [layers, symbologyLayerId, rendererPreview, zoom, groupVisible],
  );

  /** Imported GeoTIFFs — no symbology or filter to carry, only opacity. */
  const localRasterLayers = useMemo(
    () =>
      layers
        .filter((layer) => layer.checked && groupVisible(layer) && layer.localRaster && isVisibleAtZoom(layer, zoom))
        .map((layer) => ({
          id: layer.id,
          base64: layer.localRaster!.base64,
          mimeType: layer.localRaster!.mimeType,
          opacity: layer.opacity,
          bounds: layer.bounds,
        })),
    [layers, zoom, groupVisible],
  );

  /** The Layers tab's own top-to-bottom order, for MapViewOL's `tocZIndex` —
   *  see its doc comment for why draw order otherwise drifts from this. */
  const layerOrder = useMemo(() => layers.map((layer) => layer.id), [layers]);

  /** Client-side label inputs for MapView: file-backed and ArcGIS vector layers. */
  const labelLayers = useMemo(
    () =>
      layers
        .filter((layer) => layer.checked && groupVisible(layer))
        .map((layer) => ({ layer, labeling: labelingFor(layer) }))
        .filter((entry) => labelingIsActive(entry.labeling))
        .filter((entry) => entry.layer.localData || entry.layer.arcgisLayerId !== undefined)
        .map((entry) => ({
          id: entry.layer.id,
          labeling: normaliseLabeling(entry.labeling, entry.layer.geometryType ?? 'unknown'),
          /* A label for a feature the filter removed would sit on empty
             ground, which reads as the filter half-working. */
          features: entry.layer.localData ? filteredLocalFeatures(entry.layer) : undefined,
          arcgisLayerId: entry.layer.arcgisLayerId,
        })),
    [layers, labelingFor, groupVisible],
  );

  /**
   * Pop-up configuration handed to the map, keyed the way the identify path can
   * find it again: a WMS layer by service name, an ArcGIS layer by its numeric
   * id. A layer with no configuration is simply absent, which the map reads as
   * "use the automatic pop-up".
   */
  const popupConfigs = useMemo(() => {
    const configs: Record<string, LayerPopup> = {};
    for (const layer of layers) {
      if (!layer.popup) continue;
      /* An imported layer is keyed by its id: it has no service name and no
         numeric layer id, and two files can share a name. */
      const key = layer.localData
        ? localPopupKey(layer.id)
        : layer.arcgisLayerId !== undefined
          ? arcgisPopupKey(layer.arcgisLayerId)
          : wmsPopupKey(layer.name);
      configs[key] = layer.popup;
    }
    return configs;
  }, [layers]);

  const symbologyLayer = layers.find((layer) => layer.id === symbologyLayerId) ?? null;
  const labelingLayer = layers.find((layer) => layer.id === labelingLayerId) ?? null;
  const popupLayer = layers.find((layer) => layer.id === popupLayerId) ?? null;

  const toggleLayer = (id: string) =>
    setLayers((prev: Layer[]) => prev.map((l: Layer) => (l.id === id ? { ...l, checked: !l.checked } : l)));

  const toggleExpand = (id: string) =>
    setLayers((prev: Layer[]) => prev.map((l: Layer) => (l.id === id ? { ...l, expanded: !l.expanded } : l)));

  /* ── Layer management operations ──────────────────────────────────────
   * Service-agnostic state operations. Anything needing a service call goes
   * through the adapter layer instead (see layers/adapters.ts). */

  const updateLayer = (layerId: string, changes: Partial<Layer>) =>
    setLayers((prev) => prev.map((layer) => (layer.id === layerId ? { ...layer, ...changes } : layer)));

  const removeLayer = (layerId: string) => {
    const target = layers.find((layer) => layer.id === layerId);
    setLayers((prev) => prev.filter((layer) => layer.id !== layerId));
    // Keep the service-layer selections in sync so the map stops drawing it.
    if (target?.arcgisLayerId !== undefined) {
      setArcgisLayers((prev) => prev.filter((id) => id !== target.arcgisLayerId));
    } else if (target) {
      setGeoServerLayers((prev) => prev.filter((name) => name !== target.name));
    }
  };

  const duplicateLayer = (layerId: string) => {
    const source = layers.find((layer) => layer.id === layerId);
    if (!source) return;
    const copy: Layer = {
      ...source,
      id: createId(source.arcgisLayerId !== undefined ? 'arcgis' : 'geoserver'),
      displayName: `${getLayerDisplayName(source)} (copy)`,
    };
    setLayers((prev) => {
      const index = prev.findIndex((layer) => layer.id === layerId);
      const next = [...prev];
      next.splice(index + 1, 0, copy);
      return next;
    });
  };

  /** Layer order drives draw order on the map. */
  const moveLayer = (layerId: string, direction: -1 | 1) =>
    setLayers((prev) => {
      const index = prev.findIndex((layer) => layer.id === layerId);
      if (index < 0) return prev;
      // Only ever swaps with another TOP-LEVEL entry. A grouped child can sit
      // anywhere in this flat array — it is rendered under its group, not at
      // its own array position — so swapping with whatever happens to be
      // numerically adjacent could silently reorder someone else's group
      // with nothing to show for it on screen.
      let target = index + direction;
      while (target >= 0 && target < prev.length && prev[target].groupId) {
        target += direction;
      }
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const addGeoServerLayer = (geoServerLayer: GeoServerLayer) => {
    if (geoServerLayers.includes(geoServerLayer.name)) return;
    const layerId = `geoserver_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    const newLayer: Layer = {
      id: layerId,
      name: geoServerLayer.name,
      sublayers: [],
      checked: true,
      expanded: false,
      bounds: geoServerLayer.bounds,
    };
    setLayers((prev) => [...prev, newLayer]);
    setGeoServerLayers((prev) => [...prev, geoServerLayer.name]);

    // WMS GetCapabilities does not publish geometry type; the matching WFS
    // DescribeFeatureType does. Fetched in the background so adding stays fast.
    void detectWfsGeometryType(geoServerUrl, geoServerLayer.name).then((geometryType) => {
      if (geometryType !== 'unknown') {
        setLayers((prev) =>
          prev.map((layer) => (layer.id === layerId ? { ...layer, geometryType } : layer)),
        );
      }
    });

    const layerView = getLayerZoom(geoServerLayer.bounds);
    if (layerView) {
      setCenter(layerView.center);
      setZoom(layerView.zoom);
    }
  };

  /**
   * Adds every discovered WMS layer that is not already in the map.
   *
   * Deliberately NOT a loop over `addGeoServerLayer`, for three reasons:
   *  - that function's id is `Date.now()` + random(1000); called in a tight
   *    loop the timestamp is identical, so ~12 layers carry a real chance of a
   *    duplicate layer id (and duplicate React keys). Here the index makes each
   *    id unique by construction.
   *  - its `geoServerLayers.includes()` guard reads the render-time closure,
   *    which does not update between iterations. The pending list is filtered
   *    once instead.
   *  - it zooms to each layer in turn, so the map would land on whichever layer
   *    happened to be last. This frames the union of everything added.
   *
   * The single-layer add path is untouched.
   */
  const addAllGeoServerLayers = () => {
    const pending = availableGeoServerLayers.filter(
      (layer) => !geoServerLayers.includes(layer.name),
    );
    if (pending.length === 0) return;

    const stamp = Date.now();
    const created = pending.map((source, index) => ({
      source,
      // Keeps the `geoserver_` prefix: removeGeoServerLayer and
      // visibleGeoServerLayers both key off it.
      layer: {
        id: `geoserver_${stamp}_${index}_${Math.floor(Math.random() * 1000)}`,
        name: source.name,
        sublayers: [],
        checked: true,
        expanded: false,
        bounds: source.bounds,
      } as Layer,
    }));

    // One state update rather than N, so the TOC renders the batch once.
    setLayers((prev) => [...prev, ...created.map((entry) => entry.layer)]);
    setGeoServerLayers((prev) => [...prev, ...pending.map((layer) => layer.name)]);

    // Same background geometry detection the single add performs.
    for (const { source, layer } of created) {
      void detectWfsGeometryType(geoServerUrl, source.name).then((geometryType) => {
        if (geometryType === 'unknown') return;
        setLayers((prev) =>
          prev.map((entry) => (entry.id === layer.id ? { ...entry, geometryType } : entry)),
        );
      });
    }

    // Frame everything that was just added, not merely the last one.
    const layerView = getLayerZoom(unionBounds(created.map((entry) => entry.layer.bounds)));
    if (layerView) {
      setCenter(layerView.center);
      setZoom(layerView.zoom);
    }
  };

  /**
   * Adds the layers parsed from a local file. The features travel on the layer
   * itself (`localData`), so each one renders, identifies, and styles through
   * the same client-side vector path as an ArcGIS FeatureServer layer.
   *
   * One file can yield several layers — a KML's folders each become their own
   * layer — so this takes a list and zooms to the union of what was added.
   */
  const addLocalLayer = (parsedLayers: ParsedLocalLayer[]) => {
    if (parsedLayers.length === 0) return;

    // Index the id so a batch added inside the same millisecond can't collide.
    const stamp = createId('local');
    const newLayers: Layer[] = parsedLayers.map((parsed, index) => ({
      id: `${stamp}_${index}`,
      name: parsed.name,
      sublayers: [],
      checked: true,
      expanded: false,
      bounds: parsed.bounds ?? computeBounds(parsed.data),
      // A GeoTIFF carries no features at all (see localFile.ts's `fromGeoTiff`
      // — `parsed.data` is a dummy empty collection for it), so it gets
      // `localRaster` instead of `localData`, never both.
      ...(parsed.raster
        ? { localRaster: parsed.raster }
        : { localData: parsed.data, geometryType: parsed.geometryType }),
      // Symbology read from the source file (KML <Style>), so the layer looks
      // the way it did in the authoring tool instead of falling back to grey.
      renderer: parsed.renderer,
    }));
    setLayers((prev) => [...prev, ...newLayers]);

    // Union extent, so adding five folders frames all five rather than the last.
    const extents = newLayers
      .map((layer) => layer.bounds)
      .filter((bounds): bounds is [number, number, number, number] => Boolean(bounds));
    if (extents.length === 0) return;
    const union: [number, number, number, number] = [
      Math.min(...extents.map((b) => b[0])),
      Math.min(...extents.map((b) => b[1])),
      Math.max(...extents.map((b) => b[2])),
      Math.max(...extents.map((b) => b[3])),
    ];

    const layerView = getLayerZoom(union);
    if (layerView) {
      setCenter(layerView.center);
      setZoom(layerView.zoom);
    }
  };

  /**
   * Copies a layer fetched from elsewhere — one of the builder's own OTHER
   * applications, or one another owner shared directly — into this map.
   *
   * A NEW id, always: the fetched layer still carries the id it has in its
   * source module, and reusing that verbatim would collide the moment two
   * apps both held "the same" layer, breaking every id-keyed lookup (TOC
   * selection, symbology, actions) that assumes a layer's id is unique to
   * this map. Everything else — including a file-backed layer's own features
   * — comes across exactly as fetched: see `layerLibraryService.fetchLayer`,
   * which always reads the FULL document, never the reduced module-summary
   * shape used just to list candidates.
   */
  const addExistingLayer = (layer: Layer) => {
    const newLayer: Layer = { ...layer, id: createId('layer') };
    setLayers((prev) => [...prev, newLayer]);

    if (newLayer.bounds) {
      const layerView = getLayerZoom(newLayer.bounds);
      if (layerView) {
        setCenter(layerView.center);
        setZoom(layerView.zoom);
      }
    }
  };

  const removeGeoServerLayer = (layerName: string) => {
    setLayers((prev) =>
      prev.filter((layer) => !(layer.id.startsWith('geoserver_') && layer.name === layerName)),
    );
    setGeoServerLayers((prev) => prev.filter((name) => name !== layerName));
  };

  /**
   * `serviceUrl`, when given, is a SECONDARY ArcGIS service — a different
   * one than the module's primary `arcgisUrl`, resolved via "Add layers ›
   * Saved connection" while the module already has layers from another
   * service. The new layer then carries its own `arcgisServiceUrl` instead
   * of joining the shared `arcgisLayers` id list, which only ever means
   * "added from the PRIMARY service" — two different services can validly
   * reuse the same sub-layer id, so mixing them into one flat list would
   * make "already added" ambiguous. See `arcgisServiceUrlFor`.
   */
  const addArcgisLayer = async (arcgisLayer: ArcgisLayer, serviceUrl?: string) => {
    const isSecondary = Boolean(serviceUrl);
    if (isSecondary ? secondaryArcgisLayerIds(serviceUrl!).has(arcgisLayer.id) : arcgisLayers.includes(arcgisLayer.id)) {
      return;
    }

    const layerId = `arcgis_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    const newLayer: Layer = {
      id: layerId,
      name: arcgisLayer.name,
      sublayers: [],
      checked: true,
      expanded: false,
      bounds: arcgisLayer.bounds,
      arcgisLayerId: arcgisLayer.id,
      ...(isSecondary ? { arcgisServiceUrl: serviceUrl } : {}),
      // Captured from the service so symbology can pick the right editor.
      geometryType: normaliseGeometryType(arcgisLayer.geometryType),
      minScale: arcgisLayer.minScale,
      maxScale: arcgisLayer.maxScale,
    };
    setLayers((prev) => [...prev, newLayer]);
    if (!isSecondary) setArcgisLayers((prev) => [...prev, arcgisLayer.id]);

    // The service extent covers every layer and is often wrong; ask the layer for the
    // real extent of its features, already projected to WGS84 by the server.
    let bounds = arcgisLayer.bounds;
    const effectiveUrl = serviceUrl ?? arcgisUrl;
    const baseUrl = getArcgisBaseUrl(effectiveUrl);
    if (baseUrl) {
      try {
        const params = new URLSearchParams({
          where: '1=1',
          returnExtentOnly: 'true',
          outSR: '4326',
          f: 'json',
        });
        const response = await fetch(withArcgisToken(`${baseUrl}/${arcgisLayer.id}/query?${params.toString()}`, effectiveUrl));
        if (response.ok) {
          const data = await response.json();
          const layerBounds = getArcgisExtentBounds(data?.extent);
          if (layerBounds) {
            bounds = layerBounds;
            setLayers((prev) =>
              prev.map((layer) => (layer.id === layerId ? { ...layer, bounds: layerBounds } : layer)),
            );
          }
        }
      } catch {
        // Fall back to the service extent already stored on the layer.
      }
    }

    /* Framed inside the layer's scale band, not merely on its extent — see
       getLayerView. Landing on the extent alone is what made a scale-banded
       service add a layer and then show an empty map. */
    const layerView = getLayerView({
      bounds,
      minScale: arcgisLayer.minScale,
      maxScale: arcgisLayer.maxScale,
    });
    if (layerView) {
      setCenter(layerView.center);
      setZoom(layerView.zoom);
    }
  };

  /**
   * Adds every discovered ArcGIS layer that is not already in the map.
   *
   * The ArcGIS twin of addAllGeoServerLayers, and NOT a loop over
   * `addArcgisLayer`, for the same three reasons:
   *  - that function's id is `Date.now()` + random(1000); called in a tight
   *    loop the timestamp is identical, so a service with a dozen layers
   *    carries a real chance of a duplicate layer id (and React key). Here the
   *    index makes each id unique by construction.
   *  - its `arcgisLayers.includes()` guard reads the render-time closure, which
   *    does not update between iterations. The pending list is filtered once.
   *  - it zooms to each layer in turn, so the map would land on whichever
   *    extent request happened to resolve last. This frames the union.
   *
   * The single-layer add path is untouched. `serviceUrl`: see `addArcgisLayer`'s
   * own doc on the same parameter.
   */
  const addAllArcgisLayers = async (serviceUrl?: string) => {
    const isSecondary = Boolean(serviceUrl);
    const alreadyAdded = isSecondary ? secondaryArcgisLayerIds(serviceUrl!) : new Set(arcgisLayers);
    const pending = availableArcgisLayers.filter((entry) => !alreadyAdded.has(entry.id));
    if (pending.length === 0) return;

    const stamp = Date.now();
    const created = pending.map((source, index) => ({
      source,
      // Keeps the `arcgis_` prefix and the arcgisLayerId that
      // removeArcgisLayer and visibleArcgisLayers key off.
      layer: {
        id: `arcgis_${stamp}_${index}_${Math.floor(Math.random() * 1000)}`,
        name: source.name,
        sublayers: [],
        checked: true,
        expanded: false,
        bounds: source.bounds,
        arcgisLayerId: source.id,
        ...(isSecondary ? { arcgisServiceUrl: serviceUrl } : {}),
        geometryType: normaliseGeometryType(source.geometryType),
        minScale: source.minScale,
        maxScale: source.maxScale,
      } as Layer,
    }));

    // One state update rather than N, so the TOC renders the batch once.
    setLayers((prev) => [...prev, ...created.map((entry) => entry.layer)]);
    if (!isSecondary) setArcgisLayers((prev) => [...prev, ...pending.map((entry) => entry.id)]);

    // The same extent refinement the single add performs: the service extent
    // covers every layer and is often far wider than the layer's own features.
    // Fired in parallel, so a service with many layers is not serialised.
    const effectiveUrl = serviceUrl ?? arcgisUrl;
    const baseUrl = getArcgisBaseUrl(effectiveUrl);
    const framed = await Promise.all(
      created.map(async ({ source, layer }) => {
        if (!baseUrl) return layer.bounds;
        try {
          const params = new URLSearchParams({
            where: '1=1',
            returnExtentOnly: 'true',
            outSR: '4326',
            f: 'json',
          });
          const response = await fetch(withArcgisToken(`${baseUrl}/${source.id}/query?${params.toString()}`, effectiveUrl));
          if (!response.ok) return layer.bounds;
          const data = await response.json();
          const layerBounds = getArcgisExtentBounds(data?.extent);
          if (!layerBounds) return layer.bounds;
          setLayers((prev) =>
            prev.map((entry) =>
              entry.id === layer.id ? { ...entry, bounds: layerBounds } : entry,
            ),
          );
          return layerBounds;
        } catch {
          // Fall back to the service extent already stored on the layer.
          return layer.bounds;
        }
      }),
    );

    /* One zoom cannot satisfy layers from different scale bands, so this takes
       the LEAST restrictive floor: whatever draws furthest out gets shown, and
       the TOC marks the rest as out of range — better than silently framing a
       zoom where nothing at all draws. */
    const floor = Math.min(...created.map((entry) => minZoomForBand(entry.layer.minScale)));
    const layerView = getLayerZoom(unionBounds(framed));
    if (layerView) {
      setCenter(layerView.center);
      setZoom(Math.min(22, Math.max(layerView.zoom, floor)));
    }
  };

  /** `serviceUrl`: see `addArcgisLayer`'s own doc on the same parameter. */
  const removeArcgisLayer = (arcgisLayerId: number, serviceUrl?: string) => {
    if (serviceUrl) {
      const base = getArcgisBaseUrl(serviceUrl);
      setLayers((prev) =>
        prev.filter(
          (layer) =>
            !(layer.arcgisServiceUrl && getArcgisBaseUrl(layer.arcgisServiceUrl) === base && layer.arcgisLayerId === arcgisLayerId),
        ),
      );
      return;
    }
    // Only a PRIMARY-service layer with this id — never a secondary one that
    // happens to reuse the same numeric sub-layer id on a different service.
    setLayers((prev) => prev.filter((layer) => !(layer.arcgisLayerId === arcgisLayerId && !layer.arcgisServiceUrl)));
    setArcgisLayers((prev) => prev.filter((id) => id !== arcgisLayerId));
  };

  /** Every sub-layer id already added from ONE specific secondary ArcGIS
   *  service (by its base URL, token/credentials ignored) — derived from
   *  `layers` itself rather than a flat cache, since a flat id list cannot
   *  tell two different services' layer #3 apart. */
  const secondaryArcgisLayerIds = (serviceUrl: string): Set<number> => {
    const base = getArcgisBaseUrl(serviceUrl);
    const ids = new Set<number>();
    for (const layer of layers) {
      if (
        layer.arcgisServiceUrl &&
        layer.arcgisLayerId !== undefined &&
        getArcgisBaseUrl(layer.arcgisServiceUrl) === base
      ) {
        ids.add(layer.arcgisLayerId);
      }
    }
    return ids;
  };

  /**
   * `urlOverride` is only used right after "Connect & load layers" resolves
   * a saved connection: `setArcgisUrl` has just been called but React state
   * updates are not visible in this same synchronous flow, so reading
   * `arcgisUrl` from closure here would still see the URL from before the
   * connection was picked. Passing the resolved URL straight through avoids
   * that one-render staleness without waiting on an effect.
   */
  const loadArcgisLayers = async (urlOverride?: string) => {
    const baseUrl = getArcgisBaseUrl(urlOverride ?? arcgisUrl);
    if (!baseUrl) {
      setArcgisError('Enter a valid ArcGIS REST service URL');
      return;
    }

    setArcgisLoading(true);
    setArcgisError(null);
    setAvailableArcgisLayers([]);

    try {
      const response = await fetch(withArcgisToken(`${baseUrl}?f=json`, urlOverride ?? arcgisUrl));
      if (!response.ok) {
        throw new Error(`ArcGIS service returned ${response.status}`);
      }

      const data = await response.json();
      // ArcGIS reports failures as HTTP 200 with an `error` object in the body.
      if (data?.error) {
        throw new Error(data.error.message || 'ArcGIS service returned an error');
      }

      // Service-wide extent, used as a fallback until the per-layer extent loads.
      const serviceBounds = getArcgisExtentBounds(data?.fullExtent ?? data?.initialExtent);

      const rawLayers: any[] = Array.isArray(data?.layers) ? data.layers : [];
      const byId: Record<number, any> = {};
      for (const layer of rawLayers) {
        if (Number.isFinite(layer?.id)) byId[Number(layer.id)] = layer;
      }

      /* A layer inherits its ancestors' scale ranges, and only the ancestors
         may carry one. Every "_1K" layer in Esri's Water_Network_Base_Map
         publishes minScale 0 and is still invisible above 1:1,950, because the
         group holding them is what carries the limit — so reading the layer's
         own metadata gives no hint about why nothing draws. */
      const resolveScaleBand = (layer: any) => {
        let minScale = 0;
        let maxScale = 0;
        let current: any = layer;
        // Guards against a service that reports a parent cycle.
        const seen = new Set<number>();
        while (current && !seen.has(Number(current.id))) {
          seen.add(Number(current.id));
          const min = Number(current.minScale) || 0;
          const max = Number(current.maxScale) || 0;
          // Most restrictive ancestor wins at each end.
          if (min && (!minScale || min < minScale)) minScale = min;
          if (max && max > maxScale) maxScale = max;
          const parentId = Number(current.parentLayerId);
          current = Number.isFinite(parentId) && parentId >= 0 ? byId[parentId] : undefined;
        }
        return { minScale, maxScale };
      };

      const serviceLayers: ArcgisLayer[] = rawLayers
        .filter((layer: any) => Number.isFinite(layer?.id))
        /* Group layers are containers, not data. `query` fails on them, so a
           popup, an attribute table and symbology would all be dead ends, and
           the app has no group concept to render them as. Their children are
           listed individually right below them anyway. */
        .filter((layer: any) => !(Array.isArray(layer?.subLayerIds) && layer.subLayerIds.length > 0))
        .map((layer: any) => ({
          id: Number(layer.id),
          name: layer.name || `Layer ${layer.id}`,
          bounds: serviceBounds,
          geometryType: layer.geometryType,
          ...resolveScaleBand(layer),
        }));

      if (serviceLayers.length === 0) {
        throw new Error('No layers found in this ArcGIS service');
      }

      setAvailableArcgisLayers(serviceLayers);
    } catch (error: any) {
      setArcgisError(error?.message || 'Unable to load ArcGIS layers');
    } finally {
      setArcgisLoading(false);
    }
  };

  /** See `loadArcgisLayers`'s note on `urlOverride` — same staleness reason. */
  const loadGeoServerLayers = async (urlOverride?: string) => {
    const trimmed = (urlOverride ?? geoServerUrl).trim();
    if (!trimmed) {
      setGeoServerError('Enter a GeoServer WMS URL');
      return;
    }

    setGeoServerLoading(true);
    setGeoServerError(null);
    setAvailableGeoServerLayers([]);

    try {
      const url = new URL(trimmed);
      url.searchParams.set('service', 'WMS');
      url.searchParams.set('request', 'GetCapabilities');
      url.searchParams.set('version', '1.3.0');

      const response = await fetch(url.toString());
      if (!response.ok) {
        throw new Error(`GeoServer returned ${response.status}`);
      }

      const text = await response.text();
      const parser = new DOMParser();
      const xml = parser.parseFromString(text, 'application/xml');
      const serviceLayers = Array.from(xml.getElementsByTagName('Layer'))
        .filter((layer) => layer.getElementsByTagName('Layer').length === 0)
        .map((layer) => {
          const getChildText = (tagName: string) =>
            Array.from(layer.children).find(
              (child) => child.tagName.toLowerCase() === tagName.toLowerCase(),
            )?.textContent?.trim();
          const name = getChildText('name');
          if (!name || name.toLowerCase() === 'wms') return null;

          const geographicBounds = Array.from(layer.children).find(
            (child) => child.tagName.toLowerCase() === 'ex_geographicboundingbox',
          );
          const west = Number(geographicBounds?.getElementsByTagName('westBoundLongitude')[0]?.textContent);
          const south = Number(geographicBounds?.getElementsByTagName('southBoundLatitude')[0]?.textContent);
          const east = Number(geographicBounds?.getElementsByTagName('eastBoundLongitude')[0]?.textContent);
          const north = Number(geographicBounds?.getElementsByTagName('northBoundLatitude')[0]?.textContent);
          const hasBounds = [west, south, east, north].every(Number.isFinite);

          return {
            name,
            title: getChildText('title') || name,
            ...(hasBounds ? { bounds: [west, south, east, north] as [number, number, number, number] } : {}),
          };
        })
        .filter((layer): layer is GeoServerLayer => layer !== null);
      const uniqueLayers = Array.from(
        new globalThis.Map(serviceLayers.map((layer) => [layer.name, layer])).values(),
      );

      if (uniqueLayers.length === 0) {
        throw new Error('No layers found in GetCapabilities response');
      }

      setAvailableGeoServerLayers(uniqueLayers);
    } catch (error: any) {
      setGeoServerError(error?.message || 'Unable to load GeoServer layers');
    } finally {
      setGeoServerLoading(false);
    }
  };

  /**
   * GeoServer only now — ArcGIS layers from a different service are added
   * ALONGSIDE existing ones instead (`secondaryArcgisServiceUrl`, see
   * `selectConnectionService`), so switching never orphans an ArcGIS layer
   * any more. GeoServer has no equivalent yet: `module.geoServerUrl` is
   * still one string for the whole module, so switching it silently leaves
   * every earlier GeoServer layer querying the wrong server — this asks
   * before doing that rather than doing it by surprise.
   *
   * `currentUrl`/`nextUrl` are compared directly (trimmed): a GeoServer
   * connection's embedded credentials are the same on every resolve, unlike
   * an ArcGIS token, which is why this needs no `getArcgisBaseUrl`-style
   * normalisation the way the ArcGIS path used to.
   */
  const confirmServiceSwitch = (
    kind: 'GeoServer',
    currentUrl: string,
    nextUrl: string,
    existingLayerCount: number,
  ): boolean => {
    if (existingLayerCount === 0 || !currentUrl) return true;
    if (currentUrl.trim() === nextUrl.trim()) return true;
    const plural = existingLayerCount === 1 ? '' : 's';
    return window.confirm(
      `This map already has ${existingLayerCount} layer${plural} from a different ${kind} service. ` +
        `BlueView can only connect one ${kind} service per map — continuing will switch to the new one, ` +
        `and the existing layer${plural} will stop working until removed or re-added from it.\n\n` +
        `Continue anyway?`,
    );
  };

  /**
   * "Connect" for a saved connection.
   *
   * GeoServer: resolves straight to an authenticated URL (`user:pass@host/
   * ...`) — one WMS endpoint already lists every layer it publishes — saves
   * THAT as the module's own `geoServerUrl` exactly as a manually-typed one
   * would be, then runs the very same discovery fetch. Everything
   * downstream (rendering, query, WFS, the attribute table) already builds
   * its requests from `geoServerUrl` alone and never strips embedded
   * userinfo, so nothing else in the app needs to know this layer came from
   * a connection rather than a typed-in URL.
   *
   * ArcGIS Server: a connection may serve many MapServer/FeatureServer
   * services, so there is no one URL to load layers FROM yet — this starts
   * browsing its services from the root instead; picking one (below) is
   * what actually resolves and loads layers, the same way manual entry
   * only starts working once a real service URL is typed in.
   */
  const connectSelectedConnection = async () => {
    if (!selectedConnectionId) return;
    if (serviceType === 'arcgis') {
      await browseConnection('');
      return;
    }
    setConnecting(true);
    setConnectError(null);
    try {
      const { authenticatedBaseUrl } = await serverConnectionsService.resolve(selectedConnectionId);
      if (!confirmServiceSwitch('GeoServer', geoServerUrl, authenticatedBaseUrl, geoServerLayers.length)) {
        return;
      }
      setGeoServerUrl(authenticatedBaseUrl);
      await loadGeoServerLayers(authenticatedBaseUrl);
    } catch (error) {
      setConnectError(error instanceof Error ? error.message : 'Could not connect to this server.');
    } finally {
      setConnecting(false);
    }
  };

  /** Browses one folder of the selected ArcGIS Server connection. */
  const browseConnection = async (path: string) => {
    if (!selectedConnectionId) return;
    setConnectionBrowseLoading(true);
    setConnectionBrowseError(null);
    try {
      const result = await serverConnectionsService.browse(selectedConnectionId, path);
      setConnectionBrowse(result);
    } catch (error) {
      setConnectionBrowseError(error instanceof Error ? error.message : 'Could not browse this server.');
    } finally {
      setConnectionBrowseLoading(false);
    }
  };

  /** Picks one service found while browsing — resolves it (credentials
   *  embedded) and loads its layers, same as `connectSelectedConnection`
   *  does directly for GeoServer. */
  const selectConnectionService = async (service: ServerConnectionService) => {
    if (!selectedConnectionId) return;
    setConnecting(true);
    setConnectError(null);
    try {
      const { authenticatedBaseUrl, expiresAt } = await serverConnectionsService.resolveService(
        selectedConnectionId,
        service,
      );
      // A DIFFERENT service than the module's primary one, with the primary
      // already carrying layers, is added ALONGSIDE it rather than replacing
      // it — see `secondaryArcgisServiceUrl` and `addArcgisLayer`'s own doc.
      // An empty primary, or picking the SAME service again, is the plain
      // case every layer worked as before this existed.
      const isSecondary =
        arcgisLayers.length > 0 && arcgisUrl && getArcgisBaseUrl(authenticatedBaseUrl) !== getArcgisBaseUrl(arcgisUrl);
      if (isSecondary) {
        setSecondaryArcgisServiceUrl(authenticatedBaseUrl);
      } else {
        setSecondaryArcgisServiceUrl(null);
        setArcgisUrl(authenticatedBaseUrl);
        // Remembered so the token can be silently renewed later without
        // anyone re-picking this service — see the renewal effect below.
        // (Renewal today only covers the PRIMARY service — see its own note.)
        setArcgisConnectionId(selectedConnectionId);
        setArcgisConnectionService(service.name ? service : null);
        setArcgisTokenExpiresAt(expiresAt ?? null);
      }
      await loadArcgisLayers(authenticatedBaseUrl);
      // Browsing is done — the results list below now shows this service's
      // layers, the same view a manually-typed URL would land on.
      setConnectionBrowse(null);
    } catch (error) {
      setConnectError(error instanceof Error ? error.message : 'Could not connect to this service.');
    } finally {
      setConnecting(false);
    }
  };

  /**
   * Finds the browser's own reported position and centres the map there.
   *
   * This used to just flip `locating` on and off on a bare 1200ms timer —
   * the button looked alive but never actually asked the browser for
   * anything, so it always landed back on whatever the map was already
   * showing. This is the real thing: `navigator.geolocation`, a marker at the
   * result (the same one a place search drops), and an error message read
   * from the actual failure rather than silence.
   */
  const doLocate = () => {
    if (!('geolocation' in navigator)) {
      setLocateError('This browser cannot report your location.');
      return;
    }
    setLocateError(null);
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(false);
        const { latitude, longitude } = position.coords;
        setCenter([latitude, longitude]);
        // Zooms IN to a useful street-level view, but never zooms out — someone
        // already looking closer than that should stay exactly where they are.
        setZoom((current) => Math.max(current, 16));
        setSearchMarker({ latitude, longitude, label: 'Your location' });
      },
      (error) => {
        setLocating(false);
        setLocateError(
          error.code === error.PERMISSION_DENIED
            ? "Location access was denied. Allow it for this site in the browser's settings and try again."
            : error.code === error.TIMEOUT
              ? 'Finding your location took too long. Try again.'
              : 'Your location could not be determined.',
        );
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  };


  const basemapStyles: Record<'street' | 'terrain' | 'satellite', { bg: string; road: string; block: string }> = {
    street: { bg: '#e5f2fc', road: '#b5d6f4', block: '#d3e9fb' },
    terrain: { bg: '#edf3ea', road: '#c4d6c4', block: '#dbe3d3' },
    satellite: { bg: '#1e2d40', road: '#3c557a', block: '#2c4063' },
  };
  const bm = basemapStyles[basemap];

  // `colorTheme.base` wins over the toolbar's Auto/Dark toggle once an admin
  // sets it from Header settings; see AppModule.colorTheme's doc.
  const builderEffectiveDark = colorTheme?.base ? colorTheme.base === 'dark' : appearance === 'dark';

  return (
    // Scoped here so the builder shows the application's own scheme while it is
    // being configured, rather than only after publishing.
    <div
      data-theme={builderEffectiveDark ? 'dark' : undefined}
      style={colorThemeVars(colorTheme)}
      className="h-screen bg-slate-50 flex flex-col overflow-hidden"
    >
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 flex-shrink-0">
        <button
          onClick={onBack}
          className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
          aria-label="Back to modules"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 flex items-center gap-2 max-w-md">
          <span className="text-xs font-medium text-slate-400 whitespace-nowrap">Map title</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="flex-1 text-sm font-medium text-slate-800 border border-transparent hover:border-slate-200 focus:border-blue-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-100 bg-slate-50 focus:bg-white transition"
          />
        </div>
        <div className="flex-1" />
        {/* Same three actions as the dashboard builder, in the same order, so
            the two builders behave alike. Preview renders the REAL end-user
            viewer against the unsaved draft — not a mock — so what is checked
            is what will ship. */}
        <div className="flex items-center gap-2">
          {/* Per-application theme. Separate from the viewer's own Appearance
              setting: this travels WITH the application, so one map can be dark
              on a portal everyone else uses in light. */}
          <div className="mr-1 flex items-center gap-1 rounded-lg bg-slate-100 p-0.5">
            {([
              { id: 'inherit' as const, label: 'Auto', hint: "Follows each viewer's own Appearance setting." },
              { id: 'dark' as const, label: 'Dark', hint: 'This application always opens dark, for everyone.' },
            ]).map((option) => (
              <Tooltip key={option.id} content={option.hint}>
                <button
                  type="button"
                  aria-pressed={appearance === option.id}
                  onClick={() => setAppearance(option.id)}
                  className={`rounded-md px-2 py-1 text-[11px] font-medium transition ${
                    appearance === option.id
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {option.label}
                </button>
              </Tooltip>
            ))}
          </div>
          <Tooltip content="Open this map as an end user would see it, without saving.">
            <button
              type="button"
              onClick={() => setPreviewing(true)}
              className="gl-btn gl-btn--secondary px-3 py-2 text-sm"
            >
              <Eye className="w-4 h-4" />
              Preview
            </button>
          </Tooltip>
          <Tooltip content="Save changes and stay in the builder. The map view on screen becomes this app's home extent.">
            <button
              type="button"
              onClick={() => {
                saveDraft({ launch: false });
                setSavedNote(Date.now());
              }}
              className="gl-btn gl-btn--secondary px-3 py-2 text-sm"
            >
              <Save className="w-4 h-4" />
              Save
            </button>
          </Tooltip>
          <Tooltip content="Save, publish, and open the live application. The map view on screen becomes this app's home extent.">
            <button
              type="button"
              onClick={() => saveDraft()}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
            >
              <Rocket className="w-4 h-4" />
              Save &amp; launch
            </button>
          </Tooltip>
        </div>
      </div>

      {savedNote !== null && (
        <div
          role="status"
          className="absolute right-4 top-16 z-[9000] rounded-lg bg-slate-900/90 px-3 py-2 text-xs font-medium text-white shadow-lg"
        >
          Saved
        </div>
      )}

      {previewing && (
        <div className="fixed inset-0 z-[9500] flex flex-col bg-slate-100">
          <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-white px-5 py-3">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-600">
                Preview — not yet published
              </p>
              <h2 className="truncate text-base font-semibold text-slate-900">{title}</h2>
            </div>
            <button
              type="button"
              onClick={() => setPreviewing(false)}
              className="gl-btn gl-btn--secondary px-3 py-2 text-sm"
            >
              <X className="w-4 h-4" />
              Close preview
            </button>
          </div>
          <div className="min-h-0 flex-1">
            <EndUserViewer module={draftModule()} onExitPreview={() => setPreviewing(false)} />
          </div>
        </div>
      )}

      {/* The application's REAL header, in the builder. Clicking it opens the
          properties panel, the same gesture that opens Symbology from a layer,
          so the thing being designed is the thing being clicked. */}
      <AppHeader
        title={title}
        theme={headerTheme}
        editing={showHeaderDesigner}
        onEdit={() => setShowHeaderDesigner((current) => !current)}
      />

      <div className="flex flex-1 min-w-0 overflow-hidden">
        <div className="w-72 bg-white border-r border-slate-200 flex flex-col min-w-[18rem] h-full overflow-hidden">
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
            <span className="text-sm font-semibold text-slate-700">Layers</span>
            {/* Both routes open the same dialog; see AddLayersDialog. */}
            <AddLayersButton
              defaultMode={addMode}
              onOpen={(mode) => {
                setAddMode(mode);
                setAddOpen(true);
              }}
            />
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto py-2">
            <TableOfContents
              layers={layers}
              geoServerUrl={geoServerUrl}
              arcgisUrl={arcgisUrl}
              toolConfigs={toolConfigs}
              basemap={basemap}
              onBasemapChange={setBasemap}
              pointer={toolPoint}
              onOverlayChange={setToolOverlay}
              onCreateTempLayer={addTemporaryLayer}
              onCreateTempRasterLayer={addTemporaryRasterLayer}
              onCreateGroup={createLayerGroup}
              onSwipeChange={setSwipe}
              swipeLayerId={swipe.layerId}
              swipeVertical={swipe.vertical}
              onAddData={() => {
                setAddMode('server');
                setAddOpen(true);
              }}
              module={{ ...module, geoServerUrl, arcgisUrl, layers }}
              onLayerChange={updateLayer}
              onLayerRemove={removeLayer}
              onLayerDuplicate={duplicateLayer}
              onLayerMove={moveLayer}
              onLayerRefresh={() => setLayerRefreshToken((token) => token + 1)}
              onOpenLabeling={(layerId) => {
                // All three panels occupy the same right-hand slot, so opening
                // one closes the others rather than stacking them.
                setSymbologyLayerId(null);
                setRendererPreview(null);
                setPopupLayerId(null);
                setLabelingLayerId(layerId);
              }}
              onOpenSymbology={(layerId) => {
                setLabelingLayerId(null);
                setLabelingPreview(null);
                setRendererPreview(null);
                setPopupLayerId(null);
                setSymbologyLayerId(layerId);
              }}
              onOpenPopup={(layerId) => {
                setSymbologyLayerId(null);
                setRendererPreview(null);
                setLabelingLayerId(null);
                setLabelingPreview(null);
                setPopupLayerId(layerId);
              }}
              // One window at a time: opening another layer's table swaps the
              // content in place rather than spawning duplicates.
              onOpenAttributeTable={setAttributeLayer}
              onToggleLayer={toggleLayer}
              onToggleExpand={toggleExpand}
              onZoomToLayer={(layer) => {
                // getLayerView, not getLayerZoom: a Zoom to that framed the
                // extent and left the layer invisible would be a quiet lie.
                const layerView = getLayerView(layer);
                if (layerView) {
                  setCenter(layerView.center);
                  setZoom(layerView.zoom);
                }
              }}
              onZoomToSelection={zoomToSelection}
              drawType={drawType}
              onDrawTypeChange={setDrawType}
              onStartDrawing={() => setIsDrawing(true)}
              onStopDrawing={() => setIsDrawing(false)}
              onMeasureModeChange={handleMeasureMode}
              onSelectModeChange={(mode, layerIds, combine) => {
                setSelectSketchMode(mode);
                selectLayersRef.current = layerIds ?? [];
                selectCombineRef.current = combine ?? 'new';
              }}
              onPrintFrameRatioChange={setPrintFrameRatio}
              selectedFeatures={selectHits}
              onMeasurementsChange={setMeasureShapes}
              onSelectionChange={trackSelection}
              getSelection={readSelection}
              measuredFeature={measuredFeature}
              sketchVertices={sketchVertices}
              onFinishShape={() => setDrawFinishToken((count) => count + 1)}
              onEditModeChange={setEditCreateShape}
              editSketchResult={editSketchResult}
              onDraftGeometryChange={setDraftGeometry}
              onMoveGeometryChange={setMoveGeometry}
              moveResult={moveResult}
              snapEnabled={snapEnabled}
              onSnapEnabledChange={setSnapEnabled}
              onClearAll={() => {
                clearDrawings();
                setIsDrawing(false);
              }}
              onDeleteDrawnFeature={deleteDrawnFeature}
              onZoomToDrawnFeature={zoomToDrawnFeature}
              onSaveDrawings={saveDrawings}
              onCloseDrawPanel={() => setIsDrawing(false)}
              isDrawing={isDrawing}
              mapCenter={center}
              mapZoom={zoom}
              onGoToLocation={(nextCenter, nextZoom) => {
                setCenter(nextCenter);
                setZoom(nextZoom);
              }}
              tools={tools}
              openTools={builderOpenTools}
              activeToolTab={builderActiveTool ?? undefined}
              onToolTabChange={(tab) => setBuilderActiveTool(tab)}
              onToolClose={(toolId) => {
                setBuilderOpenTools((current) => current.filter((id) => id !== toolId));
                setBuilderActiveTool((current) => (current === toolId ? null : current));
              }}
              onToolOpen={openToolTab}
            />
          </div>
        </div>

        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* The tool strip replaces the old chip row: the same icons the end
              user will see, in the same place, so the builder is a preview of
              the real thing rather than a separate editing widget. */}
          <ToolStrip
            open={toolsOpen}
            toolIds={tools}
            activeToolId={builderActiveTool}
            onOpenTool={openToolTab}
            onAddTool={() => {
              setEditToolId(null);
              setShowToolDialog(true);
            }}
            onEditTool={(toolId) => {
              setEditToolId(toolId);
              setShowToolDialog(true);
            }}
          />

          <AddToolDialog
            open={showToolDialog}
            initialToolId={editToolId}
            onClose={() => {
              setShowToolDialog(false);
              setEditToolId(null);
            }}
            enabledToolIds={tools}
            toolConfigs={toolConfigs}
            layers={layers}
            fieldsByLayer={toolFieldsByLayer}
            onAdd={(toolId, config) => {
              setTools((current) => (current.includes(toolId) ? current : [...current, toolId]));
              setToolConfigs((current) => ({ ...current, [toolId]: config }));
            }}
            onRemove={(toolId) => {
              setTools((current) => current.filter((id) => id !== toolId));
              // Settings are kept: re-adding the tool should not lose its setup.
            }}
          />

          <div className="flex-1 min-h-0 min-w-0 relative overflow-hidden bg-slate-100">
          {/* Map's own box — shrinks to make room for a DOCKED attribute
              table (see FloatingAttributeTable's own `dock` doc comment).
              A plain CSS size change, not a flex restructure: the table
              itself stays a DIRECT child of the stable outer div below
              (never moves in the tree), so toggling dock never unmounts
              it — losing its loaded rows/selection/in-progress edit on
              every click would make "dock" feel like "close and reopen". */}
          <div
            className="absolute inset-0"
            style={{
              width: attributeLayer && attributeDock === 'right' ? `${(1 - attributeDockSize) * 100}%` : '100%',
              height: attributeLayer && attributeDock === 'bottom' ? `${(1 - attributeDockSize) * 100}%` : '100%',
            }}
          >
            <MapViewOL
              // Changes whenever the docked panel appears/disappears/swaps
              // sides/is resized, so OL re-measures its own canvas against
              // this box's new (possibly smaller) size.
              containerResizeToken={
                attributeLayer && attributeDock !== 'float' ? `${attributeDock}:${attributeDockSize}` : 'float'
              }
              basemap={basemap}
              center={center}
              zoom={zoom}
              geoServerUrl={geoServerUrl}
              geoServerLayers={visibleGeoServerLayers}
              arcgisUrl={arcgisUrl}
              arcgisLayers={visibleArcgisLayers}
              arcgisLayerStyles={arcgisLayerStyles}
              arcgisSecondaryLayers={arcgisSecondaryLayers}
              geoServerLayerStyles={geoServerLayerStyles}
              localLayers={localLayers}
              localRasterLayers={localRasterLayers}
              layerOrder={layerOrder}
              labelLayers={labelLayers}
              popupConfigs={popupConfigs}
              searchMarker={searchMarker}
              onMapPoint={setToolPoint}
              toolOverlay={toolOverlay}
              swipe={swipeTarget}
              selectedFeatures={tableSelection}
              selectionFlash={selectionFlash}
              // Sketching is detected inside MapView from drawMode; this
              // covers the tools that read a click without drawing anything.
              popupSuppressed={toolOwnsMapClicks(builderActiveTool)}
              drawMode={sketchMode}
              onDrawFeature={handleSketch}
              onDrawProgress={setSketchVertices}
              drawFinishToken={drawFinishToken}
              draftGeometry={draftGeometry}
              moveGeometry={moveGeometry}
              onFeatureMove={onFeatureMove}
              snapEnabled={snapEnabled}
              measureShapes={measureShapes}
              // Always on, not just while actively measuring: this is what
              // labels the FINISHED shapes MapViewOL keeps on the map (see
              // its own "kept on the map" effect) — gating it to only while
              // `measureSketchMode` was set made every measurement's label
              // vanish the moment the tool was stopped, even though the
              // shape itself correctly stayed on screen.
              measureFormat={measureFormat}
              printFrameRatio={printFrameRatio}
              key={layerRefreshToken}
              onViewChange={(newCenter, newZoom) => {
                setCenter(newCenter);
                setZoom(newZoom);
              }}
            />

            {/* Right-side header design panel, same family as Symbology and
                Label Properties. Every change lands on `headerTheme` at once,
                so the header above updates as it is edited. */}
            <HeaderProperties
              open={showHeaderDesigner}
              theme={headerTheme}
              onChange={setHeaderTheme}
              colorTheme={colorTheme}
              onColorThemeChange={setColorTheme}
              onClose={() => setShowHeaderDesigner(false)}
            />

            {/* Right-side symbology properties panel — slides over the map so
                the live preview stays visible while editing. */}
            <SymbologyPanel
              layer={symbologyLayer}
              module={{ ...module, geoServerUrl, arcgisUrl, layers }}
              open={Boolean(symbologyLayer)}
              onPreview={setRendererPreview}
              onApply={(renderer) => {
                if (!symbologyLayerId) return;
                updateLayer(symbologyLayerId, { renderer });
                setRendererPreview(null);
              }}
              // Cache a geometry type the panel had to discover, so the correct
              // editor loads instantly next time and persists with the module.
              onGeometryResolved={(layerId, geometryType) =>
                updateLayer(layerId, { geometryType })
              }
              onClose={() => {
                setSymbologyLayerId(null);
                setRendererPreview(null);
              }}
            />

            {/* Right-side Label Properties panel. Independent of symbology:
                it reads and writes only `layer.labeling`. */}
            <LabelProperties
              layer={labelingLayer}
              module={{ ...module, geoServerUrl, arcgisUrl, layers }}
              open={Boolean(labelingLayer)}
              currentScale={scaleAt(center[0], zoom)}
              onPreview={setLabelingPreview}
              onApply={(labeling) => {
                if (!labelingLayerId) return;
                updateLayer(labelingLayerId, { labeling });
                setLabelingPreview(null);
              }}
              onGeometryResolved={(layerId, geometryType) =>
                updateLayer(layerId, { geometryType })
              }
              onClose={() => {
                setLabelingLayerId(null);
                setLabelingPreview(null);
              }}
            />

            {/* Right-side Pop-up Properties panel. Third of the same family:
                it reads and writes only `layer.popup`, and previews inside
                itself because a pop-up has no on-map preview. */}
            <PopupProperties
              layer={popupLayer}
              module={{ ...module, geoServerUrl, arcgisUrl, layers }}
              open={Boolean(popupLayer)}
              onApply={(popup) => {
                if (!popupLayerId) return;
                updateLayer(popupLayerId, { popup });
              }}
              onClose={() => setPopupLayerId(null)}
            />

            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
              <div
                className={`w-3.5 h-3.5 rounded-full border-2 border-white shadow ${
                  basemap === 'satellite' ? 'bg-blue-300' : 'bg-blue-600'
                }`}
              />
            </div>



            <div className="gl-toolbar gl-surface--over-map absolute top-3 left-3 flex flex-col overflow-hidden z-[2000] pointer-events-auto">
              <button
                onClick={() => setZoom((z) => Math.min(MAX_INTERACTIVE_ZOOM, z + 1))}
                className="p-1.5 hover:bg-slate-50 text-slate-600 border-b border-slate-100"
                aria-label="Zoom in"
              >
                <ZoomIn className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setZoom((z) => Math.max(1, z - 1))}
                className="p-1.5 hover:bg-slate-50 text-slate-600 border-b border-slate-100"
                aria-label="Zoom out"
              >
                <ZoomOut className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => {
                  setCenter(homeCenter);
                  setZoom(homeZoom);
                }}
                className="p-1.5 hover:bg-slate-50 text-slate-600 border-b border-slate-100"
                aria-label="Home location"
              >
                <Home className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={doLocate}
                className={`p-1.5 hover:bg-slate-50 ${
                  locating ? 'text-blue-600' : 'text-slate-600'
                }`}
                aria-label="My location"
              >
                <LocateFixed className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Map controls slide clear of the symbology panel instead of
                sitting on top of its close button. */}
            <div
              className="gl-toolbar gl-surface--over-map absolute top-4 flex items-center gap-2 p-2 z-[3900] pointer-events-auto transition-[right] duration-300 ease-out"
              style={{ right: symbologyLayer ? 336 : 16 }}
            >
              {/* Real geocoding search; the previous input had no handler.
                  Feature search is NOT here — it opens in the tool rail like
                  every other tool, so this corner keeps meaning one thing:
                  finding a PLACE. */}
              <GlobalLocationSearch
                onGoToLocation={(nextCenter, nextZoom) => {
                  setCenter(nextCenter);
                  setZoom(nextZoom);
                }}
                onMarkerChange={setSearchMarker}
              />

              <button
                onClick={() =>
                  setBasemap((b) => (b === 'street' ? 'terrain' : b === 'terrain' ? 'satellite' : 'street'))
                }
                className="p-2 bg-white rounded-lg shadow-md border border-slate-200 text-slate-600 hover:bg-slate-50"
                aria-label="Change base map"
                title={`Base map: ${basemap}`}
              >
                <Globe2 className="w-3.5 h-3.5" />
              </button>
              <ToolsToggle
                open={toolsOpen}
                onToggle={() => setToolsOpen((current) => !current)}
                count={tools.length}
              />
            </div>


            {locating && (
              <div className="absolute bottom-4 left-4 bg-white px-3 py-1.5 rounded-lg text-xs text-blue-600 shadow border border-blue-100">
                Locating you...
              </div>
            )}
            {locateError && !locating && (
              <div
                role="alert"
                className="absolute bottom-4 left-4 max-w-xs bg-white px-3 py-1.5 rounded-lg text-xs text-red-600 shadow border border-red-100"
              >
                {locateError}
              </div>
            )}

            {/* One dialog for both sources. The service handlers are the
                existing ones — discovery/parsing/preview are unchanged. */}
            <AddLayersDialog
              open={addOpen}
              mode={addMode}
              onModeChange={setAddMode}
              onClose={() => setAddOpen(false)}
              serviceType={serviceType}
              onServiceTypeChange={setServiceType}
              geoServerUrl={geoServerUrl}
              onGeoServerUrlChange={setGeoServerUrl}
              onLoadGeoServer={loadGeoServerLayers}
              geoServerLoading={geoServerLoading}
              geoServerError={geoServerError}
              arcgisUrl={arcgisUrl}
              onArcgisUrlChange={(url) => {
                setArcgisUrl(url);
                // Manual entry always targets the module's PRIMARY service,
                // same as it always has — any secondary-service browse in
                // progress is no longer what this box is pointed at.
                setSecondaryArcgisServiceUrl(null);
              }}
              onLoadArcgis={loadArcgisLayers}
              arcgisLoading={arcgisLoading}
              arcgisError={arcgisError}
              options={
                serviceType === 'geoserver'
                  ? availableGeoServerLayers.map((entry) => ({
                      key: entry.name,
                      title: entry.title,
                      added: geoServerLayers.includes(entry.name),
                      onAdd: () => addGeoServerLayer(entry),
                      onRemove: () => removeGeoServerLayer(entry.name),
                    }))
                  : availableArcgisLayers.map((entry) => ({
                      key: String(entry.id),
                      title: entry.name,
                      added: secondaryArcgisServiceUrl
                        ? secondaryArcgisLayerIds(secondaryArcgisServiceUrl).has(entry.id)
                        : arcgisLayers.includes(entry.id),
                      onAdd: () => void addArcgisLayer(entry, secondaryArcgisServiceUrl ?? undefined),
                      onRemove: () => removeArcgisLayer(entry.id, secondaryArcgisServiceUrl ?? undefined),
                    }))
              }
              onAddAll={
                serviceType === 'geoserver'
                  ? addAllGeoServerLayers
                  : () => void addAllArcgisLayers(secondaryArcgisServiceUrl ?? undefined)
              }
              pendingCount={
                serviceType === 'geoserver'
                  ? availableGeoServerLayers.filter(
                      (entry) => !geoServerLayers.includes(entry.name),
                    ).length
                  : secondaryArcgisServiceUrl
                    ? availableArcgisLayers.filter(
                        (entry) => !secondaryArcgisLayerIds(secondaryArcgisServiceUrl).has(entry.id),
                      ).length
                    : availableArcgisLayers.filter((entry) => !arcgisLayers.includes(entry.id)).length
              }
              connections={connections}
              connectionsLoading={connectionsLoading}
              selectedConnectionId={selectedConnectionId}
              onSelectConnection={setSelectedConnectionId}
              onConnectSelected={() => void connectSelectedConnection()}
              connecting={connecting}
              connectError={connectError}
              connectionBrowse={connectionBrowse}
              connectionBrowseLoading={connectionBrowseLoading}
              connectionBrowseError={connectionBrowseError}
              onBrowseConnection={(path) => void browseConnection(path)}
              onSelectConnectionService={(service) => void selectConnectionService(service)}
              onAddLocalLayer={addLocalLayer}
              mapCenter={center}
              mapZoom={zoom}
              currentModuleId={module.id}
              currentUserId={currentUser?.id}
              onAddExistingLayer={addExistingLayer}
            />
          </div>

          {/* Floating attribute table — a DIRECT child of the stable outer
              div (never the shrinking map box above), so its own absolute
              positioning always measures the FULL area regardless of dock
              state — floating over the map, or filling exactly the half the
              map box just made room for. Kept in this one fixed JSX spot so
              toggling dock never remounts it. */}
          {attributeLayer && (
            <FloatingAttributeTable
              layer={attributeLayerLive}
              geoServerUrl={geoServerUrl}
              arcgisUrl={arcgisUrl}
              onClose={() => setAttributeLayer(null)}
              onZoomToSelection={zoomToSelection}
              onSelectionChange={trackSelection}
              preselectedKeys={selectionKeys}
              onLayerChange={updateLayer}
              canManageLayers
              moduleId={module.id}
              dock={attributeDock}
              onDockChange={setAttributeDock}
              dockSize={attributeDockSize}
              onDockSizeChange={setAttributeDockSize}
            />
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

function EndUserViewer({
  module,
  onExitPreview,
}: {
  module: AppModule;
  /** Set only when the viewer is being shown as a builder preview. */
  onExitPreview?: () => void;
}) {
  /* A module saved as dark stays dark for every viewer, whatever their own
     Appearance setting says. See AppModule.appearance. `colorTheme.base`
     wins over this once the module has one — see AppModule.colorTheme. */
  const appDark = module.colorTheme?.base
    ? module.colorTheme.base === 'dark'
    : module.appearance === 'dark';

  const { currentUser } = useAuth();
  /* The published map's layers, plus whatever scratch layers this reader has
     drawn on it before. Their own, and only theirs — see the builder's copy. */
  const [layers, setLayers] = useState<Layer[]>(() => [
    ...module.layers.map((layer) => ({ ...layer })),
    ...temporaryLayerService.load(currentUser?.id, module.id),
  ]);

  /* The published map is not editable here, but a reader's OWN scratch layers
     are — otherwise someone could create a buffer and have no way to get rid
     of it. The restriction is enforced in these handlers rather than by hiding
     a menu item: a hidden control is not a boundary. */
  /**
   * Changes a viewer is allowed to make to a layer.
   *
   * Two different permissions, deliberately not one:
   *
   *  - A layer this person created (a buffer, a sketch) is entirely theirs.
   *  - Any layer, including the published ones, accepts VIEW-ONLY changes.
   *
   * Filtering is in the second group because it is a way of LOOKING at a map,
   * not a change to it: it is session state, cleared when the Filter tool
   * closes, and the viewer never saves the module anyway. Blocking it made the
   * Filter tool silently do nothing here while working in the builder — the
   * expression applied, the map ignored it.
   *
   * ALL THREE FILTER KEYS BELONG HERE, NOT JUST `filter`.
   * A filter is written to the layer as three fields at once, and which one
   * actually does the work depends on what is behind the layer:
   *
   *   - `filter`        an expression for a SERVICE — CQL_FILTER for WMS, a
   *                     `where` clause for ArcGIS
   *   - `filterClauses` the same conditions, structured, for a layer with no
   *                     service behind it — an imported shapefile, GeoJSON or
   *                     KML, which the browser filters itself
   *   - `filterInvert`  the tool's "except these" switch, read alongside them
   *
   * Listing only `filter` let the service-backed layers through and silently
   * dropped the other two, so filtering a shapefile in the viewer set the
   * expression, showed the chip, and left every single feature on the map —
   * while the identical action in the builder worked. `hasLocalFilter` in
   * layers/localFilter.ts keys off `filterClauses` alone, so without it there
   * is nothing for the browser to evaluate.
   *
   * Renaming, symbology, reordering and removal stay blocked: those really
   * would be edits to somebody else's published map.
   */
  const VIEWER_EDITABLE_KEYS: (keyof Layer)[] = ['filter', 'filterClauses', 'filterInvert'];

  const changeTemporaryLayer = (layerId: string, changes: Partial<Layer>) =>
    setLayers((current) =>
      current.map((layer) => {
        if (layer.id !== layerId) return layer;
        if (layer.temporary) return { ...layer, ...changes };

        const permitted: Partial<Layer> = {};
        for (const key of VIEWER_EDITABLE_KEYS) {
          if (key in changes) (permitted as any)[key] = (changes as any)[key];
        }
        return Object.keys(permitted).length > 0 ? { ...layer, ...permitted } : layer;
      }),
    );

  const removeTemporaryLayer = (layerId: string) =>
    setLayers((current) => current.filter((layer) => !(layer.id === layerId && layer.temporary)));
  const [center, setCenter] = useState<[number, number]>(module.homeCenter);
  const [zoom, setZoom] = useState(module.homeZoom);
  const [basemap, setBasemap] = useState<BasemapId>(module.basemap ?? 'street');
  const [locating, setLocating] = useState(false);
  /** Set when `doLocate` fails; shown in place of the "Locating you…" chip. */
  const [locateError, setLocateError] = useState<string | null>(null);
  const [featureAttributes, setFeatureAttributes] = useState<Record<string, unknown> | null>(null);
  const [openTools, setOpenTools] = useState<string[]>([]);
  const [activeToolTab, setActiveToolTab] = useState<string>('layers');
  /* End users start with the strip hidden so the map gets the full height;
     the Tools button by the header brings it back. */
  const [toolsOpen, setToolsOpen] = useState(false);
  const [tocWidth, setTocWidth] = useState(350);
  const [tocResizing, setTocResizing] = useState(false);
  /**
   * Whether the map contents sidebar is folded away to give the map the width.
   *
   * Collapsed means GONE — zero width, so the map gets the whole window. The
   * control that brings it back floats over the map, stacked above the zoom
   * and home buttons in the same corner rather than beside them, so those
   * shift down instead of being covered.
   *
   * `tocWidth` is left alone on purpose, so reopening restores the width the
   * user had dragged the panel to rather than resetting it to the default.
   */
  const [tocCollapsed, setTocCollapsed] = useState(false);
  /** Bumped by "Refresh layer" to force the map to re-request its sources. */
  const [layerRefreshToken, setLayerRefreshToken] = useState(0);
  /** Layer whose attribute table is open in the floating window, if any. */
  const [attributeLayer, setAttributeLayer] = useState<Layer | null>(null);
  /** Free-floating (default) vs docked to a genuine half of the map area. */
  const [attributeDock, setAttributeDock] = useState<'float' | 'right' | 'bottom'>('float');
  /** Docked size, as a fraction of the map area — dragging the docked
   *  panel's own divider (in FloatingAttributeTable) reports here. */
  const [attributeDockSize, setAttributeDockSize] = useState(0.5);

  /**
   * The LIVE layer the attribute table is showing.
   *
   * `attributeLayer` is the object captured when the window was opened — a
   * snapshot. Filter it afterwards and that snapshot still carries the old
   * (or no) filter, so the table would never re-request and would keep
   * answering the previous question. Looked up by id each render instead.
   */
  const attributeLayerLive = attributeLayer
    ? layers.find((entry) => entry.id === attributeLayer.id) ?? attributeLayer
    : null;

  /** Temporary marker for the last location search. */
  const [searchMarker, setSearchMarker] = useState<
    { latitude: number; longitude: number; label: string } | null
  >(null);

  /** See the Builder's identical memo — a layer inside a GROUP that is
   *  itself switched off draws nothing, independent of its own `checked`. */
  const groupVisible = useCallback(
    (layer: Layer) =>
      !layer.groupId || layers.find((entry) => entry.id === layer.groupId)?.checked !== false,
    [layers],
  );

  const visibleGeoServerLayers = useMemo(
    () =>
      layers
        .filter(
          (layer) =>
            layer.id.startsWith('geoserver_') &&
            layer.checked &&
            groupVisible(layer) &&
            isVisibleAtZoom(layer, zoom),
        )
        .map((layer) => layer.name),
    [layers, zoom, groupVisible],
  );

  const visibleArcgisLayers = useMemo(
    () =>
      layers
        // See the Builder's identical exclusion: a secondary-service layer
        // belongs in `arcgisSecondaryLayers`, not this PRIMARY-service list.
        .filter(
          (layer) =>
            layer.checked &&
            groupVisible(layer) &&
            layer.arcgisLayerId !== undefined &&
            !layer.arcgisServiceUrl &&
            isVisibleAtZoom(layer, zoom),
        )
        .map((layer) => layer.arcgisLayerId as number),
    [layers, zoom, groupVisible],
  );

  /* Same tool state as the builder — the viewer runs the same panels, so it
     has to be able to hold their results too. See the builder's copy. */
  const [toolPoint, setToolPoint] = useState<MapPoint | null>(null);
  const [toolOverlay, setToolOverlay] = useState<GeoJSON.FeatureCollection | null>(null);
  const [swipe, setSwipe] = useState<{ layerId: string | null; vertical: boolean }>({
    layerId: null,
    vertical: false,
  });

  const swipeTarget = useMemo(() => {
    if (!swipe.layerId) return null;
    const layer = layers.find((entry) => entry.id === swipe.layerId);
    if (!layer) return null;
    // A GeoTIFF is neither an ArcGIS sublayer nor a WMS layer name — checked
    // first so it is never mistaken for the latter (any layer without an
    // `arcgisLayerId` used to fall into `wmsLayerName` by default).
    if (layer.localRaster) {
      return { localRasterLayerId: layer.id, vertical: swipe.vertical };
    }
    return {
      arcgisLayerId: layer.arcgisLayerId ?? null,
      wmsLayerName: layer.arcgisLayerId === undefined ? layer.name : null,
      vertical: swipe.vertical,
    };
  }, [swipe, layers]);

  /* What the attribute table has ticked, mirrored on the map, plus a counter
     that makes it blink. A counter and not a boolean: pressing Zoom to
     selected twice has to blink twice. */
  const [tableSelection, setTableSelection] = useState<GeoJSON.Feature[]>([]);
  const [selectionKeys, setSelectionKeys] = useState<string[]>([]);
  const [selectionFlash, setSelectionFlash] = useState(0);

  /**
   * Frames the ticked rows and draws attention to them.
   *
   * Zooming alone was the whole behaviour before, which left people hunting
   * for what had just been framed — especially with several features spread
   * across the view, where the map moves but nothing says which shapes
   * mattered.
   *
   * The actual framing happens in MapViewOL, not here: fitting a line or
   * polygon properly needs the real, on-screen pixel size of the map, which
   * only the OL view itself has. This just bumps the token MapViewOL's own
   * "fit to selection" effect watches (reading `tableSelection`, already set
   * by the `onSelectionChange` call every caller of this makes first) —
   * setting a plain `center`/`zoom` pair here the way this used to could
   * only ever express ONE fixed scale, which cropped anything bigger than
   * that scale could show.
   */
  const zoomToSelection = useCallback((features: AttributeFeature[]) => {
    // Blink whether or not the view moved: pressing the button with the map
    // already framed still has to point at something.
    setSelectionFlash((count) => count + 1);
  }, []);

  /* ── Draw ────────────────────────────────────────────────────────────
     Sketches accumulate into ONE temporary layer rather than a layer per
     shape, which is how a sketch pad behaves: the TOC would otherwise grow a
     row every time someone clicked. It is the same kind of layer the Buffer
     tool creates — this person's own, never saved into the shared map. */
  const [drawType, setDrawType] = useState<'point' | 'line' | 'polygon' | 'circle'>('point');
  const [isDrawing, setIsDrawing] = useState(false);
  /** Vertices placed in the shape being drawn right now, 0 when none. */
  const [sketchVertices, setSketchVertices] = useState(0);
  const [drawFinishToken, setDrawFinishToken] = useState(0);

  /* ── Edit: new feature / reshape ────────────────────────────────────────
     A fourth consumer of the same shared sketch controller Draw/Measure/
     Select already arm — the Edit tool's "New feature" flow. The finished
     shape is handed back as a tagged result rather than a callback (the
     Edit panel closes over neither directly), the same shape `measuredFeature`
     already uses for the Measure tool. */
  const [editCreateShape, setEditCreateShape] = useState<
    'point' | 'line' | 'polygon' | 'circle' | 'rectangle' | 'freehand-polygon' | 'freehand-line' | null
  >(null);
  const [editSketchResult, setEditSketchResult] = useState<{ token: number; feature: GeoJSON.Feature } | null>(
    null,
  );
  /** A feature's geometry, live on the map as a single draggable whole. */
  const [moveGeometry, setMoveGeometry] = useState<GeoJSON.Geometry | null>(null);
  const [moveResult, setMoveResult] = useState<{ token: number; geometry: GeoJSON.Geometry } | null>(null);
  /** The Edit tool's open record's own geometry, shown highlighted/dashed on
   *  the map so a not-yet-saved sketch, reshape, or move never just
   *  vanishes between finishing the edit and pressing Save. */
  const [draftGeometry, setDraftGeometry] = useState<GeoJSON.Geometry | null>(null);
  /** The Edit tool's own toggle — read by the map to drive ol/interaction/Snap. */
  const [snapEnabled, setSnapEnabled] = useState(false);

  /** The sketch pad is identified by its own tags, not a separately-tracked
   *  id — there is only ever one at a time, so `temporary && temporarySource
   *  === 'Draw'` finds it directly. (A previous version tracked its id in a
   *  ref written from inside this very setLayers updater — a real anti-
   *  pattern: an updater function is supposed to be pure, and a side effect
   *  inside one runs whenever/however many times React decides to call it,
   *  not necessarily once, synchronously, right here.) */
  const findDrawingsLayer = (current: Layer[]) =>
    current.find((layer) => layer.temporary && layer.temporarySource === 'Draw');

  const addDrawnFeature = useCallback((feature: GeoJSON.Feature) => {
    setLayers((current) => {
      const existing = findDrawingsLayer(current);

      if (existing) {
        const data: GeoJSON.FeatureCollection = {
          type: 'FeatureCollection',
          features: [...(existing.localData?.features ?? []), feature],
        };
        return current.map((layer) =>
          layer.id === existing.id
            ? { ...layer, localData: data, bounds: computeBounds(data) }
            : layer,
        );
      }

      const data: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [feature] };
      return [
        ...current,
        {
          id: createId('temp'),
          name: 'Drawings',
          sublayers: [],
          checked: true,
          expanded: false,
          localData: data,
          bounds: computeBounds(data),
          temporary: true,
          temporarySource: 'Draw',
          /* A sketch pad holds points, lines and polygons at once, so fill and
             stroke are set separately: a polygon wants a light fill you can
             read the map through, a line wants a solid stroke. */
          renderer: {
            type: 'single',
            color: '#F97316',
            fillOpacity: 0.25,
            lineOpacity: 1,
            outlineColor: '#C2410C',
            outlineWidth: 3,
            pointSize: 12,
          },
        } as Layer,
      ];
    });
  }, []);

  /** Empties the sketch pad without disturbing anything else on the map. */
  const clearDrawings = useCallback(() => {
    setLayers((current) => current.filter((layer) => !(layer.temporary && layer.temporarySource === 'Draw')));
  }, []);

  /** Removes one drawn feature, keeping the rest of the sketch pad intact. */
  const deleteDrawnFeature = useCallback((index: number) => {
    setLayers((current) => {
      const layer = findDrawingsLayer(current);
      const features = layer?.localData?.features;
      if (!layer || !features) return current;
      const remaining = features.filter((_, featureIndex) => featureIndex !== index);
      if (remaining.length === 0) {
        return current.filter((entry) => entry.id !== layer.id);
      }
      const data: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: remaining };
      return current.map((entry) =>
        entry.id === layer.id ? { ...entry, localData: data, bounds: computeBounds(data) } : entry,
      );
    });
  }, []);

  /** Frames one drawn feature — the same zoom-and-blink `zoomToSelection`
   *  gives the attribute table's "Zoom to selected". */
  const zoomToDrawnFeature = useCallback(
    (feature: GeoJSON.Feature) =>
      zoomToSelection([
        {
          id: String(feature.id ?? 'draw'),
          properties: (feature.properties ?? {}) as Record<string, unknown>,
          geometry: feature.geometry,
        },
      ]),
    [zoomToSelection],
  );

  /* ── Sketching, shared by Draw and Measure ───────────────────────────
     Both tools own the map's clicks, and both draw the same rubber band, so
     there is ONE sketch controller and the difference is what happens to the
     finished shape: Draw keeps it as a layer, Measure turns it into a number
     and throws it away. */
  const [measureSketchMode, setMeasureSketchMode] = useState<'line' | 'polygon' | null>(null);
  const [measureImperial, setMeasureImperial] = useState(false);
  const [measureShapes, setMeasureShapes] = useState<GeoJSON.Feature[]>([]);
  const [measuredFeature, setMeasuredFeature] = useState<
    { token: number; feature: GeoJSON.Feature } | null
  >(null);

  /** Written on every measurement label, in the unit the panel is set to. */
  const measureFormat = useCallback(
    (metres: number) => formatLength(metres, measureImperial ? 'imperial' : 'metric'),
    [measureImperial],
  );

  const handleMeasureMode = useCallback((mode: 'line' | 'polygon' | null, imperial?: boolean) => {
    setMeasureSketchMode(mode);
    setMeasureImperial(Boolean(imperial));
  }, []);


  /* ── Select ──────────────────────────────────────────────────────────
     The third destination for a sketch. The shape is not kept; it is used to
     ask which features it touched, and then discarded. */
  const [selectSketchMode, setSelectSketchMode] = useState<
    'rectangle' | 'polygon' | 'circle' | null
  >(null);
  const [selectHits, setSelectHits] = useState<{ token: number; rows: AttributeFeature[] } | null>(
    null,
  );

  /** The Print tool's extent-preview box — see MapViewOL's own doc comment
   *  on `printFrameRatio`. Null while the Print tab is closed. */
  const [printFrameRatio, setPrintFrameRatio] = useState<number | null>(null);
  const selectLayersRef = useRef<string[]>([]);
  /** How the next box combines with what is already selected. */
  const selectCombineRef = useRef<'new' | 'add' | 'remove'>('new');
  /** The current selection, for Add and Remove to build on. A ref because the
   *  sketch handler is a callback that must not go stale between boxes. */
  const selectionRowsRef = useRef<AttributeFeature[]>([]);
  /* Handed to the tool panels so one can combine with what is already
     selected. Reads the REF, so it is stable and always current: a tool that
     closed over the array instead would add to whatever happened to be
     selected when its tab was opened. */
  const readSelection = useCallback(() => selectionRowsRef.current, []);

  /** A layer's features for hit-testing, from whichever service backs it. */
  const loadFeaturesForSelect = useCallback(
    async (layer: Layer, limit = 2000): Promise<GeoJSON.Feature[]> => {
      // Filtered, matching the ArcGIS and WFS branches below.
      if (layer.localData) return filteredLocalFeatures(layer).slice(0, limit);

      if (layer.arcgisLayerId !== undefined) {
        const effectiveUrl = arcgisServiceUrlFor(layer, module);
        const baseUrl = getArcgisBaseUrl(effectiveUrl);
        if (!baseUrl) return [];
        const params = new URLSearchParams({
          // The layer's own filter still applies: a box cannot select what the
          // map is not showing.
          where: layer.filter?.trim() || '1=1',
          outFields: '*',
          outSR: '4326',
          f: 'geojson',
          resultRecordCount: String(limit),
        });
        const response = await fetch(
          withArcgisToken(`${baseUrl}/${layer.arcgisLayerId}/query?${params.toString()}`, effectiveUrl),
        );
        if (!response.ok) return [];
        return (await response.json())?.features ?? [];
      }

      if (!module.geoServerUrl) return [];
      const response = await fetch(getWfsUrl(module.geoServerUrl, layer.name, limit, layer.filter));
      if (!response.ok) return [];
      return (await response.json())?.features ?? [];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const sketchMode = isDrawing ? drawType : measureSketchMode ?? selectSketchMode ?? editCreateShape;

  const handleSketch = useCallback(
    async (feature: GeoJSON.Feature) => {
      if (measureSketchMode) {
        setMeasuredFeature((current) => ({ token: (current?.token ?? 0) + 1, feature }));
        return;
      }

      if (selectSketchMode) {
        /* The shape is a question, not a result: it asks which of the layer's
           features it touches, and is thrown away once answered.
           Uses the same intersect test as Select By Location (prepare/relate)
           rather than a vertex-only check — a vertex-only test missed a
           feature whose SEGMENT passed straight through the drawn shape
           without either endpoint landing inside it, which is exactly what
           happens drawing a small box over the middle of one long straight
           line (e.g. a single pipe between two junctions far apart) — the
           most common way to try to select just one line. */
        if (feature.geometry?.type !== 'Polygon') return;
        const shapeGeom = prepare(feature.geometry);
        const targets = layers.filter((entry) => selectLayersRef.current.includes(entry.id));
        if (targets.length === 0) return;

        /* Read in parallel — one slow layer must not hold up the others, and a
           box over four layers is four independent questions.
           Each catch is TAGGED with the layer it came from: the panel reports
           by layer, and a flattened list would have thrown that away. */
        const perLayer = await Promise.all(
          targets.map(async (target) => {
            const features = await loadFeaturesForSelect(target);
            return features
              .filter((entry) => relate(prepare(entry.geometry), shapeGeom, 'intersect', 0, 1))
              .map((entry) => ({ entry, layerId: target.id }));
          }),
        );
        const caught = perLayer.flat();

        /* New / Add / Remove — the three selection verbs every desktop GIS
           has, and what makes more than one box useful. Combined BY KEY, not
           by position: the two passes are separate reads of the service, so
           the same feature can arrive at a different index each time. */
        const mode = selectCombineRef.current;
        const previous = selectionRowsRef.current;
        const caughtRows: AttributeFeature[] = caught.map(({ entry, layerId }, index) => ({
          ...(entry.properties ?? {}),
          id: String(entry.id ?? index),
          key: featureKey(entry, layerId),
          // Which layer this came from, so the panel can report by layer.
          layerId,
          properties: (entry.properties ?? {}) as Record<string, unknown>,
          geometry: entry.geometry ?? undefined,
        }));

        let combined: AttributeFeature[] = caughtRows;
        if (mode === 'add') {
          const seen = new Set(previous.map((row) => row.key));
          combined = [...previous, ...caughtRows.filter((row) => !seen.has(row.key))];
        } else if (mode === 'remove') {
          const drop = new Set(caughtRows.map((row) => row.key));
          combined = previous.filter((row) => !drop.has(row.key));
        }

        setSelectHits((current) => ({ token: (current?.token ?? 0) + 1, rows: combined }));

        /* Stays armed. Selecting more than one thing is the normal case, and
           re-pressing Start between every box would make it the exception. */

        // The shared selection channel, so the map highlights them in cyan and
        // an open attribute table ticks the same rows.
        trackSelection(combined);
        return;
      }

      if (editCreateShape) {
        setEditSketchResult((current) => ({ token: (current?.token ?? 0) + 1, feature }));
        setEditCreateShape(null);
        return;
      }

      addDrawnFeature(feature);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [measureSketchMode, selectSketchMode, editCreateShape, addDrawnFeature, layers],
  );

  /** A feature was dragged as a whole on the map — the Edit tool's Move flow. */
  const onFeatureMove = useCallback((geometry: GeoJSON.Geometry) => {
    setMoveResult((current) => ({ token: (current?.token ?? 0) + 1, geometry }));
  }, []);

  /**
   * Table rows to something the map can draw.
   *
   * Rows without geometry are dropped, not passed through: ArcGIS happily
   * returns attribute-only records, and L.geoJSON throws "Invalid LatLng" on
   * the first one and drops the whole collection — the same trap
   * `hasDrawableGeometry` exists for on the layer side.
   */
  const trackSelection = useCallback((rows: AttributeFeature[]) => {
    // Kept alongside the geometry so a table opened LATER can tick the same
    // rows — it issues its own request, so row numbers would not line up.
    selectionRowsRef.current = rows;
    setSelectionKeys(rows.map((row) => row.key).filter((key): key is string => Boolean(key)));
    setTableSelection(
      rows
        .filter((row) => row.geometry)
        .map((row) => ({
          type: 'Feature' as const,
          properties: row.properties ?? {},
          geometry: row.geometry as GeoJSON.Geometry,
        })),
    );
  }, []);

  /* Scratch layers follow the person, not the map, so they are written to their
     own storage whenever the layer list changes — that is what "stays until
     they remove it" means across a reload. */
  useEffect(() => {
    temporaryLayerService.save(currentUser?.id, module.id, layers);
  }, [layers, currentUser?.id, module.id]);

  /**
   * Adds a tool's result to the TOC as this person's own layer.
   *
   * It arrives as `localData`, which is the same shape a file import produces —
   * so it renders, styles, labels and identifies through the existing
   * client-side path rather than a parallel one built for analysis output.
   */
  const addTemporaryLayer = useCallback(
    (
      name: string,
      data: GeoJSON.FeatureCollection,
      source: string,
      renderer?: Layer['renderer'],
      heatmap?: HeatmapConfig,
    ) => {
      const id = createId('temp');
      setLayers((current) => [
        ...current,
        {
          id,
          name,
          sublayers: [],
          checked: true,
          expanded: false,
          localData: data,
          bounds: computeBounds(data),
          // A heatmap result is a cloud of sample points, not the polygon
          // shape every other temp layer (Buffer, Draw) has produced so far.
          geometryType: heatmap ? 'point' : 'polygon',
          temporary: true,
          temporarySource: source,
          renderer,
          heatmap,
        },
      ]);
      return id;
    },
    [],
  );

  /**
   * The Georeference tool's raster twin of `addTemporaryLayer` above — a
   * tool panel had no way to add a brand-new RASTER layer before this,
   * only `onCreateTempLayer`'s vector `FeatureCollection`. Kept as a
   * separate function rather than widening `addTemporaryLayer` itself:
   * the two shapes (`localData` vs `localRaster`) don't overlap enough to
   * share one signature without optional-everything mush.
   */
  const addTemporaryRasterLayer = useCallback(
    (name: string, raster: LocalRasterData, bounds: [number, number, number, number], source: string) => {
      const id = createId('temp');
      setLayers((current) => [
        ...current,
        {
          id,
          name,
          sublayers: [],
          checked: true,
          expanded: false,
          localRaster: raster,
          bounds,
          temporary: true,
          temporarySource: source,
        },
      ]);
      return id;
    },
    [],
  );


  // Layers imported from a file carry their features on the layer itself, so
  // the viewer must hand them to the map the same way the builder does —
  // otherwise a saved local/KML layer shows in the TOC but never draws.
  const localLayers = useMemo(
    () =>
      layers
        .filter((layer) => layer.checked && groupVisible(layer) && layer.localData && isVisibleAtZoom(layer, zoom))
        .map((layer) => ({
          id: layer.id,
          // Titles the pop-up card, the same way a service layer's name does.
          name: layer.displayName?.trim() || layer.name,
          // Filtered here, because there is no service to filter it for us.
          data: filteredLocalData(layer),
          style: { renderer: layer.renderer, opacity: layer.opacity, heatmap: layer.heatmap },
        })),
    [layers, zoom, groupVisible],
  );

  /** Imported GeoTIFFs — no symbology or filter to carry, only opacity. */
  const localRasterLayers = useMemo(
    () =>
      layers
        .filter((layer) => layer.checked && groupVisible(layer) && layer.localRaster && isVisibleAtZoom(layer, zoom))
        .map((layer) => ({
          id: layer.id,
          base64: layer.localRaster!.base64,
          mimeType: layer.localRaster!.mimeType,
          opacity: layer.opacity,
          bounds: layer.bounds,
        })),
    [layers, zoom, groupVisible],
  );

  /** The Layers tab's own top-to-bottom order, for MapViewOL's `tocZIndex` —
   *  see its doc comment for why draw order otherwise drifts from this. */
  const layerOrder = useMemo(() => layers.map((layer) => layer.id), [layers]);

  /* The viewer renders the SAME saved configuration the builder produced, so
     symbology and labels have to be handed to the map here too — the builder's
     equivalents are the memos above `symbologyLayer`. There is no preview
     state: the viewer only ever shows what was applied. */
  const arcgisLayerStyles = useMemo(() => {
    const styles: Record<number, { renderer?: Layer['renderer']; opacity?: number; filter?: string }> = {};
    for (const layer of layers) {
      // See the Builder's identical exclusion: a secondary-service layer's
      // id can collide with a primary one's, and this dict only ever holds
      // the primary service's layers.
      if (layer.arcgisLayerId === undefined || layer.arcgisServiceUrl) continue;
      styles[layer.arcgisLayerId] = {
        renderer: layer.renderer,
        opacity: layer.opacity,
        filter: layer.filter,
      };
    }
    return styles;
  }, [layers]);

  /** See the Builder's identical memo — every OTHER ArcGIS service a layer
   *  was added from, grouped so ids never collide across services. */
  const arcgisSecondaryLayers = useMemo(() => {
    // A plain record, not a generic Map: this build's TypeScript rejects
    // `new Map<K, V>()` here (TS2350), the same way it did in loadArcgisLayers.
    type Group = { serviceUrl: string; layerIds: number[]; styles: Record<number, { renderer?: Layer['renderer']; opacity?: number; filter?: string }> };
    const groups: Record<string, Group> = {};
    for (const layer of layers) {
      if (layer.arcgisLayerId === undefined || !layer.arcgisServiceUrl) continue;
      if (!layer.checked || !groupVisible(layer) || !isVisibleAtZoom(layer, zoom)) continue;
      const key = getArcgisBaseUrl(layer.arcgisServiceUrl);
      if (!key) continue;
      const group = (groups[key] ||= { serviceUrl: layer.arcgisServiceUrl, layerIds: [], styles: {} });
      group.layerIds.push(layer.arcgisLayerId);
      group.styles[layer.arcgisLayerId] = {
        renderer: layer.renderer,
        opacity: layer.opacity,
        filter: layer.filter,
      };
    }
    return Object.values(groups);
  }, [layers, zoom, groupVisible]);

  const geoServerLayerStyles = useMemo(() => {
    const styles: Record<
      string,
      {
        renderer?: Layer['renderer'];
        opacity?: number;
        filter?: string;
        geometryType?: Layer['geometryType'];
        labeling?: LayerLabeling;
      }
    > = {};
    for (const layer of layers) {
      if (layer.arcgisLayerId !== undefined) continue;
      styles[layer.name] = {
        renderer: layer.renderer,
        opacity: layer.opacity,
        filter: layer.filter,
        geometryType: layer.geometryType,
        labeling: layer.labeling,
      };
    }
    return styles;
  }, [layers]);

  const labelLayers = useMemo(
    () =>
      layers
        .filter((layer) => layer.checked && groupVisible(layer) && labelingIsActive(layer.labeling))
        .filter((layer) => layer.localData || layer.arcgisLayerId !== undefined)
        .map((layer) => ({
          id: layer.id,
          labeling: normaliseLabeling(layer.labeling, layer.geometryType ?? 'unknown'),
          // Same reason as the builder: labels follow the filtered features.
          features: layer.localData ? filteredLocalFeatures(layer) : undefined,
          arcgisLayerId: layer.arcgisLayerId,
        })),
    [layers, groupVisible],
  );

  /**
   * Pop-up configuration handed to the map, keyed the way the identify path can
   * find it again: a WMS layer by service name, an ArcGIS layer by its numeric
   * id. A layer with no configuration is simply absent, which the map reads as
   * "use the automatic pop-up".
   */
  const popupConfigs = useMemo(() => {
    const configs: Record<string, LayerPopup> = {};
    for (const layer of layers) {
      if (!layer.popup) continue;
      /* An imported layer is keyed by its id: it has no service name and no
         numeric layer id, and two files can share a name. */
      const key = layer.localData
        ? localPopupKey(layer.id)
        : layer.arcgisLayerId !== undefined
          ? arcgisPopupKey(layer.arcgisLayerId)
          : wmsPopupKey(layer.name);
      configs[key] = layer.popup;
    }
    return configs;
  }, [layers]);

  useEffect(() => {
    setOpenTools((current) => current.filter((toolId) => module.tools.includes(toolId)));
  }, [module.tools]);

  useEffect(() => {
    if (activeToolTab !== 'layers' && activeToolTab !== 'legend' && !openTools.includes(activeToolTab)) {
      setActiveToolTab('layers');
    }
  }, [activeToolTab, openTools]);

  useEffect(() => {
    if (!tocResizing) return undefined;

    const handleMouseMove = (event: MouseEvent) => {
      const nextWidth = Math.min(Math.max(event.clientX, 320), 700);
      setTocWidth(nextWidth);
    };

    const handleMouseUp = () => {
      setTocResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [tocResizing]);

  /* Telling everything that measures itself that the layout moved.
     The map doesn't actually need this any more — OL's Map keeps its own
     ResizeObserver on the container, so it re-measures continuously
     throughout the sidebar's width animation, not just before and after it.
     This still matters for the tool tabs, which recompute their scroll
     arrows on a window resize event and have no observer of their own. */
  useEffect(() => {
    const id = window.setTimeout(
      () => window.dispatchEvent(new Event('resize')),
      TOC_TRANSITION_MS + 40,
    );
    return () => window.clearTimeout(id);
  }, [tocCollapsed]);

  const activateTool = (toolId: string) => {
    if (!module.tools.includes(toolId)) return;

    // A tool opened while the sidebar is folded away has nowhere to show
    // itself — the panel exists, but its width is 0 and its content is
    // `hidden`. Un-collapse so the tab that just got selected is on screen.
    setTocCollapsed(false);
    setOpenTools((current) => (current.includes(toolId) ? current : [...current, toolId]));
    setActiveToolTab(toolId);

    if (toolId === 'draw') {
      setIsDrawing(true);
      return;
    }

    setIsDrawing(false);
  };

  const closeTool = (toolId: string) => {
    setOpenTools((current) => current.filter((id) => id !== toolId));
    setActiveToolTab('layers');

    if (toolId === 'draw') {
      setIsDrawing(false);
    }
  };

  const toggleLayer = (id: string) =>
    setLayers((currentLayers) =>
      currentLayers.map((layer) => (layer.id === id ? { ...layer, checked: !layer.checked } : layer)),
    );

  const toggleExpand = (id: string) =>
    setLayers((currentLayers) =>
      currentLayers.map((layer) => (layer.id === id ? { ...layer, expanded: !layer.expanded } : layer)),
    );

  /**
   * Flips one layer's labels on or off for this viewing session.
   *
   * Only `enabled` moves — field, text style, placement and scale range are the
   * Admin's configuration and are left untouched, so switching back on restores
   * exactly what was there. Like every other viewer-side toggle (visibility,
   * expansion) this is view state and is not written back to the saved module.
   */
  const toggleLabels = (id: string) =>
    setLayers((currentLayers) =>
      currentLayers.map((layer) =>
        layer.id === id && layer.labeling
          ? { ...layer, labeling: { ...layer.labeling, enabled: !layer.labeling.enabled } }
          : layer,
      ),
    );

  /**
   * Sets one layer's opacity for this viewing session only — the same
   * view-state pattern as `toggleLabels` just above: it moves the local
   * `layers` state the map already renders from, and never reaches
   * `onSave`, so a refresh (which re-seeds this state from `module`) puts
   * the layer back exactly at the Admin's configured transparency.
   */
  const setViewerOpacity = (id: string, opacity: number) =>
    setLayers((currentLayers) =>
      currentLayers.map((layer) => (layer.id === id ? { ...layer, opacity } : layer)),
    );

  /**
   * Finds the browser's own reported position and centres the map there —
   * the same real implementation as the Builder's `doLocate` (see its doc
   * comment for why this replaced a bare on/off timer that never actually
   * asked the browser for anything).
   */
  const doLocate = () => {
    if (!('geolocation' in navigator)) {
      setLocateError('This browser cannot report your location.');
      return;
    }
    setLocateError(null);
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(false);
        const { latitude, longitude } = position.coords;
        setCenter([latitude, longitude]);
        // Zooms IN to a useful street-level view, but never zooms out — someone
        // already looking closer than that should stay exactly where they are.
        setZoom((current) => Math.max(current, 16));
        setSearchMarker({ latitude, longitude, label: 'Your location' });
      },
      (error) => {
        setLocating(false);
        setLocateError(
          error.code === error.PERMISSION_DENIED
            ? "Location access was denied. Allow it for this site in the browser's settings and try again."
            : error.code === error.TIMEOUT
              ? 'Finding your location took too long. Try again.'
              : 'Your location could not be determined.',
        );
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  };

  return (
    <div
      data-theme={appDark ? 'dark' : undefined}
      style={colorThemeVars(module.colorTheme)}
      className="h-screen bg-slate-50 flex flex-col overflow-hidden"
    >
      {/* The application's own header — the same component the builder shows,
          so what an admin designed is what the end user gets. */}
      <AppHeader title={module.title} theme={module.headerTheme} />

      <div className="flex flex-1 min-w-0 overflow-hidden">
        <aside
          className={`relative flex flex-col overflow-hidden bg-white shadow-[0_0_0_1px_rgba(15,23,42,0.04)] ${
            // The border goes with the panel. Kept, it would leave a stray
            // hairline down the left of a map that is meant to be full width.
            tocCollapsed ? '' : 'border-r border-slate-300'
          }`}
          style={{
            width: tocCollapsed ? 0 : tocWidth,
            minWidth: tocCollapsed ? 0 : 320,
            maxWidth: tocCollapsed ? 0 : 700,
            // Not while dragging the resize handle: a transition there fights
            // the pointer and the edge lags behind the cursor.
            transition: tocResizing ? undefined : `width ${TOC_TRANSITION_MS}ms ease-out`,
          }}
        >

          {/* Hidden, NOT unmounted. The panel holds work in progress — filter
              clauses, measurements, print settings, the open tool tab — and
              unmounting would throw all of it away every time someone folded
              the sidebar to get a better look at the map. */}
          <div className={tocCollapsed ? 'hidden' : 'flex min-h-0 flex-1 flex-col'}>
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 bg-slate-50/80">
              <Layers className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-semibold text-slate-700">Map contents</span>
              <button
                type="button"
                onClick={() => setTocCollapsed(true)}
                aria-label="Hide map contents"
                aria-expanded
                title="Hide map contents"
                className="ml-auto flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            </div>
            <TableOfContents
              layers={layers}
              geoServerUrl={module.geoServerUrl}
              arcgisUrl={module.arcgisUrl || ''}
              // REQUIRED: buildLayerMenu returns an empty list without a module,
              // which made the 3-dot menu open with no items in the viewer.
              module={module}
              /* Read-only context. The 3-dot menu offers only Zoom to layer /
                 Attribute table / Refresh / Layer information — never rename,
                 symbology, reorder or remove.
                 The two callbacks below ARE supplied, and are not a hole in that:
                 each is guarded in the handler rather than by hiding a control.
                 They cover a reader's own scratch layers, and the view-only
                 changes any reader may make — which is how the Filter tool
                 works here at all. */
              canManageLayers={false}
              onLayerChange={changeTemporaryLayer}
              onLayerRemove={removeTemporaryLayer}
              toolConfigs={module.toolConfigs ?? {}}
              basemap={basemap}
              onBasemapChange={setBasemap}
              pointer={toolPoint}
              onOverlayChange={setToolOverlay}
              onCreateTempLayer={addTemporaryLayer}
              onCreateTempRasterLayer={addTemporaryRasterLayer}
              onSwipeChange={setSwipe}
              swipeLayerId={swipe.layerId}
              swipeVertical={swipe.vertical}
              onLayerRefresh={() => setLayerRefreshToken((token) => token + 1)}
              // Labels on/off only. The style, field and scale range stay exactly
              // as the Admin configured them — this flips the switch, nothing more.
              onToggleLabels={toggleLabels}
              // Transparency, same view-only rule: this session's map redraws
              // at the new opacity, the saved module never hears about it.
              onOpacityPreview={setViewerOpacity}
              onOpenAttributeTable={setAttributeLayer}
              onToggleLayer={toggleLayer}
              onToggleExpand={toggleExpand}
              onZoomToLayer={(layer) => {
                // See the builder's Zoom to: the band, not just the extent.
                const layerView = getLayerView(layer);
                if (layerView) {
                  setCenter(layerView.center);
                  setZoom(layerView.zoom);
                }
              }}
              drawType={drawType}
              onDrawTypeChange={(type) => setDrawType(type)}
              onStartDrawing={() => setIsDrawing(true)}
              onStopDrawing={() => setIsDrawing(false)}
              onMeasureModeChange={handleMeasureMode}
              onSelectModeChange={(mode, layerIds, combine) => {
                  setSelectSketchMode(mode);
                  selectLayersRef.current = layerIds ?? [];
                  selectCombineRef.current = combine ?? 'new';
                }}
              onPrintFrameRatioChange={setPrintFrameRatio}
              selectedFeatures={selectHits}
              onMeasurementsChange={setMeasureShapes}
              onSelectionChange={trackSelection}
              getSelection={readSelection}
              onZoomToSelection={zoomToSelection}
              measuredFeature={measuredFeature}
              sketchVertices={sketchVertices}
              onFinishShape={() => setDrawFinishToken((count) => count + 1)}
              onEditModeChange={setEditCreateShape}
              editSketchResult={editSketchResult}
              onDraftGeometryChange={setDraftGeometry}
              onMoveGeometryChange={setMoveGeometry}
              moveResult={moveResult}
              snapEnabled={snapEnabled}
              onSnapEnabledChange={setSnapEnabled}
              onClearAll={() => {
                clearDrawings();
                setIsDrawing(false);
              }}
              onDeleteDrawnFeature={deleteDrawnFeature}
              onZoomToDrawnFeature={zoomToDrawnFeature}
              onCloseDrawPanel={() => {
                setIsDrawing(false);
              }}
              isDrawing={isDrawing}
              tools={module.tools}
              openTools={openTools}
              activeToolTab={activeToolTab}
              onToolTabChange={setActiveToolTab}
              onToolClose={closeTool}
              onToolOpen={activateTool}
              mapCenter={center}
              mapZoom={zoom}
              onGoToLocation={(nextCenter, nextZoom) => {
                setCenter(nextCenter);
                setZoom(nextZoom);
              }}
            />
          </div>
          {/* Nothing to resize while it is folded, and a drag handle sitting on
              the rail's edge would only produce a panel the user cannot see. */}
          {!tocCollapsed && (
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize table of contents"
              onMouseDown={(event) => {
                event.preventDefault();
                setTocResizing(true);
              }}
              className="absolute inset-y-0 right-0 z-[2500] w-3 translate-x-1/2 cursor-col-resize"
            >
              <div className="mx-auto h-full w-[3px] rounded-full bg-transparent transition hover:bg-blue-400/70" />
            </div>
          )}
        </aside>

        <main className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
          <ToolStrip
            open={toolsOpen}
            toolIds={module.tools}
            activeToolId={activeToolTab}
            onOpenTool={activateTool}
          />

          <div className="flex-1 min-h-0 relative overflow-hidden bg-slate-100">
          {/* Map's own box — shrinks for a DOCKED attribute table, the same
              plain-CSS-size approach as the Builder's (see its own comment):
              the table itself stays a direct child of the stable outer div
              below, never moving in the tree, so toggling dock never
              unmounts it. */}
          <div
            className="absolute inset-0"
            style={{
              width: attributeLayer && attributeDock === 'right' ? `${(1 - attributeDockSize) * 100}%` : '100%',
              height: attributeLayer && attributeDock === 'bottom' ? `${(1 - attributeDockSize) * 100}%` : '100%',
            }}
          >
            <MapViewOL
              basemap={basemap}
              center={center}
              zoom={zoom}
              geoServerUrl={module.geoServerUrl}
              geoServerLayers={visibleGeoServerLayers}
              arcgisUrl={module.arcgisUrl || ''}
              arcgisLayers={visibleArcgisLayers}
              arcgisLayerStyles={arcgisLayerStyles}
              arcgisSecondaryLayers={arcgisSecondaryLayers}
              geoServerLayerStyles={geoServerLayerStyles}
              localLayers={localLayers}
              localRasterLayers={localRasterLayers}
              layerOrder={layerOrder}
              labelLayers={labelLayers}
              popupConfigs={popupConfigs}
              // The width the sidebar is actually RENDERING at, not the width
              // it would have if it were open — otherwise collapsing changes
              // nothing here and the map keeps its old size. Combined with
              // the docked attribute table's own side/off state, the other
              // thing that changes this box's actual size.
              containerResizeToken={`${tocCollapsed ? 0 : tocWidth}:${
                attributeLayer && attributeDock !== 'float' ? `${attributeDock}:${attributeDockSize}` : 'float'
              }`}
              searchMarker={searchMarker}
              onMapPoint={setToolPoint}
              toolOverlay={toolOverlay}
              swipe={swipeTarget}
              selectedFeatures={tableSelection}
              selectionFlash={selectionFlash}
              popupSuppressed={toolOwnsMapClicks(activeToolTab)}
              // Remounting re-requests every source; center/zoom live in this
              // component's state, so the current view is preserved.
              key={layerRefreshToken}
              onViewChange={(newCenter, newZoom) => {
                setCenter(newCenter);
                setZoom(newZoom);
              }}
              drawMode={sketchMode}
              onDrawFeature={handleSketch}
              onDrawProgress={setSketchVertices}
              drawFinishToken={drawFinishToken}
              draftGeometry={draftGeometry}
              moveGeometry={moveGeometry}
              onFeatureMove={onFeatureMove}
              snapEnabled={snapEnabled}
              measureShapes={measureShapes}
              // Always on, not just while actively measuring: this is what
              // labels the FINISHED shapes MapViewOL keeps on the map (see
              // its own "kept on the map" effect) — gating it to only while
              // `measureSketchMode` was set made every measurement's label
              // vanish the moment the tool was stopped, even though the
              // shape itself correctly stayed on screen.
              measureFormat={measureFormat}
              printFrameRatio={printFrameRatio}
            />

            {featureAttributes && (
              <div className="gl-modal-backdrop fixed inset-0 z-[3000] px-4 py-6 flex items-center justify-center">
                <div className="gl-modal w-full max-w-2xl overflow-hidden">
                  <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-blue-600">Feature attributes</p>
                      <h2 className="text-base font-semibold text-slate-900">Full attribute details</h2>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFeatureAttributes(null)}
                      className="rounded-full border border-slate-200 bg-white p-2 text-slate-600 hover:bg-slate-50"
                      aria-label="Close full attributes"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="max-h-[70vh] overflow-y-auto p-6 space-y-4">
                    {Object.entries(featureAttributes)
                      .filter(([key]) => key !== 'geometry')
                      .map(([key, value]) => (
                        <div key={key} className="grid grid-cols-[160px_1fr] gap-3 rounded-2xl bg-slate-50 px-4 py-3">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{key}</span>
                          <span className="text-sm text-slate-700">{typeof value === 'object' ? JSON.stringify(value) : String(value ?? '—')}</span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}

            {/* One column, two pills. The reopen button is STACKED ABOVE the
                zoom controls rather than placed beside them, so adding it
                pushes them down instead of covering them — and when the
                sidebar is open there is nothing here and they sit exactly
                where they always did. */}
            <div className="absolute top-3 left-3 z-[2000] flex flex-col gap-2 pointer-events-auto">
              {tocCollapsed && (
                <div className="gl-toolbar gl-surface--over-map overflow-hidden">
                  <button
                    onClick={() => setTocCollapsed(false)}
                    className="p-1.5 hover:bg-slate-50 text-slate-600"
                    aria-label="Show map contents"
                    aria-expanded={false}
                    title="Show map contents"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <div className="gl-toolbar gl-surface--over-map flex flex-col overflow-hidden">
                <button
                  onClick={() => setZoom((currentZoom) => Math.min(MAX_INTERACTIVE_ZOOM, currentZoom + 1))}
                  className="p-1.5 hover:bg-slate-50 text-slate-600 border-b border-slate-100"
                  aria-label="Zoom in"
                >
                  <ZoomIn className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setZoom((currentZoom) => Math.max(1, currentZoom - 1))}
                  className="p-1.5 hover:bg-slate-50 text-slate-600 border-b border-slate-100"
                  aria-label="Zoom out"
                >
                  <ZoomOut className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => {
                    setCenter(module.homeCenter);
                    setZoom(module.homeZoom);
                  }}
                  className="p-1.5 hover:bg-slate-50 text-slate-600 border-b border-slate-100"
                  aria-label="Home location"
                >
                  <Home className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={doLocate}
                  className={`p-1.5 hover:bg-slate-50 ${locating ? 'text-blue-600' : 'text-slate-600'}`}
                  aria-label="My location"
                >
                  <LocateFixed className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {locating && (
              <div className="absolute bottom-4 left-4 z-[2000] bg-white px-3 py-1.5 rounded-lg text-xs text-blue-600 shadow border border-blue-100">
                Locating you...
              </div>
            )}
            {locateError && !locating && (
              <div
                role="alert"
                className="absolute bottom-4 left-4 z-[2000] max-w-xs bg-white px-3 py-1.5 rounded-lg text-xs text-red-600 shadow border border-red-100"
              >
                {locateError}
              </div>
            )}

            {/* End users get the same location search as the builder. */}
            <div className="gl-toolbar gl-surface--over-map absolute top-3 right-3 z-[2000] flex items-center gap-1.5 p-1.5 pointer-events-auto">
              <GlobalLocationSearch
                onGoToLocation={(nextCenter, nextZoom) => {
                  setCenter(nextCenter);
                  setZoom(nextZoom);
                }}
                onMarkerChange={setSearchMarker}
              />

              <button
                onClick={() =>
                  setBasemap((currentBasemap) =>
                    currentBasemap === 'street'
                      ? 'terrain'
                      : currentBasemap === 'terrain'
                        ? 'satellite'
                        : 'street',
                  )
                }
                className="gl-btn gl-btn--ghost p-1.5"
                aria-label="Change base map"
                title={`Base map: ${basemap}`}
              >
                <Globe2 className="w-3.5 h-3.5" />
              </button>
              <ToolsToggle
                open={toolsOpen}
                onToggle={() => setToolsOpen((current) => !current)}
                count={module.tools.length}
              />
            </div>

          </div>

          {/* Floating attribute table — a DIRECT child of the stable outer
              div, not the shrinking map box above, so its own positioning
              always measures the full area — the same reasoning as the
              Builder's identical placement. */}
          {attributeLayer && (
            <FloatingAttributeTable
              layer={attributeLayerLive}
              geoServerUrl={module.geoServerUrl}
              arcgisUrl={module.arcgisUrl || ''}
              onClose={() => setAttributeLayer(null)}
              onZoomToSelection={zoomToSelection}
              onSelectionChange={trackSelection}
              preselectedKeys={selectionKeys}
              onLayerChange={changeTemporaryLayer}
              canManageLayers={false}
              moduleId={module.id}
              dock={attributeDock}
              onDockChange={setAttributeDock}
              dockSize={attributeDockSize}
              onDockSizeChange={setAttributeDockSize}
            />
          )}
          </div>
        </main>
      </div>
    </div>
  );
}

function Portal() {
  const { currentUser, loading } = useAuth();
  const { can } = usePermissions();
  const canShareModules = can('modules:share');

  /* `modules` holds every module this principal may see; the API has already
     scoped the list before it arrives. Seeded from the cache the auth context
     hydrated at sign-in, so the first render has the real data rather than an
     empty flash. */
  const [modules, setModules] = useState<AppModule[]>(() => moduleService.listAllModules());
  const [saveError, setSaveError] = useState<string | null>(null);
  /* Not an error: the save landed, but on top of a copy the server considered
     newer. Said out loud rather than swallowed, because an overwrite the
     person did not know about is the one thing optimistic locking exists to
     prevent. */
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const [homeConfig, setHomeConfig] = useState<HomePageConfig>(DEFAULT_HOME_CONFIG);
  /* Whether the server has answered yet. `homeConfig` starts as the stock
     defaults — no background image, no logo — because the real branding has
     to be fetched. Rendering the portal on that placeholder shows the wrong
     background for one frame and then swaps it the moment the fetch lands,
     which is the flash this exists to prevent: the portal waits for the real
     answer the same way it already waits for the module list. */
  const [homeConfigLoaded, setHomeConfigLoaded] = useState(false);
  /* The Builder, addressed the same way the viewer is (below) — an `app`
     query parameter, so a refresh, a bookmark, or the Copy-link-style habit
     of reading the address bar while BUILDING an app all show something
     other than the bare portal URL, and Back/Forward move the app instead
     of reloading it. */
  const [activeId, setActiveId] = useState<string | null>(
    () => new URLSearchParams(window.location.search).get('app'),
  );
  const [sharingModuleId, setSharingModuleId] = useState<string | null>(null);
  const [permissionError, setPermissionError] = useState<string | null>(null);

  /* Which published app is open, as STATE rather than a re-read of the URL.
     Moving between the portal, a builder and a viewer used to be
     `window.location.href = …`, which reloads the whole page: the bundle is
     re-parsed, the session re-checked, the module list re-fetched, and the
     module itself downloaded again — for an app whose uploaded layers make it
     megabytes, that is most of the wait after Save & launch. The URL still
     changes (below) so links and refreshes keep working; only the reload
     goes. */
  const [viewerId, setViewerId] = useState<string | null>(
    () => new URLSearchParams(window.location.search).get('viewer'),
  );

  /* The admin Settings console and the Profile page, addressed the same way
     the viewer is — a query param, moved with pushState rather than a page
     load, so Back/Forward and a bookmarked link both work. `settings` also
     carries WHICH section, so switching sections inside the console is its
     own step in history rather than one page that swallows all of it. */
  const [settingsView, setSettingsView] = useState<string | null>(
    () => new URLSearchParams(window.location.search).get('settings'),
  );
  const [profileOpen, setProfileOpen] = useState<boolean>(
    () => new URLSearchParams(window.location.search).get('profile') === '1',
  );

  /* The Back and Forward buttons move the app, now that they are no longer
     page loads. Without this the URL would change and the screen would not. */
  useEffect(() => {
    const onPopState = () => {
      const params = new URLSearchParams(window.location.search);
      setViewerId(params.get('viewer'));
      setActiveId(params.get('app'));
      setSettingsView(params.get('settings'));
      setProfileOpen(params.get('profile') === '1');
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  /* The COMPLETE document for whichever module is open.
     The portal's list holds summaries — everything a card needs, without the
     uploaded features that make a module megabytes — so the module being
     opened is fetched in full first. See moduleService.loadFull. */
  const [openDoc, setOpenDoc] = useState<AppModule | null>(null);
  const [openDocError, setOpenDocError] = useState<string | null>(null);
  const openId = viewerId ?? activeId;

  useEffect(() => {
    /* Not before the session is restored. This effect runs on mount, which is
       while `loading` is still true — so a hard refresh of a viewer URL fired
       this request in parallel with the sign-in check and the module list. At
       best that is a duplicate request racing `hydrate`; at worst it answers
       401 first and drops the whole app back to the login screen. */
    if (loading || !currentUser || !openId) {
      setOpenDoc(null);
      setOpenDocError(null);
      return undefined;
    }

    let cancelled = false;
    setOpenDoc(null);
    setOpenDocError(null);

    moduleService
      .loadFull(openId)
      .then((full) => {
        if (!cancelled) setOpenDoc(full);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error('[modules] could not load', openId, error);
        setOpenDocError(
          error instanceof Error ? error.message : 'This application could not be loaded.',
        );
      });

    return () => {
      cancelled = true;
    };
  }, [openId, loading, currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Silently renews this module's ArcGIS connection token if it is close to
  // expiring — see renewArcgisConnectionIfNeeded. Runs once per module open,
  // covering BOTH the standalone viewer (`EndUserViewer` reads `module.
  // arcgisUrl` straight from this state, with no local copy of its own) and
  // the Builder's initial load (which then keeps its own copy fresh with an
  // identical check of its own — see Builder's matching effect).
  useEffect(() => {
    if (!openDoc) return undefined;
    let cancelled = false;
    const openedId = openDoc.id;
    void renewArcgisConnectionIfNeeded(openDoc, (nextArcgisUrl, nextExpiresAt) => {
      if (cancelled) return;
      setOpenDoc((current) =>
        current && current.id === openedId
          ? { ...current, arcgisUrl: nextArcgisUrl, arcgisTokenExpiresAt: nextExpiresAt }
          : current,
      );
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDoc?.id]);

  /* The portal's branding, which lives on the server so every device and every
     user sees the same one.

     Gated on the session for the same reason the module fetch above is: this
     ran on mount, which is while `loading` is still true, so before the
     branding moved off localStorage it was harmless — and the moment it became
     an authenticated request it would have fired anonymously, answered 401,
     and dropped the whole app back to the sign-in screen.

     Keyed on the user id so switching accounts re-reads it, which is also what
     picks up another admin's change without a hard refresh. */
  useEffect(() => {
    if (loading || !currentUser) return;
    void settingsService
      .getHomeConfig(currentUser.role === 'admin')
      .then((config) => {
        setHomeConfig(config);
        setHomeConfigLoaded(true);
      });
  }, [loading, currentUser?.id, currentUser?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Signing in hydrates the module cache; this copies it into state. Keyed on
     the user id so switching accounts reloads rather than showing the previous
     person's portal. */
  /**
   * Loads the module list, and says so when it cannot.
   *
   * Sign-in hydrates the cache; this copies it into state. But that hydrate is
   * wrapped in a `catch` that only logs — so a list that failed to arrive left
   * the portal simply EMPTY, with nothing on screen to say the applications
   * exist and could not be fetched. "My modules are gone" and "the request
   * failed" look identical, and only one of them is true.
   */
  const [listError, setListError] = useState<string | null>(null);
  const [listLoading, setListLoading] = useState(false);

  const reloadModules = useCallback(async () => {
    setListLoading(true);
    try {
      setModules(await moduleService.hydrate());
      setListError(null);
    } catch (error) {
      console.error('[modules] list could not be loaded', error);
      setListError(
        error instanceof Error ? error.message : 'Your applications could not be loaded.',
      );
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setModules([]);
      setListError(null);
      return;
    }
    // `isReady` is false when the hydrate at sign-in threw; retry it here
    // rather than showing an empty portal that looks like an empty account.
    if (moduleService.isReady()) {
      setModules(moduleService.listAllModules());
      setListError(null);
    } else {
      void reloadModules();
    }
  }, [currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * ACCESS BOUNDARY: filtering happens in the selector layer, so a module the
   * user is not entitled to never reaches the component tree.
   */
  const visibleModules = useMemo(
    () => moduleService.listModulesForUser(currentUser, modules),
    [currentUser, modules],
  );

  /**
   * SECURITY: the `app` URL parameter must never be enough, on its own, to
   * open the Builder.
   *
   * `activeId` can be set from the address bar directly — typed, pasted, or
   * (the reported case) hand-edited from a `viewer=<id>` link someone with
   * VIEW-ONLY access was legitimately given, swapped to `app=<id>`. Every
   * call that sets it FROM CODE (`openBuilder`, via `editModule`/
   * `openModule`/`addModule`) already checks `canEdit` first — but a value
   * read straight from the URL bypasses all of them, and `visibleModules`
   * alone is not the right gate: it answers "may this person see the
   * module", which a view-only grant already satisfies, not "may they edit
   * it". Without this check, editing that URL parameter was enough to reach
   * the Builder screen with no edit permission at all.
   *
   * The write itself was never actually at risk — `moduleService.
   * updateModule` refuses client-side (`ModulePermissionError`) and
   * `PUT /modules/{id}` refuses again server-side (`_may_edit`) regardless
   * of what the UI shows. This closes the UI-level exposure: a non-editor
   * redirected to the read-only viewer (if they can at least see the
   * module) or the portal (if they cannot see it at all) never has the
   * Builder rendered for them in the first place, not even for one frame —
   * see the render guard below that this effect pairs with.
   */
  useEffect(() => {
    if (!activeId) return;
    const module = visibleModules.find((m) => m.id === activeId);
    if (!module) return; // not visible to this user at all; falls through to Landing below
    const access = getModuleAccess(currentUser, module);
    if (!access.canEdit) {
      console.error(
        `[security] blocked Builder access via ?app= without edit permission: module="${module.id}" user="${currentUser?.id}"`,
      );
      if (access.canView) {
        openViewer(module.id);
      } else {
        returnToPortal();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, visibleModules, currentUser]);

  /* Optimistic: state moves first so the UI stays responsive, then the write
     is sent. If the server refuses, the cache is the truth and state is put
     back — a silent failure here would look like a save that worked until the
     next reload. */
  const persist = useCallback(async (next: AppModule[]) => {
    setModules(next);
    try {
      await moduleService.saveAll(next);
      setSaveError(null);
    } catch (error) {
      setModules(moduleService.listAllModules());
      setSaveError(error instanceof Error ? error.message : 'Could not save your changes.');
    }
    return next;
  }, []);

  /**
   * Save feedback, rendered by every screen that can trigger a save.
   *
   * THIS USED TO BE UNREACHABLE WHERE IT MATTERED MOST.
   * These banners lived only in the portal's return, but the builder is
   * returned EARLIER — so a save that failed inside the builder set the
   * message and then rendered nothing at all. Pressing "Save & launch" on a
   * refused write did visibly nothing: no navigation, no error, no clue. The
   * production log shows fourteen consecutive rejected saves on one module,
   * which is what that silence looks like from the other end.
   *
   * Defined once and rendered by both screens, so the two can never disagree
   * about whether a failure is worth showing again.
   */
  const saveBanners = (
    <>
      {/* A save that did not reach the server. Shown, never swallowed: the
          builder looks identical whether a save landed or not, so silence here
          means someone finds out at the next reload that an afternoon's work
          is gone. */}
      {saveError && (
        <div
          role="alert"
          className="fixed bottom-6 left-1/2 z-[9600] -translate-x-1/2 max-w-md rounded-xl bg-amber-600 px-4 py-3 text-sm font-medium text-white shadow-2xl"
        >
          <span className="font-semibold">Not saved.</span> {saveError}
          <button
            type="button"
            onClick={() => setSaveError(null)}
            className="ml-3 rounded px-1.5 text-white/80 hover:text-white"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {saveNotice && (
        <div
          role="status"
          className="fixed bottom-6 left-1/2 z-[9600] -translate-x-1/2 max-w-md rounded-xl bg-slate-900 px-4 py-3 text-sm font-medium text-white shadow-2xl"
        >
          <span className="font-semibold">Saved.</span> {saveNotice}
          <button
            type="button"
            onClick={() => setSaveNotice(null)}
            className="ml-3 rounded px-1.5 text-white/80 hover:text-white"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {permissionError && (
        <div
          role="alert"
          className="fixed bottom-6 left-1/2 z-[9600] -translate-x-1/2 rounded-xl bg-red-600 px-4 py-3 text-sm font-medium text-white shadow-2xl"
        >
          {permissionError}
          <button
            type="button"
            onClick={() => setPermissionError(null)}
            className="ml-3 rounded px-1.5 text-white/80 hover:text-white"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}
    </>
  );

  /** Published apps are addressed by a `viewer` query parameter. */
  const openViewer = (id: string) => {
    const viewerUrl = new URL(window.location.href);
    viewerUrl.searchParams.set('viewer', id);
    viewerUrl.searchParams.delete('app');
    viewerUrl.searchParams.delete('settings');
    viewerUrl.searchParams.delete('profile');
    // pushState, not assignment: the address bar still says where you are, and
    // the link is still shareable, but nothing is re-downloaded.
    window.history.pushState({}, '', viewerUrl.toString());
    setViewerId(id);
    setActiveId(null);
    setSettingsView(null);
    setProfileOpen(false);
  };

  /** Returns to the Landing page from the viewer, Builder, Settings or
   *  Profile — every one of them is "the portal minus one query param", so
   *  one function clearing all four covers all four. */
  const returnToPortal = () => {
    const portalUrl = new URL(window.location.href);
    portalUrl.searchParams.delete('viewer');
    portalUrl.searchParams.delete('app');
    portalUrl.searchParams.delete('settings');
    portalUrl.searchParams.delete('profile');
    window.history.pushState({}, '', portalUrl.toString());
    setViewerId(null);
    setActiveId(null);
    setSettingsView(null);
    setProfileOpen(false);
  };

  /** The Builder is addressed by an `app` query parameter — see `activeId`'s
   *  own doc. Mirrors `openViewer` exactly, one param apart. */
  const openBuilder = (id: string) => {
    const builderUrl = new URL(window.location.href);
    builderUrl.searchParams.set('app', id);
    builderUrl.searchParams.delete('viewer');
    builderUrl.searchParams.delete('settings');
    builderUrl.searchParams.delete('profile');
    window.history.pushState({}, '', builderUrl.toString());
    setActiveId(id);
    setViewerId(null);
    setSettingsView(null);
    setProfileOpen(false);
  };

  /** Opens the admin Settings console, optionally landing on one section —
   *  the "Add/Change logo" shortcuts on the Landing page use this to jump
   *  straight to Home Page rather than dropping someone at whatever section
   *  they last left the console on. */
  const openSettings = (section: string = 'general') => {
    const url = new URL(window.location.href);
    url.searchParams.delete('viewer');
    url.searchParams.delete('app');
    url.searchParams.delete('profile');
    url.searchParams.set('settings', section);
    window.history.pushState({}, '', url.toString());
    setViewerId(null);
    setActiveId(null);
    setProfileOpen(false);
    setSettingsView(section);
  };

  /** Switching sections WITHIN the console — its own history entry, so Back
   *  steps between sections before it steps out of Settings entirely. */
  const selectSettingsSection = (section: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('settings', section);
    window.history.pushState({}, '', url.toString());
    setSettingsView(section);
  };

  const openProfile = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('viewer');
    url.searchParams.delete('app');
    url.searchParams.delete('settings');
    url.searchParams.set('profile', '1');
    window.history.pushState({}, '', url.toString());
    setViewerId(null);
    setActiveId(null);
    setSettingsView(null);
    setProfileOpen(true);
  };

  const addModule = async (name: string, kind: AppKind) => {
    if (!currentUser) return;
    // Both kinds are AppModules, so ownership, sharing, and assignment work
    // through the same service; only the builder they open differs.
    const created =
      kind === 'dashboard' ? createDashboardModule(name, currentUser) : makeModule(name, currentUser);

    /* AWAITED, which it was not before. The builder used to open while the
       create was still in the air, and a Save pressed in that window found no
       cached module and issued a SECOND create for the same id — which the
       server rightly refused. Proven with the real service against a fake API:
       two POSTs, the second answering 409. */
    await persist([...modules, created]);
    openBuilder(created.id);
  };

  /**
   * Delete goes through the guarded service, which re-validates ownership
   * against persisted state — a shared module can never be deleted by its
   * recipient, even if the UI were bypassed.
   */
  const deleteModule = async (id: string) => {
    try {
      const next = await moduleService.deleteModule(currentUser, id);
      setModules(next);
      // Grants are removed by the database with the module (ON DELETE
      // CASCADE); this call is now a no-op kept for the call site's shape.
      void userService.revokeModuleEverywhere(id);
      setPermissionError(null);
    } catch (error) {
      if (error instanceof ModulePermissionError) {
        console.error('[modules] delete denied:', error.message);
        setPermissionError(error.message);
        return;
      }
      // A locked module refuses this way too (see moduleService.deleteModule)
      // — not a permission error, but the same "say why, don't crash" banner.
      if (error instanceof Error) {
        setPermissionError(error.message);
        return;
      }
      throw error;
    }
  };

  const saveModule = async (updated: AppModule, options: { launch?: boolean } = {}) => {
    const savedModule = { ...updated, built: true };
    try {
      setSaveNotice(null);
      const next = await moduleService.updateModule(currentUser, savedModule, {
        onOverwroteNewer: () =>
          setSaveNotice(
            'This app had been changed somewhere else — another tab, or this ' +
              'page reopened with the Back button. Your version has been saved ' +
              'over it.',
          ),
      });
      setModules(next);
      setSaveError(null);
      /* The document we just sent IS the current one — re-fetching it to open
         the viewer would download every uploaded feature back again, which for
         this deployment is 12–19 MB of coordinates the browser already holds. */
      setOpenDoc(savedModule);
    } catch (error) {
      if (error instanceof ModulePermissionError) {
        console.error('[modules] save denied:', error.message);
        setPermissionError(error.message);
        returnToPortal();
        return;
      }
      /* A network or conflict failure must NOT navigate on to the viewer:
         doing that would show the previous saved version and read as the save
         having worked. The builder stays open with the message. */
      console.error('[modules] save failed:', error);
      setSaveError(error instanceof Error ? error.message : 'Could not save this application.');
      return;
    }

    // A plain Save keeps the admin in the builder; only Save & launch navigates.
    if (options.launch === false) return;
    openViewer(savedModule.id);
  };

  /**
   * Shown while the opened module's full document is on its way.
   *
   * A named state, not a blank screen. The portal used to sit on a bare dark
   * rectangle for as long as the modules took to arrive, which reads as a page
   * that has failed rather than one that is working.
   */
  const loadingScreen = (label: string) => (
    <div className="flex h-screen items-center justify-center bg-slate-50">
      <div className="flex items-center gap-3 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
        {label}
      </div>
    </div>
  );

  const openFailedScreen = (message: string) => (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 text-center">
      <div className="max-w-md">
        <h1 className="text-xl font-semibold text-slate-800">This application could not be opened</h1>
        <p className="mt-2 text-sm text-slate-500">{message}</p>
        <button
          type="button"
          onClick={returnToPortal}
          className="gl-btn gl-btn--secondary mt-5 px-4 py-2 text-sm"
        >
          Back to the portal
        </button>
      </div>
    </div>
  );

  /* Restoring the session and fetching the module list. This was a bare dark
     rectangle, which is indistinguishable from a page that has crashed — and
     it was on screen for as long as the list took, which used to be the whole
     of every module's uploaded data. */
  if (loading) {
    return loadingScreen('Loading your applications…');
  }

  if (!currentUser) {
    return <LoginScreen />;
  }

  // Deep-linked viewer: re-check entitlement rather than trusting the URL.
  if (viewerId) {
    const viewerModule = moduleService.getModuleById(viewerId);
    if (!moduleService.canAccessModule(currentUser, viewerModule)) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6 text-center">
          <div>
            <h1 className="text-xl font-semibold text-slate-800">
              {viewerModule ? 'You do not have access to this module' : 'Map module not found'}
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              Ask an administrator to assign it to your account.
            </p>
          </div>
        </div>
      );
    }
    if (openDocError) return openFailedScreen(openDocError);
    /* The summary answered "may you see this"; only the full document can be
       DRAWN — its file-backed layers carry their features, and the summary's
       do not. */
    if (!openDoc || openDoc.id !== viewerId) return loadingScreen('Opening…');

    // Dashboards and map apps have separate view-only experiences.
    return isDashboard(openDoc) ? (
      <DashboardViewer module={openDoc} onBack={returnToPortal} />
    ) : (
      <EndUserViewer module={openDoc} />
    );
  }

  const activeModule = visibleModules.find((module) => module.id === activeId);

  if (activeModule) {
    // See the SECURITY effect above, which this pairs with: the Builder JSX
    // itself must never be returned for a module this user cannot edit, not
    // even for the one render before that effect's redirect lands. A
    // view-only `?app=<id>` (typed, pasted, or a hand-edited `?viewer=`
    // link) shows this same loading screen for a moment, then leaves.
    if (!getModuleAccess(currentUser, activeModule).canEdit) {
      return loadingScreen('Opening…');
    }
    if (openDocError) return openFailedScreen(openDocError);
    if (!openDoc || openDoc.id !== activeModule.id) return loadingScreen('Opening the builder…');

    return (
      <div className="h-screen overflow-hidden bg-slate-50">
        {isDashboard(openDoc) ? (
          <DashboardBuilder
            module={openDoc}
            onBack={returnToPortal}
            /* Keeps the portal's module list in step with what was persisted —
               and `openDoc` too, so launching straight after a save shows what
               was just saved rather than the copy loaded when the builder
               opened. The cache already holds the full saved document, so this
               costs no request. */
            onSaved={() => {
              setModules(moduleService.listAllModules());
              const fresh = moduleService.getModuleById(openDoc.id);
              if (fresh) setOpenDoc(fresh);
            }}
            onLaunch={(saved) => openViewer(saved.id)}
          />
        ) : (
          <Builder module={openDoc} onBack={returnToPortal} onSave={saveModule} />
        )}
        {saveBanners}
      </div>
    );
  }

  const openModule = (id: string) => {
    const module = visibleModules.find((m) => m.id === id);
    if (!module) return;

    // Shared (view-only) modules always open in the read-only viewer, never the
    // Builder — even when still a draft, which would otherwise be an edit path.
    const access = getModuleAccess(currentUser, module);
    if (module.built || !access.canEdit) {
      openViewer(id);
      return;
    }
    openBuilder(id);
  };

  const editModule = (id: string) => {
    const module = visibleModules.find((m) => m.id === id) ?? null;
    const access = getModuleAccess(currentUser, module);
    if (!access.canEdit) {
      console.error('[modules] edit denied for module:', id);
      setPermissionError('You do not have permission to edit this module.');
      return;
    }
    openBuilder(id);
  };

  const shareModule = (id: string) => {
    const module = visibleModules.find((m) => m.id === id) ?? null;
    if (!getModuleAccess(currentUser, module, canShareModules).canShare) {
      console.error('[modules] share denied for module:', id);
      return;
    }
    setSharingModuleId(id);
  };

  /* The list could not be fetched. Said plainly, with the way out, instead of
     an empty portal that reads as "your applications are gone". */
  if (listError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 text-center">
        <div className="max-w-md">
          <h1 className="text-xl font-semibold text-slate-800">
            Your applications could not be loaded
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            {listError} — they are still on the server; this browser could not fetch the list.
          </p>
          <button
            type="button"
            onClick={() => void reloadModules()}
            disabled={listLoading}
            className="gl-btn gl-btn--secondary mt-5 px-4 py-2 text-sm disabled:opacity-60"
          >
            {listLoading ? 'Retrying…' : 'Try again'}
          </button>
        </div>
      </div>
    );
  }

  // The portal's own background/logo/colours are what would otherwise flash
  // to their defaults for one frame — wait for the real answer, same as the
  // module list above.
  if (!homeConfigLoaded) {
    return loadingScreen('Loading your portal…');
  }

  // The admin Settings console and the Profile page are full pages, the same
  // tier as the viewer above — checked here rather than earlier because both
  // need `homeConfig` loaded (Settings' own Home Page section edits it).
  // All three branches below share ONE ShareModuleDialog rather than each
  // carrying its own: the App Gallery (inside Settings) and the home page's
  // own cards both start a share the same way, and a module being shared
  // must survive a navigation between them without the dialog vanishing.
  return (
    <>
      {settingsView ? (
        <SettingsPage
          section={settingsView}
          onSectionChange={selectSettingsSection}
          onBack={() => {
            returnToPortal();
            // Module assignments (User Management) may have changed while open.
            setModules(moduleService.listAllModules());
          }}
          homeConfig={homeConfig}
          onHomeConfigSaved={setHomeConfig}
          modules={visibleModules}
          onOpenModule={openModule}
          onEditModule={editModule}
          onShareModule={shareModule}
          onDeleteModule={deleteModule}
          onModulesChanged={setModules}
        />
      ) : profileOpen ? (
        <ProfilePage onBack={returnToPortal} />
      ) : (
        <div className="h-screen overflow-hidden bg-slate-50">
          <Landing
            modules={visibleModules}
            homeConfig={homeConfig}
            onOpen={openModule}
            onEdit={editModule}
            onDelete={deleteModule}
            onShare={shareModule}
            onAdd={addModule}
            onOpenSettings={openSettings}
            onOpenProfile={openProfile}
            onHomeConfigSaved={setHomeConfig}
          />
          {saveBanners}
        </div>
      )}

      {sharingModuleId && (
        <ShareModuleDialog
          module={modules.find((module) => module.id === sharingModuleId) as AppModule}
          onClose={() => {
            setSharingModuleId(null);
            setModules(moduleService.listAllModules());
          }}
        />
      )}
    </>
  );
}

export default function BlueviewGISPortal() {
  return (
    <AuthProvider>
      <Portal />
    </AuthProvider>
  );
}
